/**
 * The deal's line items — the BASKET, edited in place.
 *
 * Built on `QuoteLineItemsTable` and `InvoiceLineItemsTable`; read the quote
 * grid's header before changing a cell — the layout rules, the `<colgroup>`
 * discipline and the no-prose-in-a-grid-cell rule are stated there and are not
 * repeated here. What IS stated here is every place a deal line **differs**,
 * because the three tables look alike and the differences are silent.
 *
 * ## ⚠️ A DEAL LINE CARRIES NO TAX. THAT IS THE WHOLE DIFFERENCE, AND THE
 * ## SYMMETRY WILL MISLEAD YOU
 *
 * `f_deal_products` has **ONE** generated column, `line_total`:
 *
 * ```sql
 * ROUND(quantity * COALESCE(unit_price, 0) * (1 - discount / 100), 2)
 * ```
 *
 * There is **no `tax_rate` column, no `xero_tax_type` column, no
 * `line_subtotal` and no `line_tax`** — quote and invoice lines have all five.
 * The schemas are `.strict()`, so `taxRate`, `lineSubtotal` and `lineTax` are
 * each a **`422` naming the key**, alongside `lineTotal`, `dealId` and `cityId`
 * (`docs/api/deals.md`).
 *
 * Everything else — `productId`, `projectId`, `cityIds`, `inclusions`,
 * `description`, `quantity`, `unitPrice`, `discount`, `sortOrder` — matches the
 * quote and invoice line **field for field**. Nine write keys and nothing else.
 * That near-identity is exactly what makes the tax difference easy to undo by
 * accident, so it is closed **structurally rather than by remembering**:
 *
 * - **`taxRate` and `xeroTaxType` are not keys on {@link Draft} at all**, so no
 *   payload this file can build is able to express either and `tsc` refuses a
 *   call site that tries. **A guard somebody can forget is not a guard.**
 * - There is **no Tax rate column** in the `<colgroup>`, no `TaxRateControl`, no
 *   `useXeroTaxRates` and no `useXero`. A deal never reaches Xero.
 * - The detail panel shows **no Subtotal and no Tax figures**. Those come off
 *   `lineSubtotal` / `lineTax`, which do not exist on this table.
 *
 * ⚠️ **Do not "harmonise" this grid's money block with the other two.** The
 * resemblance of the other eight keys is the trap, not the licence.
 *
 * ⚠️ **`GeneratedLineInput` still has a `taxRate` field and this file supplies
 * `'0'`** — in exactly one place, {@link previewInputOf}, with the literal
 * written out rather than defaulted. `computeGeneratedLinePreview` is the one
 * owner of the generated-column arithmetic and its step 1 IS the deal's
 * `line_total` formula, so at 0% its `lineSubtotal` and its `lineTotal` are the
 * same figure and both equal what the database will store. Reusing it keeps a
 * single copy of the rounding; the `'0'` is a statement that this table has no
 * tax, not a tax rate of zero being sent anywhere.
 *
 * ## ⚠️ There is no `allCities` flag on a deal line either
 *
 * Quote and invoice lines carry one (migration 083): a line meaning *every city
 * its project has*, resolved live. **Deal lines do not.** It is absent from
 * `createDealProductSchema`, absent from `DealProduct`, and
 * `deal-products.service.ts` passes `allCities: false` into the shared guard as
 * a written-out literal. Under `.strict()` sending the key is a 422.
 *
 * So this grid has the plain city multiselect and **no tick box**, and
 * {@link Draft} has no `allCities` key. Do not port the other two grids' flag
 * handling across.
 *
 * ⚠️ **There IS a tick box in that cell now, and it is not that one.** The
 * regions block below the cities gained an **“All regions”** checkbox on
 * 2026-08-31 (see {@link RegionsControl}), which is a **select-all writing an
 * explicit `regionIds` array** — there is no `allRegions` key on the wire on any
 * of the three tables. So the paragraph above is unchanged and still binding:
 * the CITY flag is what does not exist here. Two boxes that look alike and are
 * not the same kind of thing is exactly the confusion this note exists to stop.
 *
 * ## SEED's regions of focus — added to this grid last of the three
 *
 * `regionIds` has been on `f_deal_product_regions` since migration 112 and this
 * grid could not send it until 2026-08-31: the only deal picker for it sat in
 * `LineItemFormModal`. See {@link Draft.regionIds}.
 *
 * ⚠️ **A deal line's regions REACH THE CUSTOMER'S DOCUMENT.** `deal-convert.ts`
 * copies them onto the quote or invoice line it creates, alongside the project,
 * the cities, the inclusions and the description — so this is not an internal
 * pipeline note. It is one of the things that decides how the resulting document
 * is tracked in Xero, which is why the control had to exist here and not only on
 * the two document grids.
 *
 * ## What a deal line is FOR, which is not what a quote line is for
 *
 * A deal is an internal pipeline record: not a document a customer receives, and
 * **there is no deal push to Xero**. The basket records what the deal is
 * expected to sell, and is carried across by `POST /deals/{id}/convert` — which
 * copies the project, the cities, the inclusions and the description, and copies
 * `unitPrice` **verbatim rather than re-snapshotting it**, so the price agreed
 * at deal time is the price quoted. That is also why `description` is here at
 * all: nothing consumes it on a deal, and it survives into the quote or invoice
 * the deal becomes.
 *
 * ⚠️ **The derived text therefore previews a document that does not exist yet**
 * — but the two documents it might become now compose the SAME sentence, so
 * there is nothing left to hedge against. *SUPERSEDED: "{@link descriptionOf}
 * passes `inclusions: []` deliberately. The quote composer appends a bracketed
 * inclusions segment and the invoice composer does not, so guessing shows a
 * bracketed promise on a deal bound for an invoice."* `xero-invoice.payload.ts`
 * **imports** `inclusionsSegment` and `geographySegment` from
 * `xero-quote.payload.ts` (`3cd2e1c`, `6d17eea`), so whichever document a deal
 * converts into prints the same string. {@link descriptionOf} therefore passes
 * the row's real cities, regions and inclusions.
 *
 * ⚠️ **The struck reasoning was sound and its PREMISE expired**, which is the
 * only way this class of comment ever fails: "the two composers disagree, so
 * show only what they agree on" stayed true-sounding for as long as anyone cared
 * to read it, in a file that cannot see either composer.
 *
 * ## One line per product PER PROJECT — and the picker does not exclude
 *
 * ⚠️ *SUPERSEDED: "`UNIQUE (deal_id, product_id, project_key)`, where
 * `project_key` is a generated `COALESCE(project_id, 0)` … The clash is caught
 * across rows by {@link duplicatePairsOf} and, failing that, by the server's
 * 409."* **Migration 129 dropped that key.** A deal may carry one product on as
 * many lines as it likes, so there is no clash and no 409.
 *
 * **"Exclude products already on the deal" is still wrong**, and now for a
 * plainer reason than the two-projects one it used to rest on: a document may
 * simply sell the same thing more than once. {@link duplicatePairsOf} survives
 * as a NOTICE on `RowProblems.notice`, which `blockedKeys` does not read.
 *
 * ## The project and city guard is the SAME code, and that is the point
 *
 * The guard is `assertLineProjectUsable`, byte for byte the one the quote and
 * invoice lines run, and all seven refusals are reachable; `@/lib/line-project`
 * is the single client-side model of them, used unchanged. A deal line that is
 * valid is a quote line that converts cleanly. (Deals do not push to Xero, so a
 * project's third job on a quote — the line's Xero tracking category — has no
 * effect here.)
 *
 * ## What a line STARTS WITH — the recommended discount and the inclusion preset
 *
 * Once a row names a product it seeds two things into the DRAFT: the product's
 * `recommendedDiscountAmount`, and the campaign inclusions the product's band
 * ladder presets for that quantity. Both decisions live in `@/lib/line-presets`,
 * shared with the quote and invoice grids; this file owns only the requests, the
 * cache and the patch. See {@link useInclusionPresets} and
 * {@link seedsFromCatalogue}.
 *
 * ⚠️ **NEITHER OF THIS GRID'S DIFFERENCES REACHES THE SEED, and both were
 * checked before anything was wired rather than assumed from the other two
 * files.** A deal line DOES carry `discountAmount` — it is a {@link Draft} key, a
 * `ROW_WRITE_KEYS` member and emitted by both payload builders — and it DOES
 * carry inclusions. Neither seed is tax-shaped, so the missing tax column does
 * not touch them; and a seeded AMOUNT reaches the Amount cell the same way a
 * typed one does, through {@link previewInputOf}, which reads the RESOLVED pair
 * off {@link discountWriteOf} rather than off the draft.
 *
 * ⛔ **A SEED NEVER TOUCHES A SAVED LINE THAT NOBODY HAS MOVED.**
 * {@link seedsFromCatalogue} is the gate. `@/lib/line-presets` cannot see
 * saved-ness — it only knows whether a figure was hand-edited — so without this
 * every existing line whose product recommends a discount would be rewritten on
 * LOAD, going dirty with nothing on screen to say why and offering to save a
 * figure somebody agreed months ago. The owner's rule is *re-apply only if
 * untouched*; a stored line is the most touched thing there is.
 *
 * ⛔ **AND IT NEVER SEEDS INCLUSIONS ONTO A ROW WHOSE BLOCK IS HIDDEN.** An
 * inclusions write REPLACES the whole set, and `createPayload` / `updatePayload`
 * OMIT the key when `offersInclusions` is false precisely so `[]` can never
 * clear a line's stored rows. Seeding into a hidden block would put a set into a
 * draft nobody can see or undo — and the very next save with the block shown
 * would write it.
 *
 * ## Saving is N requests, and it says so
 *
 * No bulk endpoint, no transaction across the rows: if the third 409s, the first
 * two are committed. Every row carries its own status and reason and the run
 * reports "2 of 3 lines saved". A retry never re-POSTs a row that already landed
 * — a successful POST turns its row into a saved row with a real line id.
 *
 * ⚠️ **Unlike the quote and invoice grids, a line write here rewrites NOTHING on
 * the header.** A deal has no `subTotal` / `taxTotal` / `grandTotal` columns for
 * a line to recalculate; `deal.amount` is a hand-entered figure that the basket
 * does not touch and must not be made to. So `onLinesChanged` exists to let the
 * parent re-read derived fields (`project`, `cities`), **not** to repair totals,
 * and there is no Xero push to queue behind it.
 *
 * ## `mode: 'draft'` — lines collected before the deal exists
 *
 * `POST /deals/{id}/products` needs an id, so a line cannot be written before
 * the deal is created. Draft mode holds the rows locally, issues **no line
 * request of any kind**, and reports the payloads through `onDraftLinesChange`
 * for the parent to write once the deal exists. The same four properties the
 * quote grid's draft mode has: one payload builder shared with the saved POST,
 * money labelled as a preview throughout, the duplicate the server would refuse
 * caught here, and **only rows the server would accept emitted** — the rest
 * withheld and counted through `onDraftBlockedChange`.
 *
 * The reference-data fetches still run in draft mode and must: the products,
 * campaign-inclusions and active-project lists are what the pickers are made of.
 * "No requests" means no *line* requests.
 */
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, api } from '@/lib/api';
/*
 * ⚠️ **IT LIVES IN `api.ts`, NOT `types.ts`, AND THAT IS DELIBERATE.**
 * `types.ts` mirrors the API's own response shapes; this is a shape THIS APP
 * composes for a form — `plannedProductLines()` and `companyProjectLines()` are
 * both mappers, not endpoints, and the type belongs beside the function that
 * builds it. Moving it would put a client-side view of several reads in the file
 * whose whole contract is "this is what the wire sends".
 */
import type { PlannedProductLine } from '@/lib/api';
import { cx } from '@/lib/cx';
/*
 * ⚠️ **`solveLineAmount` is the ONE place a typed Amount is turned back into a
 * writable field**, shared with the quote and invoice grids. It is imported
 * rather than reimplemented for the same reason `computeGeneratedLinePreview` is:
 * this cell solves BACKWARDS from the figure that one solves forwards to, and a
 * second copy of the arithmetic would agree on the day it was written and drift
 * at the first rounding correction — into a discount computed against a price
 * the row is not actually using.
 */
import {
  computeGeneratedLinePreview,
  solveLineAmount,
  splitAmountOverQuantity,
  sumDecimalStrings,
  type GeneratedLineInput,
} from '@/lib/decimal';
/*
 * ⚠️ **Project pricing (owner 2026-10-27) replaces the bare city pro-rata as the
 * inherited price.** `computeLinePrice` tries the project's price entry (per
 * currency × tier, per city or flat, with overrides) and falls back to the
 * legacy catalogue rule itself; this file never calls either rule directly.
 */
import { computeLinePrice, tierOptionsFor, type ComputedLinePrice } from '@/lib/line-pricing';
import type { ProjectPriceEntry } from '@/lib/project-pricing';
import { useProjectPrices } from '@/lib/use-project-prices';
/*
 * ⚠️ **WORDING ONLY.** The mode's two labels live in one module so the three
 * grids cannot word this choice differently; nothing about the payload, the
 * refusals or the arithmetic comes from there. `discountWriteOf` below is still
 * what decides which of the two columns the wire carries, and this grid keeps
 * its own refusal set — a deal line has ONE percentage, not two, and its
 * validation differs from the other two grids' on purpose.
 *
 * ⚠️ **`percentageModeIsChosen` is NOT an exception to that, and it is not
 * `draft.discountAmount === null`.** It answers a question about the MODE —
 * this module's whole subject — and returns a boolean; it computes no money and
 * builds no pair. It is imported rather than spelled out at the Amount cell
 * because the distinction it draws is subtle enough to be got wrong: a line
 * nobody has discounted reads as percentage mode, so the naive test is true of
 * the commonest row in the grid. See {@link AmountCell}.
 */
import {
  DISCOUNT_MODE_GROUP_LABEL,
  DISCOUNT_MODE_OPTIONS,
  discountModeOf,
  percentageModeIsChosen,
} from '@/lib/discount-mode';
import { formatDecimalString } from '@/lib/format';
import { isValidInclusionQuantity, renderInclusion } from '@/lib/inclusions';
/*
 * ⚠️ **The ONE copy of the join.** The backend composes the same string
 * independently, so a second implementation here diverges silently — the output
 * is well-formed either way and simply names the wrong thing. Do not
 * re-implement it and do not inline it "because it is one template literal".
 */
import {
  composeLineDescription,
  effectiveInclusionQuantity,
  type LineDescriptionInclusion,
} from '@/lib/line-description';
/*
 * ⚠️ **THE DECIDING IS NOT DONE HERE, AND MUST NOT BE.** `@/lib/line-presets` is
 * ONE copy for the quote, invoice and deal grids of *"given what the product
 * recommends and what the reader has already done, what should change?"*. Three
 * copies would agree the day they were written and drift on the first
 * correction — and what would drift is a figure a customer is billed against.
 *
 * This grid owns the REQUESTS and the DRAFT; it never re-implements the rule.
 * In particular it does not re-derive "is this untouched": both functions
 * already answer `null` for "change nothing", and a caller that second-guesses a
 * `null` is overwriting somebody's edit.
 */
import {
  inclusionPresetSeed,
  recommendedDiscountSeed,
  type LineInclusionDraft,
} from '@/lib/line-presets';
import { lineRefusalOf, type LineRefusal } from '@/lib/line-project';
/*
 * ⚠️ `shouldShowInclusionsEditor` is deliberately NOT imported: the owner chose
 * the strict predicate `productOffersInclusions` over the lenient one, accepting
 * that an inclusion on a non-offering line becomes read-only in this app.
 * Restoring it reverses a decision rather than fixing a bug.
 */
/*
 * ⛔ `inclusionsRequiredOnLine` and `INCLUSION_REQUIRED_MESSAGE` are GONE, not
 * merely unimported — the at-least-one rule was withdrawn on 11 Sep 2026 and
 * both were deleted from `@/lib/product-inclusions` with it. See `validateRow`.
 */
import {
  hasOrphanedInclusions,
  inclusionsDenialReason,
  offeredInclusionIds,
  productOffersInclusions,
  NO_PRODUCT_CHOSEN,
  type NoProductChosen,
  type ProductInclusionOffer,
} from '@/lib/product-inclusions';
/*
 * ⚠️ **Per-unit products (owner, 2026-10-16) — the RULES live in
 * `@/lib/product-units`, one copy for the three grids.** This grid only asks:
 * the step a quantity must be a multiple of, the quantity a new pick starts at,
 * the per-unit recommended discount scaled to the line, and the auto
 * description. Nothing here reads a product NAME to decide any of it.
 */
import {
  autoLineDescription,
  defaultQuantityFor,
  isAutoDescription,
  perUnitDiscountTotal,
  quantityStepError,
  type AutoLineDescriptionArgs,
  type ProductUnitFields,
} from '@/lib/product-units';
import { productOfProject, projectSellsProduct, useCycleScopeIds } from '@/lib/use-line-project';
import {
  DEFAULT_PROJECT_CYCLE_SCOPE,
  PROJECT_CYCLE_SCOPES,
  PROJECT_CYCLE_SCOPE_LABELS,
  projectOptionsByScope,
  projectScopeFootnote,
  type ProjectCycleScope,
} from '@/lib/project-cycle-scope';
import { cityLabel } from '@/lib/use-project-options';
import { useReferenceOptions } from '@/lib/use-reference-options';
import type {
  CampaignInclusion,
  DealProduct,
  DealProductCreateInput,
  DealProductUpdateInput,
  LineCity,
  LineInclusion,
  PriceTier,
  Project,
  ProjectCity,
  ResolvedInclusionPreset,
} from '@/lib/types';
import {
  Badge,
  Button,
  Checkbox,
  Combobox,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  MultiCombobox,
  SegmentedControl,
  Select,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
  Textarea,
} from '@/components/ui';
import type { ComboboxOption, MultiComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { LineNoteField } from '@/components/documents/LineNoteField';

/* ── The server's own patterns, so a refusal is caught before it is sent ──── */

/**
 * ⛔ **ONE PATTERN BECAME TWO ON 2026-09-12, AND MERGING THEM AGAIN IS A
 * SURCHARGE.** A single `PRICE_PATTERN` served `unitPrice` AND `discountAmount`
 * — correct while both were non-negative, wrong the moment one stopped being.
 *
 * Migration 171 lets a line PRICE be negative (a DEDUCTION LINE: INV-0886
 * carries one at `-4150.00`), while `discountAmount` stays non-negative under
 * `ck_*_discount_amount_sign`. A negative in the first box is a credit; a
 * negative in the second is a **surcharge arriving through the discount
 * field**, which the database refuses only as errno 4025 — a 422 naming no
 * field at all, which nobody can bind to the control they typed in.
 *
 * The backend pins the pair behaviourally in
 * `tests/line-negative-unit-price.test.ts`. **They differ by one character and
 * must never converge.**
 */

/** `unitPrice`: `^-?\d{1,10}(\.\d{1,2})?$`, straight off the POST schema. */
const PRICE_PATTERN = /^-?\d{1,10}(\.\d{1,2})?$/;

/** `discountAmount`: unsigned — a discount is not a surcharge. */
const DISCOUNT_AMOUNT_PATTERN = /^\d{1,10}(\.\d{1,2})?$/;
/**
 * `discount`: a PERCENTAGE 0..100, `^\d{1,3}(\.\d{1,2})?$`.
 *
 * ⚠️ Named for the one column that uses it. The other two grids call the
 * identical constant `PERCENT_PATTERN` and apply it to `discount` **and**
 * `taxRate`; there is no second percentage on a deal line.
 */
const DISCOUNT_PATTERN = /^\d{1,3}(\.\d{1,2})?$/;

/** A longer array is a 422 on `inclusions`. */
const MAX_INCLUSIONS_PER_LINE = 50;
/** `/products` caps here; 28 rows exist today, so one page is the whole list. */
const PRODUCTS_PER_PAGE = 100;
/** `/campaign-inclusions` caps here. */
const INCLUSIONS_PER_PAGE = 200;
/*
 * ⚠️ **`PROJECTS_PER_PAGE` IS GONE — the grid PAGES now (2026-09-15).**
 * *SUPERSEDED: `const PROJECTS_PER_PAGE = 100`, sent as the `perPage` of one
 * read.* It was being hit — 118 active projects against it — and the same 18
 * rows fell off every time. `api.listAllProjects` owns the page size; it is not
 * a number this file may choose. See `useActiveProjects`.
 */
/**
 * `cityIds` caps here. A denial-of-service bound rather than a business rule —
 * the real limit is how many cities the line's project declares. It matches
 * `MAX_CITIES_PER_PROJECT`, so it can never refuse a legitimate "all of them".
 */
const MAX_CITIES_PER_LINE = 200;
/**
 * How long the Qty box settles before its inclusion preset is resolved.
 *
 * The house figure for a typed control, and it is doing the same job here as it
 * does on a search box: `18` typed towards `180` is three quantities, two of
 * which nobody meant, and each one is a request against a shared budget
 * — and a cached answer for a quantity nobody wanted is a cache entry that
 * outlives the keystroke. ⚠️ *SUPERSEDED: "a shared 120/60s budget."* It is
 * **12,000/60s** (corrected 2026-09-17); the cache entry that outlives the
 * keystroke is the half that still argues for the debounce.
 */
const PRESET_DEBOUNCE_MS = 250;

/**
 * `description` caps here.
 *
 * Checked before sending rather than enforced with a `maxLength` on the box: a
 * `maxLength` truncates a PASTE silently, and the tail it drops is wording
 * somebody meant to keep.
 */
const MAX_DESCRIPTION_LENGTH = 16000;

/**
 * The default explanation for a delete control an account cannot use.
 *
 * ⚠️ **DELETE on a line is `ADMIN_ROLES` while POST and PATCH are
 * `WRITE_ROLES`.** A `basic_user` can add a line and edit it and cannot remove
 * it again — an asymmetry nobody would predict, and one that has to be visible
 * before the row is built rather than discovered afterwards.
 */
// 2026-10-01: deletes follow the set. *SUPERSEDED: "restricted to administrators".*
const DEFAULT_DELETE_DENIAL =
  'Your permission set does not include this. An administrator can change that.';

/* ── The two pinned columns ───────────────────────────────────────────────── */

/*
 * ⚠️ **AMOUNT AND THE ACTIONS COLUMN ARE PINNED TO THE TRAILING EDGE**: the
 * `<colgroup>` sums well past the wrapper's width, so without this the figure
 * the whole row exists to produce is never on screen. The quote grid's copy of
 * this block carries the measurements and the three details that make it work —
 * the offset equals the actions column's own width, the pinned cells must be
 * OPAQUE, and the leading hairline is an inset shadow because `border-collapse`
 * would leave a real border behind while the cell slid away from it.
 */
const PIN_AMOUNT = 'sticky right-[94px] z-10 shadow-[inset_1px_0_0_var(--hairline)]';
/** Hard against the trailing edge; the Amount column is offset by its width. */
const PIN_ACTIONS = 'sticky right-0 z-10';
/** An ordinary body row: the `Panel` surface the table sits on. */
const PIN_ROW_BG = 'bg-surface';
/**
 * A FAILED row's pinned cells. `bg-danger-soft/40` is translucent and a
 * translucent pinned cell is the defect the pins exist to fix, so the exact
 * composite is computed instead.
 */
const PIN_ROW_BG_FAILED = 'bg-[color-mix(in_srgb,var(--danger-soft)_40%,var(--surface))]';

/* ── Props ────────────────────────────────────────────────────────────────── */

/**
 * Whether a deal exists behind this grid.
 *
 * - `saved` — every edit is a request against `/deals/{id}/products`.
 * - `draft` — no deal exists yet. Rows are held locally, **no line request is
 *   issued**, and the payloads are reported through `onDraftLinesChange` for the
 *   parent to write once the deal has an id.
 */
export type DealLineItemsMode = 'saved' | 'draft';

interface DealLineItemsCommonProps {
  /**
   * **The header project's whole city set** — `deal.cities` on a saved deal, and
   * **empty on a create**.
   *
   * ⚠️ A deal created in this app has `projectId: null` for ever (`projectId` is
   * not a `FormValues` key on `DealFormModal` at all), so on create there is
   * nothing to derive a header city set from.
   *
   * ⚠️ **It is NOT a line's option list and is not what the server validates
   * against.** A line names its own project and `cityIds` is checked against
   * THAT project's cities; offering the header's set on a line whose project
   * differs would offer guaranteed 422s.
   *
   * It is kept for one honest use: naming a city we hold no other record of.
   * Never pass `/cities` here — the master offers rows every line write refuses
   * wholesale.
   */
  cities: ProjectCity[];
  /**
   * `deal.currency`. Rendered beside every figure; never converted.
   *
   * ⚠️ A deal's currency is nullable and most deals record none, so every figure
   * has to read correctly with no code beside it.
   */
  currency: string | null;
  /** `ability.canUpdate('deals')`. False renders the table read-only. */
  canEdit: boolean;
  /** `ability.canDelete('deals')` — admin tier, unlike create and update. */
  canDelete: boolean;
  /**
   * Why this account cannot delete, from `denialReason(ability, 'delete',
   * 'deals')`. Shown on the disabled control rather than hiding it, because a
   * missing control does not explain the create/delete asymmetry.
   */
  deleteDenialReason?: string;
  /**
   * Rendered above the save report — for a parent that created the deal in the
   * same action and needs to say so, or that needs to state, before the create,
   * that the two halves are not atomic. Someone who closes a form after a
   * partial failure believing nothing was created will make a second deal.
   */
  contextNote?: ReactNode;
}

export interface DealLineItemsSavedProps extends DealLineItemsCommonProps {
  mode?: 'saved';
  dealId: number;
  /**
   * Called after any successful line write — create, update or delete, and
   * **once per save run rather than once per row**.
   *
   * ⚠️ **This is NOT the quote and invoice grids' callback wearing the same
   * name, and on a deal it is genuinely OPTIONAL** — there, omitting it is a
   * defect, because every line write rewrites four header totals and returns
   * none of them. **A deal line write changes nothing on the deal at all:**
   *
   * - no header total columns on `d_deals` — `amount` is hand-entered, the
   *   basket never maintains it, and nothing here may make it;
   * - `deal.project` is the deal's OWN column, written by `projectId`, not
   *   derived from its lines;
   * - `deal.cities` is derived through **that header project**, so a line naming
   *   a different project does not move it either;
   * - there is no deal push, so there is nothing to queue.
   *
   * So a parent that shows only the deal record needs nothing here. It exists
   * for a parent displaying something of its own about the basket — a count, a
   * total — that wants to know the set moved. **Do not add a totals repair
   * behind it.**
   */
  onLinesChanged?: () => void;
  /**
   * How many rows are unsaved, and how many of those cannot be sent.
   *
   * ⚠️ **THIS EXISTS BECAUSE A PARENT WITH ITS OWN SAVE BUTTON WAS LYING.**
   * `DealFormModal` disabled its footer Save on `!isDirty` — where dirty means
   * the HEADER form — and its `handleSubmit` answered *"Nothing has changed
   * yet."* whenever the payload came back empty. So somebody who edited only
   * line items met a greyed-out "Save changes", and the one form-level sentence
   * they could reach asserted their edits did not exist. The same defect
   * `QuoteLineItemsTable` closed on 2026-09-01, found there in the audit log.
   *
   * Reported for saved mode only. Draft rows have nowhere to be saved to and
   * their parent already counts them through `onDraftBlockedChange`.
   */
  onUnsavedChange?: (state: { dirty: number; blocked: number }) => void;
  /**
   * Hands the parent this grid's own save so ONE control can finish the job.
   *
   * Called with the function after every render — the closure must be the
   * current one — and with `null` on unmount. It resolves `true` when every
   * row the run targeted landed, so a parent can decide whether to close.
   *
   * 🔴 **PASSING IT HIDES THE GRID'S OWN SAVE CONTROL, AND THAT IS A REVERSAL.**
   * *SUPERSEDED, from the quote grid's twin, which said the same thing there:
   * "The grid still renders its own Save and its own per-row report. This is a
   * second way to reach the same run, not a replacement for it."* Owner's
   * instruction, 10 September 2026: one button, reading "Save changes", with no
   * second save-looking control beside it. **The per-row report is UNCHANGED** —
   * only the button goes, and the run still reports row by row, which is the
   * half a parent genuinely cannot restate.
   *
   * ⚠️ **A parent that takes the save MUST call it.** The grid stops offering
   * any way to write a row the moment this prop is present, so a parent that
   * registers and never calls leaves line edits with nowhere to go — and
   * nothing on screen would say so.
   */
  registerSave?: (save: (() => Promise<boolean>) | null) => void;
  onDraftLinesChange?: never;
  onDraftBlockedChange?: never;
  /** There is a real basket to read, so nothing may seed rows over it. */
  initialDraftRows?: never;
}

/**
 * No deal exists yet: hold lines locally, issue no line request, and report them
 * upward. The parent writes them after the deal is created.
 *
 * 🔴 **THERE IS AN `initialDraftRows` HERE NOW — owner's instruction, 2026-09-13:
 * the "+" on a company row opens the create form with line items already seeded
 * from that company's PROJECTS OF INTEREST, for a quote, an invoice AND a deal.**
 * This reverses a decision taken on 2026-09-11, quoted below in full because two
 * of its three reasons were answered by the instruction rather than by being
 * wrong, and the third was not answered at all.
 *
 * ⚠️ *SUPERSEDED: "**THERE IS NO `initialDraftRows` HERE, AND THAT IS A DECISION
 * TAKEN ON 2026-09-11 RATHER THAN AN OMISSION.** The quote and invoice grids
 * gained one so a planned basket — and, since the same day, a PROJECT — can seed
 * their draft rows from a caller that knows both. The deal grid was deliberately
 * left out of that change. Three reasons, in the order they decided it:*
 *
 *  1. *"**Nothing asks for it.** The seam exists to close the sales-tracker
 *     board's `(company, project)` join, and the board's statuses advance on a
 *     QUOTE being sent and an INVOICE being billed, server-side. A deal advances
 *     no card, so a project reaching a deal's lines closes nothing that is
 *     currently open."* — 🔴 **ANSWERED: something asks for it now.** The owner
 *     named all three documents, and the caller is the company row rather than
 *     the board, so "a deal advances no card" is no longer the question being
 *     put. It remains true and is no longer a reason.
 *  2. *"**`DealFormModal` has no prefill seam at all** — only `lockedCompany`.
 *     Adding this prop alone would be inert; it needs a `DealFormPrefill` type,
 *     the prop, the remount-key fingerprint, a `seededRowOf`, the two seeded
 *     NAME maps the other grids carry, and a `?new=1` handler on `/deals` to
 *     build one. That is a feature, not a widening."* — ✅ **ANSWERED, and the
 *     list was right**: every item on it is being built, the modal's half by
 *     another session. ⚠️ **So this prop IS inert until that lands**, and a
 *     `tsc` error naming `initialDraftRows` at `DealFormModal` is the other half
 *     arriving, not a fault here.
 *  3. 🔴 **NOT ANSWERED, AND IT IS LIVE RATHER THAN HISTORICAL: "a deal, once
 *     created, cannot be un-created"**, and this grid withholds a blocked row
 *     from `onDraftLinesChange` rather than collecting a 422 the person can no
 *     longer see. **A seed is a new source of rows that have to pass that
 *     filter, and it deserves its own verification run.** Nothing about the
 *     owner's instruction touches that: an instruction says what to build, not
 *     that it has been driven. ⚠️ **This repo has no test setup, so `tsc` green
 *     says the types line up and nothing about what renders.** Treat every
 *     sentence below as built rather than verified until somebody has raised a
 *     seeded deal in a browser and read the lines back.
 *
 * ⚠️ **THE COST THE OLD BLOCK RECORDED IS WHAT THIS CLOSES**, and it is kept
 * because it is the reason the seam is worth having: `f_deal_products` carries a
 * `project_id` and the identity is `(deal_id, product_id, project_key)`, so a
 * deal line CAN name a project and this grid's picker already sets one. A deal
 * raised with no project on any line is a correct document that leaves the
 * board quietly wrong, and `POST /deals/{id}/convert` copies the line overrides
 * onward, so the gap travelled one document further into the quote the deal
 * becomes. **Nothing failed when it was forgotten** — which is exactly why a
 * seed is better than asking somebody to remember.
 */
export interface DealLineItemsDraftProps extends DealLineItemsCommonProps {
  mode: 'draft';
  /** There is no deal to address, so there is no id to take. */
  dealId?: never;
  onLinesChanged?: never;
  onUnsavedChange?: never;
  registerSave?: never;
  /**
   * The lines as they stand, in grid order, **exactly the body
   * `POST /deals/{id}/products` takes** — built by the same `createPayload()`
   * the saved mode POSTs, so the two cannot diverge. Each carries an explicit
   * `sortOrder` (its index), because the parent may write them in any order and
   * `sort_order ASC, id ASC` is the order the basket reads back in.
   *
   * ⚠️ **Only rows that would be ACCEPTED are included.** A row with no product,
   * a bad quantity, an out-of-range percentage, a city its project does not
   * declare, or a `(product, project)` pair another row already claims is
   * withheld and counted by {@link onDraftBlockedChange} instead. Creating the
   * deal and then collecting a 422 for a line the person can no longer see is
   * the failure this split prevents — **and a deal, once created, cannot be
   * un-created** — so read the blocked count before submitting, never
   * `lines.length` alone.
   *
   * Fires on every change and only on a change; the callback's own identity is
   * not a trigger, so an inline arrow in the parent is safe.
   */
  onDraftLinesChange: (lines: DealProductCreateInput[]) => void;
  /**
   * How many rows are currently withheld from `onDraftLinesChange`. `0` means
   * every row on screen will be written.
   *
   * Optional, and the grid already refuses to hide the problem — it renders its
   * own blocking banner. This exists so the parent can disable its own "Create
   * deal" button rather than letting someone submit and silently lose a row.
   */
  onDraftBlockedChange?: (blockedRows: number) => void;
  /**
   * Rows the grid OPENS WITH — a company's projects of interest, seeded once at
   * mount. Owner's instruction, 2026-09-13; see this interface's own header.
   *
   * ⚠️ **Read in the `useState` INITIALISER and never again.** An effect that
   * re-ran would put back rows the person had already edited or deleted, and
   * deleting a line and watching it come back is worse than no prefill at all.
   * So the array's identity is not a trigger and a parent may build it inline —
   * which is what a modal will do, on every keystroke in its own header fields.
   *
   * ⚠️ **A SEED IS NOT A LINE.** These are ordinary draft rows the moment they
   * exist: editable, deletable, and written through the same
   * {@link onDraftLinesChange} payloads as a row somebody typed. Nothing here is
   * pinned, nothing is written twice, and a seeded row that cannot be saved is
   * withheld and counted by {@link onDraftBlockedChange} exactly like any other.
   */
  initialDraftRows?: readonly PlannedProductLine[];
}

export type DealLineItemsTableProps = DealLineItemsSavedProps | DealLineItemsDraftProps;

/* ── The draft a row is edited as ─────────────────────────────────────────── */

/**
 * What the Price box means.
 *
 * - `inherit` — the box follows the computed price ({@link InheritedPrice}). A
 *   project price is SENT; otherwise the key is omitted on a new row and the
 *   server snapshots the catalogue pro-rata (2026-10-18). An existing row keeps
 *   its stored price unless that price is still the computed price of the
 *   saved selection, in which case a selection change reprices (and sends) it.
 * - `value` — an explicit decimal string.
 * - `none` — an explicit `null`: **no price on this line**, which is not the
 *   same fact as a price of `"0.00"`, even though both make `lineTotal` read
 *   `"0.00"` (the column `COALESCE`s a null price to 0).
 *
 * ⚠️ Sending an explicit price where the user set none is what once put `100` on
 * a line whose catalogue price was 17,500. `inherit` must never be spelled `''`,
 * `null` or `0`.
 */
type PriceMode = 'inherit' | 'value' | 'none';

interface InclusionDraft {
  inclusionId: number;
  /** Empty means "omit the key", which the server reads as 1. Zero is refused. */
  quantity: string;
}

/**
 * One row's editable state.
 *
 * ⚠️ **THERE IS NO `taxRate` AND NO `xeroTaxType` KEY HERE, AND THAT ABSENCE IS
 * THE GUARD.** `f_deal_products` has neither column; both write schemas are
 * `.strict()`, so either key is a 422 naming it. Because they are not `Draft`
 * keys, {@link createPayload} and {@link updatePayload} have no way to emit
 * them, `ROW_WRITE_KEYS` cannot claim a 422 naming them can be marked, and a
 * call site that tries to set one does not compile. That is structural, which a
 * comment asking the next person to remember is not.
 *
 * ⚠️ **There is no `allCities` key either** — deal lines have no such column.
 * See this file's header.
 */
interface Draft {
  productId: string;
  /**
   * The text this line carries — **`null` means DERIVE, never "empty"**.
   *
   * While it is `null` the cell follows {@link composeLineDescription} over the
   * line's project, item and cities. A string is the person's own wording,
   * stored verbatim and frozen against later product and city edits. `''` is not
   * a third state — it is folded into `null` by {@link descriptionForWrite}
   * before any payload is built.
   *
   * ⚠️ **Nothing consumes this on a deal.** There is no deal push and no deal
   * document; it is stored, and it survives into the quote or invoice the deal
   * is converted into.
   */
  description: string | null;
  /**
   * The LINE's own project. `''` is "no project", which is a real and legal
   * state — some products are not sold through projects at all.
   *
   * It is half of the line's identity: `(product, project)`.
   */
  projectId: string;
  /**
   * **The WHOLE SET of cities this line runs in.** `[]` is "not city-specific",
   * which is the ORDINARY case. Order is the server's own per-line `sortOrder`,
   * then the order the user added in. Never re-sorted.
   *
   * ⚠️ *SUPERSEDED: "never a source of arithmetic: the money is on the line, not
   * per city."* **City pro-rata (owner 2026-10-18):** an INHERITED price is the
   * catalogue full-tour rate × selected ÷ the project's city count
   * (`@/lib/city-pro-rata`), so 2 of 5 cities at 17,500 is a unit price of
   * 7,000. The money still sits on the unit price: quantity is never derived
   * from cities and no total is multiplied by a city count.
   *
   * **Project pricing (owner 2026-10-27)** comes first: a project price entry
   * for the product, deal currency and tier prices the line per city (or flat),
   * through `computeLinePrice`, and that figure IS sent as `unitPrice`. The
   * catalogue pro-rata is the fallback, still left to the server on a create. A
   * saved price that still equals its computed price follows a selection
   * change, a typed one does not. See {@link savedPriceFollows}.
   */
  cityIds: number[];
  /**
   * **SEED's regions of focus for this line — the WHOLE SET.** Migration 112,
   * `f_deal_product_regions`; read as `regions`, written as `regionIds`.
   *
   * ⚠️ **A deal line has carried this on the wire since migration 112 and this
   * grid could not send it until 2026-08-31.** `DealProductCreateInput.regionIds`
   * was declared, `deal-products.schema.ts` accepted it, `DealProduct.regions`
   * came back on every read — and no deal screen had a control. The identical
   * gap existed on quotes and invoices, where the picker sat in
   * `QuoteLineFormModal` / `InvoiceLineFormModal`, which nothing mounted.
   *
   * ✅ **Both of those files were DELETED on 2026-09-19** and the control now
   * lives in `QuoteLineItemsTable` / `InvoiceLineItemsTable`. The two names are
   * kept here as the record of where the picker used to sit — ⛔ **neither is
   * live code, and a grep for either finds this note rather than a file.**
   *
   * ⚠️ **There is no `allRegions` flag and none may be invented** — measured in
   * `deal-products.schema.ts`, which is `.strict()` and declares `regionIds` and
   * nothing beside it. The "All regions" box in {@link RegionsControl} writes an
   * explicit set of ids; it is a select-all, not a rule.
   *
   * ⚠️ **`regionIds` has no `allCities` twin here in the OTHER direction
   * either** — `f_deal_products` has no `all_project_cities` column at all, so
   * unlike the quote and invoice grids there is no city flag beside this. See
   * this file's header; that absence is unchanged.
   *
   * {@link regionsKey} is order-INSENSITIVE where {@link citiesKey} is not: the
   * order of a line's regions is not stored (the repository sorts by the
   * master's own `sortOrder`) and nothing prints them, so a reorder is not a
   * change and must not light up "Save".
   */
  regionIds: number[];
  quantity: string;
  priceMode: PriceMode;
  price: string;
  /**
   * The line's price tier (migration 328, owner 2026-10-27). `null` = the
   * default tier, and it is what a new line starts with. Sent only beside a
   * project; a product change clears it with the project.
   */
  priceTierId: number | null;
  /**
   * The discount as a PERCENTAGE, as typed. Live only while
   * {@link Draft.discountAmount} is `null`.
   */
  discount: string;
  /**
   * The discount as an EXACT MONEY AMOUNT, as typed — and the MODE SWITCH.
   *
   *  - `null` — the box is taking a PERCENTAGE, which is the ordinary case;
   *  - a string (`''` included) — the box is taking an AMOUNT.
   *
   * ⚠️ **This is draft state, unlike the screen-only map it replaced.** It has
   * a column behind it now (`discount_amount`, mutually exclusive with
   * `discount`), so it belongs in `ROW_WRITE_KEYS`, counts towards `isDirty`,
   * and is emitted by both payload builders — all the things the old
   * client-side convenience was deliberately kept OUT of.
   *
   * ⚠️ **NOTHING CONVERTS BETWEEN THE TWO ANY MORE, AND NOTHING MAY.** An
   * amount used to be turned into the percentage that produces it, which was
   * lossy by POUNDS: £1,000 off an £18,000 line became `5.56%`, taking
   * £1,000.80. The column removed the need and the conversion is deleted —
   * `percentageForAmount` no longer exists in `@/lib/decimal`. Switching mode
   * moves the SWITCH and never rewrites the other box.
   */
  discountAmount: string | null;
  /** Ordered — the index becomes each entry's `sortOrder`. */
  inclusions: InclusionDraft[];
  /**
   * The line's INTERNAL note (owner 2026-10-18) — never on a customer document,
   * never sent to Xero, copied onto the quote or invoice the deal converts into.
   *
   * ⚠️ **Read only by {@link createPayload}, and deliberately NOT by
   * {@link updatePayload}.** On a saved line the note is written on its own,
   * immediately, by {@link LineNoteField} with a notes-only PATCH — so it never
   * lights up "Save", never rides along with a money edit and is never re-sent.
   * On a row that has never been saved there is no line to PATCH, so the note
   * waits here and travels in the POST (and in draft mode's
   * `onDraftLinesChange` payloads) with everything else.
   */
  notes: string | null;
}

type RowState = 'idle' | 'saving' | 'saved' | 'failed';

/**
 * A seeded row whose unit price does not divide the plan's amount evenly.
 *
 * Every figure here is a STRING taken from, or computed once against, the seed —
 * never re-derived at render. `lineAmount` comes from
 * `computeGeneratedLinePreview`, the one place money arithmetic lives, so the
 * figure the notice names is the same arithmetic the Amount column shows.
 */
interface PlannedShortfall {
  /** The plan's flat amount for the line, verbatim, so the sentence names it. */
  plannedAmount: string;
  /** The seeded unit price. Displayed, and the stand-down test. */
  unitPrice: string;
  /** The seeded quantity, as the box holds it. Displayed, and the stand-down test. */
  quantity: string;
  /** `quantity × unitPrice` — what the line comes to at the seeded figures. */
  lineAmount: string;
}

interface Row {
  /** Stable across re-renders and independent of the server id, which new rows lack. */
  key: string;
  /** The last state the server confirmed. `null` on a row that has never been saved. */
  saved: DealProduct | null;
  draft: Draft;
  state: RowState;
  /** Form-level failure text — a 409, a role refusal, a strict-schema rejection. */
  error: string | null;
  fieldErrors: Record<string, string>;
  /**
   * The project/city refusal the last save collected, classified. `null` on any
   * failure that was not one of the seven — `lineRefusalOf` returns `null`
   * rather than guessing.
   *
   * Kept beside `fieldErrors` because it answers two questions the map alone
   * cannot: WHICH chips are wrong, and whether the fix is even on this screen.
   */
  refusal: LineRefusal | null;
  /**
   * The PLANNED quantity this row was seeded from, when that figure **cannot be
   * a line quantity** — `null` on every other row, seeded ones included.
   *
   * ⚠️ **It is a REFUSAL FLAG, not a display value, and it is the only thing
   * standing between a planned `"2.50"` and a line that silently bills 1.** An
   * empty quantity box is ordinarily fine: {@link createPayload} omits the key
   * and the server defaults it to 1, which is the right answer for a row
   * somebody typed and left alone. It is the WRONG answer for a row seeded from
   * a figure that was measured and could not be expressed — there, an omitted
   * key is a number nobody chose, arriving on a customer's document with a 201
   * and nothing on either side of the wire disagreeing. {@link validateRow}
   * reads this and refuses the row while the box is empty, which is what puts it
   * in `blockedKeys`, withholds it from `onDraftLinesChange` and disables the
   * parent's Create.
   *
   * The string is the planned figure verbatim, so the message can name it.
   *
   * ⚠️ **REQUIRED, not optional**, and that is the guard rather than a style:
   * an optional member would let a row be built without deciding, and `tsc`
   * refusing {@link rowOf} and `addRow` is the only check nobody can forget.
   */
  plannedQuantity: string | null;
  /**
   * The plan's own flat amount for a seeded row whose seeded PRICE cannot
   * express it exactly — `null` on every other row, seeded ones included.
   *
   * ⚠️ **It is a NOTICE flag, not a refusal, and that is the whole difference
   * from {@link Row.plannedQuantity} above.** An unstatable quantity has no
   * right answer and the row is refused until somebody supplies one. An inexact
   * price HAS an answer and it is right to the penny in every case that divides:
   * `10000.00` over 3 seeds `3333.33` and the line comes to `9999.99`, which is
   * a real figure a person may perfectly well accept. Blocking the save over a
   * cent would refuse the feature; saying nothing would put the cent on a
   * document a customer is billed against with nobody able to see it, because
   * **this is the only moment anybody can** — after the create there is no plan
   * beside the line to compare it to.
   *
   * ⚠️ **Both boxes are carried so the notice can STAND DOWN.** It describes the
   * SEEDED state, so the moment the person changes the price or the quantity it
   * stops being true of the row and goes. Without them the sentence would go on
   * naming a total the row no longer has.
   *
   * ⚠️ **REQUIRED, for the reason {@link Row.plannedQuantity} is.**
   */
  plannedShortfall: PlannedShortfall | null;
}

/* ── Pure helpers ─────────────────────────────────────────────────────────── */

function draftOf(line: DealProduct): Draft {
  return {
    productId: String(line.product.id),
    /*
     * Straight through, both ways. `null` opens the cell derived-and-untouched
     * and a string opens it typed — so "has the user typed" is a fact about the
     * RECORD, not only about this session.
     */
    description: line.description,
    projectId: line.project === null ? '' : String(line.project.id),
    /*
     * Already ordered by the server's own per-line `sortOrder`, so keep that
     * order. Re-sorting it — by name, "to be tidy" — is a change nobody asked
     * for that this grid would then offer to save.
     */
    cityIds: line.cities.map((city) => city.id),
    /*
     * `regions` is always present and `[]` when there are none — never `null` —
     * so there is no `?? []` here and none is needed. The order is the master's
     * `sortOrder`, which {@link regionsKey} ignores; it is kept only so the chips
     * read in a stable order rather than because it means anything.
     */
    regionIds: line.regions.map((region) => region.id),
    quantity: String(line.quantity),
    priceMode: line.unitPrice === null ? 'none' : 'value',
    price: line.unitPrice ?? '',
    // `undefined` is an older API; both read as the default tier.
    priceTierId: line.priceTierId ?? null,
    discount: line.discount,
    // A stored amount opens the row in amount mode; `null` opens it on the
    // percentage. The server keeps the pair exclusive, so this cannot be a row
    // that holds both.
    discountAmount: line.discountAmount,
    // Already ordered by the server; keep the order it arrived in.
    inclusions: line.inclusions.map((entry) => ({
      inclusionId: entry.id,
      quantity: String(entry.quantity),
    })),
    // Draft state only: the saved row's note is rendered off `line.notes`
    // itself, where `undefined` (an older API) still means "render nothing".
    notes: line.notes ?? null,
  };
}

/**
 * A new row starts with no project and no cities.
 *
 * The header project is deliberately NOT seeded (and on a deal created here
 * there is none to seed from anyway): a project sells exactly one product and
 * the row has not chosen one yet, so pre-filling puts a value in the box that
 * the very next choice can turn into a refusal.
 */
/** The key of the blank row a new document opens with; Add row keys are `new-N`. */
const DEFAULT_BLANK_ROW_KEY = 'new-default';

/** True while a row still holds exactly what {@link blankDraft} gave it. */
function isUntouchedBlankDraft(draft: Draft): boolean {
  return JSON.stringify(draft) === JSON.stringify(blankDraft());
}

/**
 * A row invented on screen — by Add row, or the one blank row a new deal
 * opens with. One builder for both, so the two cannot differ.
 */
function blankRowOf(key: string): Row {
  return {
    key,
    saved: null,
    draft: blankDraft(),
    state: 'idle',
    error: null,
    fieldErrors: {},
    refusal: null,
    /*
     * A row somebody added by hand answers to no plan: its empty quantity
     * box means the server's default of 1, which is the right answer here
     * and the wrong one on a seeded row. See {@link Row.plannedQuantity}.
     */
    plannedQuantity: null,
    plannedShortfall: null,
  };
}

function blankDraft(): Draft {
  return {
    productId: '',
    // `null` is deriving, which is where a new line starts and where almost
    // every line stays. It is not "blank".
    description: null,
    projectId: '',
    cityIds: [],
    // A new line names its regions or names none, and there is no "all" to start
    // from — see {@link Draft.regionIds}.
    regionIds: [],
    quantity: '1',
    // ⚠️ Never 'value' with an empty price. `inherit` is the only spelling of
    // "snapshot the catalogue price".
    priceMode: 'inherit',
    price: '',
    priceTierId: null,
    discount: '',
    // Percentage mode, which is where nearly every line stays.
    discountAmount: null,
    inclusions: [],
    notes: null,
  };
}

function rowOf(line: DealProduct): Row {
  return {
    key: `line-${line.id}`,
    saved: line,
    draft: draftOf(line),
    state: 'idle',
    error: null,
    fieldErrors: {},
    refusal: null,
    /* A stored line HAS a quantity; there is no plan left unsettled here. */
    plannedQuantity: null,
    /* And a stored price, which no plan is standing beside to disagree with. */
    plannedShortfall: null,
  };
}

/**
 * A PLANNED product, as a draft row of this grid — `mode: 'draft'` only, and
 * only at mount. See {@link DealLineItemsDraftProps.initialDraftRows}.
 *
 * ⚠️ **THE QUANTITY IS THE WHOLE OF THIS FUNCTION'S JUDGEMENT, and both wrong
 * answers are silent.** The mapper has already decided whether the planned
 * figure can be a line quantity; what happens when it cannot is decided here:
 *
 *  - **NOT rounded.** `"2.50"` → `1` is a figure nobody chose, printed on a
 *    document a customer is billed against, with nothing anywhere disagreeing.
 *  - **NOT merely left blank.** An empty box means "omit the key", and an
 *    omitted `quantity` is **1** on the server — the same wrong number by a
 *    longer route, and harder to see because no line of code states it.
 *
 * So the box opens empty AND the row carries {@link Row.plannedQuantity}, which
 * {@link validateRow} turns into a refusal. The person supplies the figure; the
 * grid refuses to let the deal be created until they do.
 *
 * ⚠️ **CITIES, REGIONS AND THE DISCOUNT ARE A BLANK DRAFT ROW ON PURPOSE.** A
 * seed records what is to be sold, for which project, and how much of it —
 * nothing else. ⛔ **Never seed `cityIds` or `regionIds` beside the project**:
 * both are whole-set REPLACE writes and a seed cannot know which were meant.
 * Spreading over {@link blankDraft} is what leaves the row in **exactly the
 * state a freshly-chosen project leaves it in**, which is also why the project
 * and its cities stay one patch here without anything having to arrange it — the
 * server judges that pair POST-MERGE, and a seeded `cityIds` would be re-checked
 * against a project the person never picked.
 *
 * ⛔ **NO CLIENT-SIDE CHECK THAT THIS PROJECT SELLS THIS PRODUCT.** That is
 * refusal 5 and it belongs to the server, which judges the pair POST-MERGE;
 * reproducing it here would refuse a pair the API accepts. The row shows both
 * controls, the person can change either, and a 422 routes to `projectId` on
 * this row.
 */
function seededRowOf(planned: PlannedProductLine, index: number): Row {
  return {
    /*
     * `seed-` rather than `new-`: `addRow` counts from `new-0` on its own
     * sequence, so a shared prefix would collide on the first added row, and two
     * rows sharing a key is a React reconciliation bug rather than a display one.
     */
    key: `seed-${index}`,
    saved: null,
    draft: {
      ...blankDraft(),
      productId: String(planned.productId),
      /*
       * ⚠️ **The absent branch leaves {@link blankDraft}'s `''`, which is "no
       * project", and writes nothing.** {@link createPayload} tests `!== ''`
       * before it sends, so a seed that named no project omits the key entirely
       * — it does not send a `projectId` of `0`, an empty string or a `null`,
       * each of which is a different wrong instruction on a `.strict()` schema.
       */
      ...(planned.project === undefined ? {} : { projectId: String(planned.project.id) }),
      /*
       * ⚠️ *Narrows "Never seed `cityIds`" above* — owner 2026-10-23: the
       * sales-tracker band "+" seeds EVERY city of the line's project, stated
       * by the caller in `planned.cityIds`. Only beside a project; every other
       * seed leaves the key `undefined` and the row's cities empty as before.
       */
      ...(planned.project !== undefined && planned.cityIds !== undefined
        ? { cityIds: [...planned.cityIds] }
        : {}),
      // `blankDraft()` opens at '1'; an unstatable plan must NOT inherit that.
      quantity: planned.lineQuantity === null ? '' : String(planned.lineQuantity),
      /*
       * ⚠️ **The `null` branch leaves `blankDraft()`'s `'inherit'` and empty box
       * exactly as they were, and that is the load-bearing half.** ⛔ It is NOT
       * `'none'` — that sends an explicit `null`, which is *"this line has no
       * price at all"*, a claim about the line rather than the absence of one —
       * and it is NOT `'value'` with an empty string, which {@link validateRow}
       * refuses and which would open the form already broken. An unpriced plan
       * says nothing about price, and `'inherit'` is the only spelling of that:
       * the key is omitted and the server snapshots the catalogue figure when
       * the line is written, exactly as every unplanned line has always done.
       */
      ...(planned.unitPrice === null
        ? {}
        : { priceMode: 'value' as const, price: planned.unitPrice }),
      /*
       * ⚠️ **SEEDING A DESCRIPTION PUTS THE CELL IN ITS TYPED STATE
       * PERMANENTLY.** While `draft.description` is `null` the cell IS the
       * derivation — product, then cities — and it rewrites itself as the row
       * changes. A value here stops that for good; "Reset to derived" is the
       * only way back, and the cell says which state it is in.
       *
       * ⛔ **SO THE `null` CASE MUST LEAVE THE KEY ALONE, NOT WRITE `''`.**
       * `blankDraft()` opens it `null`, which means DERIVING; an empty string is
       * indistinguishable in the payload and opposite in meaning — every seeded
       * line would arrive frozen and blank, on a document a customer reads, with
       * nothing on screen explaining why the product name never appeared. And
       * never the product NAME either: that freezes the cell on a value that
       * merely looks derived, so the first city added would silently stop
       * appearing.
       */
      ...(planned.description === null ? {} : { description: planned.description }),
    },
    state: 'idle',
    error: null,
    fieldErrors: {},
    refusal: null,
    plannedQuantity: planned.lineQuantity === null ? planned.quantity : null,
    plannedShortfall: plannedShortfallOf(planned),
  };
}

/**
 * The shortfall a seeded row carries, or `null` when there is nothing to say.
 *
 * ⚠️ **`unitPriceExact` is the AUTHORITY, and this must not second-guess it.**
 * The mapper did the division; whether it came out even is its answer, not a
 * comparison to be re-run here against two decimal strings whose scales need not
 * match (`"10000"` and `"10000.00"` are the same amount and different strings,
 * and a string compare would report a shortfall on a plan that divides
 * perfectly).
 *
 * The line figure goes through `computeGeneratedLinePreview` — the one place
 * money arithmetic lives — at a neutral discount, because the question is
 * whether the PRICE divides the plan. A discount applied afterwards is a second,
 * deliberate decision and must not read as the seed having gone wrong. A preview
 * that answers `null` yields no shortfall: nothing is invented.
 *
 * ⚠️ **`taxRate: ''` IS THE SAME STATEMENT {@link previewInputOf} MAKES, not a
 * default being leaned on.** `f_deal_products` has no tax column, so there is
 * nothing to read; the shared preview requires the field because it also serves
 * quote and invoice lines, and at a blank rate its step 1 is byte for byte the
 * deal's own `line_total` formula. `lineSubtotal` is therefore the deal line's
 * one generated figure — ⛔ **do not reach for `lineTotal` here thinking it is
 * the "with tax" one; on a deal the two are equal and neither is a second
 * figure this grid may invent.**
 */
function plannedShortfallOf(planned: PlannedProductLine): PlannedShortfall | null {
  if (planned.unitPriceExact) return null;
  if (planned.unitPrice === null || planned.plannedAmount === null) return null;
  if (planned.lineQuantity === null) return null;
  const quantity = String(planned.lineQuantity);
  const lineAmount = computeGeneratedLinePreview({
    quantity,
    unitPrice: planned.unitPrice,
    discount: '',
    discountAmount: null,
    taxRate: '',
  })?.lineSubtotal;
  if (lineAmount === undefined) return null;
  return {
    plannedAmount: planned.plannedAmount,
    unitPrice: planned.unitPrice,
    quantity,
    lineAmount,
  };
}

/** An order-sensitive fingerprint, because the index IS the stored `sortOrder`. */
function inclusionsKey(entries: InclusionDraft[]): string {
  return entries.map((entry) => `${entry.inclusionId}:${entry.quantity || '1'}`).join('|');
}

/**
 * An ORDER-SENSITIVE fingerprint of the city set.
 *
 * The stored `sortOrder` is this array's order, and reordering the cities on a
 * line changes the string a converted document will print — "Bangalore, Mumbai"
 * and "Mumbai, Bangalore" are the same set and two different sentences. So a
 * reorder is a change and must be saved as one.
 */
function citiesKey(ids: number[]): string {
  return ids.join(',');
}

/**
 * An ORDER-INSENSITIVE fingerprint of the region set.
 *
 * ⚠️ **Deliberately the opposite of {@link citiesKey}, which sits directly above
 * it, and the difference is not an oversight.** A line's city ORDER is stored
 * (`f_deal_product_cities.sort_order`) and prints into the description a
 * converted document carries, so reordering cities is a real change. A line's
 * region order is stored by nothing this app can influence — the repository
 * returns them by the `/regions` master's own `sortOrder` and deliberately does
 * not read `f_deal_product_regions.sort_order` — and nothing prints them. So a
 * reorder here expresses nothing, and a sensitive key would light up "Save" for
 * a PATCH whose body is a set the server already holds.
 */
function regionsKey(ids: number[]): string {
  return [...ids].sort((a, b) => a - b).join(',');
}

/**
 * The draft's description as the WRITE means it — `null` for "derive".
 *
 * ⚠️ **An emptied box is a Reset, folded into one HERE rather than left to the
 * server.** The server normalises `{ description: "" }` to `null` too, but then
 * the two ways of asking for the derivation back travel as two different bodies
 * that happen to agree today.
 *
 * The emptiness test TRIMS and the value sent does not: three spaces is emptied,
 * but a description that survives the test is stored **verbatim**.
 */
function descriptionForWrite(value: string | null): string | null {
  if (value === null || value.trim() === '') return null;
  return value;
}

/**
 * The server's own uniqueness key: **(product, project)**.
 *
 * `UNIQUE (deal_id, product_id, project_key)`, where `project_key` is a
 * generated `COALESCE(project_id, 0)` rather than `project_id` itself: MySQL
 * treats NULLs as distinct in a unique index, so keying on the raw column would
 * look tighter and be looser — it would lose duplicate protection for every
 * project-less line, which on a deal is most of them.
 *
 * ⚠️ **The CITY is not part of this.** A line runs in a set of cities, so the
 * set can never be an identity.
 */
function pairKey(productId: string, projectId: string): string {
  return `${productId}:${projectId === '' ? '0' : projectId}`;
}

/* ── What a line starts with ──────────────────────────────────────────────── */

/**
 * The cache key for one `(product, quantity)` resolve.
 *
 * ⚠️ **Both halves, always.** The ladder answers per quantity, so a cache keyed
 * on the product alone would hand a line of 20 the band that covers 1 — a real
 * set of inclusions, for the wrong order size, with nothing on screen wrong.
 *
 * The line's PROJECT is the third segment (empty for no project), because a
 * project ladder for (project, product) wins over the product's own
 * (2026-10-18). Parsed back out with `split(':')`.
 */
function presetCacheKey(productId: number, quantity: number, projectId: string): string {
  return `${productId}:${quantity}:${projectId}`;
}

/**
 * The quantity this row would be saved at, or `null` when it is not askable.
 *
 * ⚠️ **An EMPTY box is 1, not "no quantity".** {@link createPayload} omits the
 * key when the box is blank and the column defaults to 1, so the band that
 * covers the line is the band that covers 1 — asking about nothing, or skipping
 * the resolve, would leave a blank-quantity line unpreset for a reason the
 * person cannot see.
 *
 * ⚠️ **A malformed box answers `null` and is NOT coerced.** `validateRow`
 * already refuses it at `quantity`; resolving `Number('2x')` as 2 would preset a
 * set against a figure the row cannot save.
 */
function presetQuantityOf(draft: Draft): number | null {
  if (draft.quantity === '') return 1;
  const quantity = Number(draft.quantity);
  return Number.isInteger(quantity) && quantity >= 1 ? quantity : null;
}

/**
 * Whether this row is one the catalogue may still seed into.
 *
 * ⛔ **A SAVED LINE NOBODY HAS MOVED IS NEVER SEEDED.** `@/lib/line-presets`
 * guards against overwriting a HAND-EDITED figure; it cannot guard against
 * overwriting a STORED one, because it is handed a draft and knows nothing about
 * what the server holds. Without this gate every existing line whose product
 * recommends a discount would be rewritten the moment the grid loaded — going
 * dirty on its own, lighting up "Unsaved" and the parent's Save button, and
 * offering to change a figure agreed months ago. Nothing would fail.
 *
 * So the seed follows the two events the owner named: a line NAMING a product
 * (every row that has never been saved, including every row in draft mode), and
 * a line whose product or quantity has been MOVED away from what was stored.
 * Once such a row saves, the reload gives it a `saved` that matches its draft
 * and it stops seeding — which is what stops a saved line being re-seeded for
 * ever.
 */
function seedsFromCatalogue(row: Row): boolean {
  if (row.saved === null) return true;
  const base = draftOf(row.saved);
  return row.draft.productId !== base.productId || row.draft.quantity !== base.quantity;
}

/**
 * A row's inclusions in the shape `@/lib/line-presets` compares.
 *
 * ⚠️ **`effectiveInclusionQuantity` rather than `Number()`**, so a blank count
 * reads as the 1 the server will store and a malformed one does not compare as
 * `NaN` — which is never equal to itself, so every comparison would report the
 * line as hand-edited and the preset would silently stop following the quantity.
 * It is the same normalisation {@link inclusionsKey} applies for dirtiness.
 */
function lineInclusionDraftsOf(entries: readonly InclusionDraft[]): LineInclusionDraft[] {
  return entries.map((entry) => ({
    inclusionId: entry.inclusionId,
    quantity: effectiveInclusionQuantity(entry.quantity),
  }));
}

/** The same set back in the shape the editor holds, counts as typed text. */
function inclusionDraftsOf(entries: readonly LineInclusionDraft[]): InclusionDraft[] {
  return entries.map((entry) => ({
    inclusionId: entry.inclusionId,
    quantity: String(entry.quantity),
  }));
}

/**
 * The value the inclusions rule is asked about, for one row: **this row's
 * product's link-table offer**.
 *
 * ⚠️ **TWO distinguishable "nothing" results, and merging them is a real bug.**
 *
 * - {@link NO_PRODUCT_CHOSEN} — the item box is empty. A fact this row holds
 *   with certainty. Answers `'not-chosen'`, so nothing at all renders where the
 *   block would be.
 * - `null` — an item IS chosen and its offer is unread: in flight, or the read
 *   failed. Answers `'unknown'` and **still shows the block**, on purpose —
 *   hiding a feature on a load failure is unrecoverable from the user's side.
 * - A {@link ProductInclusionOffer} — the read succeeded. `inclusionIds: []` is
 *   a real answer meaning *an admin has linked none*, and is **not** the same
 *   value as `null`.
 */
function inclusionsOfferOf(
  row: Row,
  offers: ReadonlyMap<number, ProductInclusionOffer>,
): ProductInclusionOffer | NoProductChosen | null {
  if (row.draft.productId === '') return NO_PRODUCT_CHOSEN;
  return offers.get(Number(row.draft.productId)) ?? null;
}

/** One row's answer from `@/lib/product-inclusions`, resolved once. */
interface RowInclusions {
  offers: boolean;
  offerUnread: boolean;
  /** ⚠️ `null` means DO NOT NARROW — an unread offer, not "none linked". */
  offeredIds: ReadonlySet<number> | null;
  denialReason: string | null;
  orphaned: boolean;
}

/**
 * The fallback for a key the map has not caught up with: show, do not narrow.
 *
 * *SUPERSEDED 11 Sep 2026: a fourth field, `required: false`, resolved the same
 * uncertainty in the opposite direction — towards demanding less — because a
 * `true` would have turned an unresolved row into a line that could not be
 * written. The at-least-one rule is gone, so there is nothing left to demand.*
 */
const INCLUSIONS_SHOWN: RowInclusions = {
  offers: true,
  offerUnread: true,
  offeredIds: null,
  denialReason: null,
  orphaned: false,
};

/**
 * What one draft's discount actually MEANS on the wire.
 *
 * ⚠️ **The two columns are mutually exclusive: sending both non-zero is a 422
 * naming `discountAmount`.** So this resolves the pair once, and both payload
 * builders emit it as a pair — whichever half is being set says the other is
 * off in the same request, because a request that sets one and leaves the other
 * stored is the refusal.
 *
 * A blank `discount` stays blank rather than becoming `'0'`: on a create it
 * means "take the server's default" and on a patch it means "leave the stored
 * value alone", and only the callers can tell those apart.
 *
 * ⚠️ **An emptied AMOUNT box is a real zero, not a blank.** The percentage
 * control is not on screen in that mode, so "no amount" can only mean "no
 * discount on this line" — leaving a stored percentage in place would show an
 * empty discount box over a line that is still discounted.
 */
interface DiscountWrite {
  /** A percentage, as typed. `''` means "say nothing about it". */
  discount: string;
  /** An exact amount, as typed, or `null` for "not discounted by amount". */
  discountAmount: string | null;
}

function discountWriteOf(draft: Draft): DiscountWrite {
  if (draft.discountAmount === null) {
    return { discount: draft.discount.trim(), discountAmount: null };
  }
  const amount = draft.discountAmount.trim();
  return { discount: '0', discountAmount: amount === '' ? null : amount };
}

/**
 * What an `inherit` row resolves to (owner 2026-10-27, `@/lib/line-pricing`).
 *
 * - `project_price` — the line's project has a price entry for this product,
 *   currency and tier: the figure is SENT as `unitPrice` (the server resolves
 *   only a create that omits it).
 * - `catalogue` — the legacy pro-rata; a create still omits the key so the
 *   server snapshots the same figure, a saved line sends it once it moved.
 * - `stored` — a saved line whose price was typed over: a blank box keeps it.
 */
interface InheritedPrice {
  unitPrice: string | null;
  /** The resolver's rounding discount (a quantity override that is not whole cents). */
  discountAmount: string | null;
  /** True while the row's discount is untouched, so the resolver's replaces it. */
  discountFollows: boolean;
  source: ComputedLinePrice['source'] | 'stored';
}

/**
 * {@link discountWriteOf}, with the resolver's rounding discount applied to an
 * `inherit` row whose discount nobody touched. Everything else is the draft's.
 */
function effectiveDiscountWriteOf(draft: Draft, inherited: InheritedPrice | undefined): DiscountWrite {
  if (draft.priceMode !== 'inherit' || inherited === undefined || !inherited.discountFollows) {
    return discountWriteOf(draft);
  }
  if (inherited.discountAmount !== null) {
    return { discount: '0', discountAmount: inherited.discountAmount };
  }
  // No rounding discount any more: a stored one the resolver placed goes.
  return draft.discountAmount === null
    ? discountWriteOf(draft)
    : { discount: '0', discountAmount: null };
}

/** The tier a draft writes: none without a project. */
function tierOfDraft(draft: Draft): number | null {
  return draft.projectId === '' ? null : draft.priceTierId;
}

function inclusionsPayload(entries: InclusionDraft[]) {
  return entries.map((entry, index) => {
    const quantity = Number(entry.quantity);
    return {
      inclusionId: entry.inclusionId,
      ...(entry.quantity !== '' && isValidInclusionQuantity(quantity) ? { quantity } : {}),
      sortOrder: index,
    };
  });
}

/**
 * The POST body for a new line.
 *
 * ⚠️ **`unitPrice` is OMITTED under `inherit`, and that omission is the whole
 * point of {@link PriceMode}** — it is the only way to say "snapshot the
 * catalogue price". An explicit `''`, `null` or `0` here all say something else.
 *
 * ⚠️ **`inclusions` is omitted outright when the product does not offer them.**
 * An inclusions write REPLACES the whole set, so emitting `[]` on a hidden
 * editor would silently clear a line's campaign inclusions with a 200. The
 * verdict is a parameter with no default, so `tsc` refuses a call site that
 * forgets it.
 *
 * ⚠️ **`offersProjects` is the same shape, for the same reason.** When the
 * project block is not rendered, both `projectId` and `cityIds` are omitted
 * outright — `cityIds: []` CLEARS a stored set, and a hidden control that clears
 * what it was hiding is the defect the inclusions parameter already prevents.
 *
 * ⚠️ **`offersRegions` is a THIRD parameter of the same shape.** `regionIds` is
 * a full REPLACE too, so a hidden region block must omit the key rather than
 * emit `[]`. On a POST nothing is stored for `[]` to clear, which makes the two
 * look alike — they are not alike on the PATCH below, and this is the builder
 * both modes use, so the rule is stated in both places rather than being true
 * here by accident.
 *
 * ⚠️ **No tax key is emitted because none exists to emit.** See {@link Draft}.
 */
function createPayload(
  draft: Draft,
  sortOrder: number | undefined,
  offersInclusions: boolean,
  offersProjects: boolean,
  offersRegions: boolean,
  inherited: InheritedPrice | undefined,
): DealProductCreateInput {
  const body: DealProductCreateInput = { productId: Number(draft.productId) };
  if (sortOrder !== undefined) body.sortOrder = sortOrder;
  const description = descriptionForWrite(draft.description);
  if (description !== null) body.description = description;
  if (offersProjects && draft.projectId !== '') body.projectId = Number(draft.projectId);
  if (offersProjects && draft.cityIds.length > 0) body.cityIds = [...draft.cityIds];
  /*
   * ⚠️ **OMITTED when the control is not on screen.** A row can hold regions
   * chosen while its product was projectless and then have its item changed to
   * one that IS sold through city-bearing projects, at which point the block is
   * gone and the person can no longer see or undo the set. Sending it would
   * store a geography nobody on this screen can look at.
   */
  if (offersRegions && draft.regionIds.length > 0) body.regionIds = [...draft.regionIds];
  if (draft.quantity !== '') body.quantity = Number(draft.quantity);
  if (draft.priceMode === 'none') body.unitPrice = null;
  else if (draft.priceMode === 'value') body.unitPrice = draft.price;
  // A project price is sent; the catalogue rule is left to the server's snapshot.
  else if (inherited?.source === 'project_price') body.unitPrice = inherited.unitPrice;
  const tier = offersProjects ? tierOfDraft(draft) : null;
  if (tier !== null) body.priceTierId = tier;
  const money = effectiveDiscountWriteOf(draft, inherited);
  if (money.discountAmount !== null) {
    // The pair, always. `discount: 0` is what makes the amount the only live
    // discount on the line rather than the second one.
    body.discountAmount = money.discountAmount;
    body.discount = 0;
  } else if (money.discount !== '') {
    body.discount = money.discount;
  }
  if (offersInclusions && draft.inclusions.length > 0) {
    body.inclusions = inclusionsPayload(draft.inclusions);
  }
  // Omitted when blank: the server folds '' and whitespace to null anyway, and
  // an absent key is the honest spelling of "no note".
  if (draft.notes !== null && draft.notes.trim() !== '') body.notes = draft.notes;
  return body;
}

/**
 * The PATCH body for an existing line: only what actually moved.
 *
 * ⚠️ **`notes` is never emitted here.** A saved line's note is written on its
 * own by a notes-only PATCH — see {@link Draft.notes}.
 *
 * ⚠️ **`projectId` and `cityIds` go together whenever either changes.** The
 * guard runs on the POST-MERGE state, so a `projectId`-only PATCH re-validates
 * the STORED cities and can be refused naming cities the caller never sent.
 *
 * ⚠️ **`inclusions` now travels with a project MOVE for the same reason**
 * (migration 096). A product may offer an inclusion only in certain regions, and
 * a line's region comes from its project — so moving the line re-validates the
 * inclusions it already holds and can answer
 * `422 INCLUSION_NOT_OFFERED_IN_REGION` on a key nobody sent. Re-sending the
 * draft makes the refusal name what is on screen. See
 * `patchRevalidatesStoredInclusions` in `@/lib/line-project`.
 *
 * ⚠️ **It is NOT coupled the other way, and must not become `inclusions: []`.**
 * When `offersInclusions` is false the key stays omitted even on a project move:
 * an inclusions write REPLACES the whole set, so `[]` would destroy the line's
 * stored rows with a 200 and nothing to restore from. The refusal — which cannot
 * be fixed from a line whose editor is hidden anyway — is the lesser cost.
 *
 * ⚠️ **`offersProjects: false` omits BOTH keys.** The project controls are not
 * on screen for this row, so there is nothing anybody changed and nothing to
 * send — and the alternative spelling, `cityIds: []`, would destroy a line's
 * stored cities with a 200. A row that already names a project never reaches
 * this branch ({@link RowProject.showProject} keeps its block), so this is a
 * structural backstop rather than a live path.
 */
function updatePayload(
  draft: Draft,
  saved: DealProduct,
  offersInclusions: boolean,
  offersProjects: boolean,
  offersRegions: boolean,
  inherited: InheritedPrice | undefined,
): DealProductUpdateInput {
  const base = draftOf(saved);
  const body: DealProductUpdateInput = {};
  if (draft.productId !== base.productId) body.productId = Number(draft.productId);
  const description = descriptionForWrite(draft.description);
  if (description !== base.description) body.description = description;
  const projectMoved = draft.projectId !== base.projectId;
  const citiesMoved = citiesKey(draft.cityIds) !== citiesKey(base.cityIds);
  if (offersProjects && (projectMoved || citiesMoved)) {
    body.projectId = draft.projectId === '' ? null : Number(draft.projectId);
    body.cityIds = [...draft.cityIds];
  }
  /*
   * ⚠️ **Sent ONLY when the person actually moved it, and the ABSENT form is the
   * load-bearing one.** `regionIds` is a full REPLACE: the key present replaces
   * the whole set, `[]` CLEARS it, and omitting it leaves the stored set exactly
   * as it is. An unconditional send wipes a set another surface populated, on a
   * save that only changed the price.
   *
   * ⚠️ **It is deliberately NOT coupled to `projectId` the way `cityIds` is, and
   * coupling it would be a bug rather than a tidy-up.** Cities travel with the
   * project because the server re-validates them against the POST-MERGE project
   * and can refuse naming a control nobody touched. Regions are validated
   * against the `/regions` master alone, so a project move says nothing about
   * them — and clearing them on a move, which is what the city branch does one
   * line up, would turn choosing a different project into a silent whole-set
   * DELETE of a geography the person never looked at. A region the new project
   * does not declare is kept, labelled and editable instead; see
   * `regionOptionsFor`.
   *
   * `offersRegions` cannot fire on its own — hiding never touches the draft, so
   * a hidden set stays byte-identical to the stored one and `regionsKey` already
   * finds no change — which is precisely why it is written into the payload
   * rather than left true by accident, one edit away from somebody normalising a
   * hidden set to `[]`.
   */
  if (offersRegions && regionsKey(draft.regionIds) !== regionsKey(base.regionIds)) {
    body.regionIds = [...draft.regionIds];
  }
  if (draft.quantity !== '' && draft.quantity !== base.quantity) {
    body.quantity = Number(draft.quantity);
  }
  /*
   * `inherit` on a line whose price was typed over ({@link InheritedPrice}
   * `stored`) emits nothing: an omitted key leaves the stored price alone. On a
   * line whose untouched price follows its selection it SENDS the repriced
   * figure (owner 2026-10-27) — the server reprices a create only.
   */
  if (draft.priceMode === 'none' && base.priceMode !== 'none') body.unitPrice = null;
  if (draft.priceMode === 'value' && draft.price !== base.price) body.unitPrice = draft.price;
  if (
    draft.priceMode === 'inherit' &&
    inherited !== undefined &&
    inherited.source !== 'stored' &&
    inherited.unitPrice !== null &&
    (base.priceMode === 'none' || !sameDecimal(inherited.unitPrice, base.price))
  ) {
    body.unitPrice = inherited.unitPrice;
  }
  if (offersProjects && tierOfDraft(draft) !== tierOfDraft(base)) {
    body.priceTierId = tierOfDraft(draft);
  }
  /*
   * ⚠️ **The discount travels as a PAIR whenever either half moves**, because
   * the server refuses two live discounts — setting one while the other stays
   * stored is exactly the 422. A blank percentage box still says nothing at
   * all, which is what keeps "leave the stored value alone" available.
   */
  const money = effectiveDiscountWriteOf(draft, inherited);
  const baseMoney = discountWriteOf(base);
  const amountMoved = money.discountAmount !== baseMoney.discountAmount;
  const percentMoved = money.discount !== '' && money.discount !== baseMoney.discount;
  if (amountMoved || percentMoved) {
    body.discountAmount = money.discountAmount;
    body.discount = money.discount === '' ? 0 : money.discount;
  }
  const inclusionsMoved = inclusionsKey(draft.inclusions) !== inclusionsKey(base.inclusions);
  if (offersInclusions && (inclusionsMoved || (offersProjects && projectMoved))) {
    body.inclusions = inclusionsPayload(draft.inclusions);
  }
  return body;
}

/** Dirty IS "the PATCH would carry something", so the two can never disagree. */
function isDirty(
  row: Row,
  offersInclusions: boolean,
  offersProjects: boolean,
  offersRegions: boolean,
  inherited: InheritedPrice | undefined,
): boolean {
  if (row.saved === null) return true;
  return (
    Object.keys(
      updatePayload(
        row.draft,
        row.saved,
        offersInclusions,
        offersProjects,
        offersRegions,
        inherited,
      ),
    ).length > 0
  );
}

/** City count of each project in the read list, by id — `N` in the pro-rata rule. */
type ProjectCityCounts = ReadonlyMap<number, number>;

/** Everything {@link computeFor} reads besides the line itself. */
interface PricingContext {
  entriesByProject: ReadonlyMap<number, readonly ProjectPriceEntry[]>;
  /** The price read failed: lines fall back to the catalogue rule. */
  pricesFailed: boolean;
  defaultTierId: number | null;
  currency: string | null;
  catalogPrices: ReadonlyMap<number, string | null>;
  counts: ProjectCityCounts;
}

/** The pricing-relevant half of a line, read off a draft or a saved line. */
interface PricedSelection {
  productId: number | null;
  projectId: number | null;
  priceTierId: number | null;
  selectedCityCount: number;
  quantity: number;
}

/**
 * {@link computeLinePrice} for one selection, or `undefined` while something it
 * needs is UNREAD: the catalogue price, the project's city count (a project not
 * in the read list is never 0 cities — unless no city is picked, where the
 * catalogue rule needs no count), or the project's price entries and tiers.
 */
function computeFor(ctx: PricingContext, selection: PricedSelection): ComputedLinePrice | undefined {
  if (selection.productId === null) return undefined;
  const catalog = ctx.catalogPrices.get(selection.productId);
  if (catalog === undefined) return undefined;
  const projectCityCount =
    selection.projectId === null
      ? 0
      : (ctx.counts.get(selection.projectId) ?? (selection.selectedCityCount === 0 ? 0 : undefined));
  if (projectCityCount === undefined) return undefined;
  let entries: readonly ProjectPriceEntry[] = [];
  if (selection.projectId !== null && ctx.currency !== null && ctx.currency.trim() !== '') {
    const loaded = ctx.entriesByProject.get(selection.projectId);
    if (loaded !== undefined) entries = loaded;
    else if (!ctx.pricesFailed) return undefined;
    if (ctx.defaultTierId === null && !ctx.pricesFailed) return undefined;
  }
  return computeLinePrice({
    entries,
    defaultTierId: ctx.defaultTierId,
    productId: selection.productId,
    currency: ctx.currency,
    priceTierId: selection.priceTierId,
    selectedCityCount: selection.selectedCityCount,
    projectCityCount,
    quantity: selection.quantity,
    catalogUnitPrice: catalog,
  });
}

/** A saved line's own selection, as {@link computeFor} reads it. */
function savedSelectionOf(saved: DealProduct): PricedSelection {
  return {
    productId: saved.product.id,
    projectId: saved.project?.id ?? null,
    priceTierId: saved.priceTierId ?? null,
    selectedCityCount: saved.cities.length,
    quantity: saved.quantity,
  };
}

/** The draft's selection. A blank quantity is the stored one, else the server's 1. */
function draftSelectionOf(row: Row): PricedSelection {
  const { draft } = row;
  return {
    productId: draft.productId === '' ? null : Number(draft.productId),
    projectId: draft.projectId === '' ? null : Number(draft.projectId),
    priceTierId: tierOfDraft(draft),
    selectedCityCount: draft.cityIds.length,
    quantity: draft.quantity.trim() === '' ? (row.saved?.quantity ?? 1) : Number(draft.quantity),
  };
}

/** `''`, `0`, `0.00`: a percentage nobody set. */
function isBlankOrZero(value: string): boolean {
  return /^(0+(\.0+)?)?$/.test(value.trim());
}

/** Exact equality of two decimal strings, scale-insensitive ("7000" = "7000.00"). */
function sameDecimal(a: string, b: string): boolean {
  if (!/^-?\d+(\.\d+)?$/.test(a.trim()) || !/^-?\d+(\.\d+)?$/.test(b.trim())) return false;
  const negated = b.trim().startsWith('-') ? b.trim().slice(1) : `-${b.trim()}`;
  const diff = sumDecimalStrings([a, negated]);
  return diff !== null && /^-?0+(\.0+)?$/.test(diff);
}

/**
 * Whether a saved line's stored price is still what {@link computeLinePrice}
 * gives its OWN saved product, project, tier, cities and quantity — "never typed
 * over". A line written under the legacy catalogue pro-rata before its project
 * was priced counts as untouched too. Only then does a product, project, tier,
 * city or quantity change reprice the line; a price somebody typed stays.
 * `false` whenever something the rule reads is unread (the stored price shows),
 * and for a stored `null` — an explicit "no price" is never repriced.
 */
function savedPriceFollows(saved: DealProduct, ctx: PricingContext): boolean {
  if (saved.unitPrice === null) return false;
  const selection = savedSelectionOf(saved);
  const expected = computeFor(ctx, selection)?.unitPrice;
  if (typeof expected === 'string' && sameDecimal(expected, saved.unitPrice)) return true;
  const legacy = computeFor({ ...ctx, entriesByProject: NO_PRICE_ENTRIES, pricesFailed: true }, selection)
    ?.unitPrice;
  return typeof legacy === 'string' && sameDecimal(legacy, saved.unitPrice);
}

/** No project prices: {@link computeFor} then asks the catalogue rule alone. */
const NO_PRICE_ENTRIES: ReadonlyMap<number, readonly ProjectPriceEntry[]> = new Map();

/**
 * The input to `computeGeneratedLinePreview`, with the blanks resolved.
 *
 * A blank box means two different things by row, so it is resolved before the
 * call rather than inside it: on a NEW line a blank percentage takes the
 * server's default of 0 and a blank price snapshots the catalogue; on an
 * EXISTING line a blank leaves the stored value alone.
 *
 * ⚠️ **`taxRate: '0'` IS THE ONE PLACE THIS FILE MENTIONS TAX, AND IT IS A
 * STATEMENT THAT THERE IS NONE.** `f_deal_products` has no `tax_rate` column, so
 * there is nothing to read and nothing to send; the shared preview requires the
 * field because it also serves quote and invoice lines, and at `'0'` its step 1
 * is byte for byte the deal's own `line_total` formula. Written out rather than
 * defaulted so a reader can check the claim — see this file's header.
 */
function previewInputOf(row: Row, inherited: InheritedPrice | undefined): GeneratedLineInput {
  const { draft, saved } = row;
  const inheritedPrice = inherited?.unitPrice ?? null;
  const money = effectiveDiscountWriteOf(draft, inherited);
  const stored = (typed: string, value: string | undefined) =>
    typed !== '' ? typed : (value ?? '');
  // In amount mode the percentage is written as 0, so the preview must not fall
  // back to the stored one — that would take BOTH discounts off the line.
  const percentage = money.discountAmount === null ? undefined : '0';
  return {
    quantity: stored(draft.quantity, saved === null ? undefined : String(saved.quantity)),
    unitPrice:
      draft.priceMode === 'none'
        ? // An explicit null: no price on this line, which is not a price of zero.
          null
        : draft.priceMode === 'value'
          ? draft.price
          : // `inherit` — resolved by the caller ({@link InheritedPrice}): the
            // project price or catalogue pro-rata on a new line or on a saved
            // line whose stored price still follows its selection, else the
            // stored price, because that is what the write will leave.
            inheritedPrice,
    discount: percentage ?? stored(draft.discount, saved?.discount),
    /*
     * Read off the RESOLVED pair rather than off the draft, so the figure
     * previewed is the one the payload will carry: in amount mode the
     * percentage is zero and the money comes off exactly, and in percentage
     * mode there is no amount at all.
     */
    discountAmount: money.discountAmount,
    taxRate: '0',
  };
}

/**
 * What the database will store in `line_total`, previewed.
 *
 * ⚠️ **A deal line has ONE generated column and this returns ONE figure.** The
 * other two grids' preview is a `{lineSubtotal, lineTax, lineTotal}` triple
 * because their tables have three columns; reproducing that shape here would
 * invent two figures with nothing behind them. At `taxRate: '0'` the shared
 * preview's `lineSubtotal` and `lineTotal` are equal and both are the deal's
 * `ROUND(quantity * COALESCE(unit_price,0) * (1 - discount/100), 2)`.
 *
 * `null` — never `"0.00"` — whenever the line has no price, an unusable
 * quantity, or a price nothing has read yet. An unpriced line and a line priced
 * at zero produce identical strings, so a row with no price shows no preview at
 * all rather than a figure that cannot be told from a real one.
 */
function lineTotalPreview(input: GeneratedLineInput): string | null {
  return computeGeneratedLinePreview(input)?.lineTotal ?? null;
}

/* ── Discount: TWO columns now, and nothing converts between them ─────────── */

/*
 * ⚠️ **THE AMOUNT-TO-PERCENTAGE CONVERSION IS DELETED. DO NOT BRING IT BACK.**
 *
 * `f_deal_products` carries `discount_amount` alongside `discount`, mutually
 * exclusive, so an amount is stored as the amount. What used to be here — a
 * `percentageForAmount` call, a `lossy` flag, and a paragraph under the box
 * explaining that the figure typed was not the figure stored — existed only
 * because there was one column and it was a percentage.
 *
 * The loss it was apologising for was in POUNDS, not pence: two decimal places
 * on a percentage space the representable amounts by `quantity × unitPrice ×
 * 0.0001`, so on an 18,000 line the two amounts either side of £1,000 were
 * £999.00 and £1,000.80 with nothing in between. There is nothing left to
 * apologise for, and a re-derived conversion would reintroduce exactly that.
 */

/**
 * ⛔ **`DISCOUNT_AMOUNT_PATTERN`, NOT `PRICE_PATTERN` — and this function used
 * to read the other one.** Its doc line said *"an amount is money, so it is a
 * price"*, which was true until 2026-09-12 and is now precisely the confusion
 * to avoid: a price may be negative, a discount may not. See the block at both
 * declarations.
 */
function isWellFormedAmount(value: string): boolean {
  return DISCOUNT_AMOUNT_PATTERN.test(value.trim());
}

interface RowProblems {
  fieldErrors: Record<string, string>;
  error: string | null;
  /**
   * Something true about the row that is **not** a reason to refuse it.
   *
   * ⚠️ **Only `error` blocks a save; this never does.** The repeated-product
   * message moved here rather than being softened on `error`, where any wording
   * would still have disabled Save. Migration 129 dropped the
   * (deal_id, product_id, project_key) unique key on all three line tables.
   */
  notice: string | null;
}

/** Whether this row's save will actually carry `cityIds` and trigger the guard. */
function writesCities(row: Row): boolean {
  if (row.saved === null) return row.draft.cityIds.length > 0;
  const base = draftOf(row.saved);
  return (
    row.draft.projectId !== base.projectId ||
    citiesKey(row.draft.cityIds) !== citiesKey(base.cityIds)
  );
}

/**
 * *SUPERSEDED 11 Sep 2026: a sixth parameter, `inclusionsRequired`, carried the
 * owner's at-least-one rule as a second and strictly narrower inclusions gate.
 * The rule was withdrawn, so the parameter went with it rather than being left
 * accepted and ignored — an argument nothing reads is the shape this repo has
 * already been bitten by.*
 */
function validateRow(
  row: Row,
  duplicatePairs: Set<string>,
  retiredIds: Set<number>,
  declaredCityIds: Set<number> | null,
  offersInclusions: boolean,
  units: ProductUnitFields | undefined,
): RowProblems {
  const fieldErrors: Record<string, string> = {};
  const draft = row.draft;
  if (draft.productId === '') fieldErrors.productId = 'Choose a product.';
  const description = descriptionForWrite(draft.description);
  if (description !== null && description.length > MAX_DESCRIPTION_LENGTH) {
    fieldErrors.description = `At most ${MAX_DESCRIPTION_LENGTH.toLocaleString()} characters — this is ${description.length.toLocaleString()}.`;
  }
  if (draft.cityIds.length > MAX_CITIES_PER_LINE) {
    fieldErrors.cityIds = `At most ${MAX_CITIES_PER_LINE} cities on one line.`;
  } else if (draft.cityIds.length > 0 && draft.projectId === '') {
    fieldErrors.cityIds = 'Choose this line’s project before naming cities.';
  } else if (declaredCityIds !== null && writesCities(row)) {
    const stray = draft.cityIds.filter((id) => !declaredCityIds.has(id));
    if (stray.length > 0) {
      fieldErrors.cityIds = `Not declared on this line’s project: ${stray.length} of ${draft.cityIds.length} chosen. Remove them, or choose the project that runs there.`;
    }
  }
  /*
   * ⚠️ **AN EMPTY BOX IS FINE ON AN ORDINARY ROW AND A REFUSAL ON A SEEDED ONE,
   * AND THE ASYMMETRY IS THE POINT.**
   *
   * {@link createPayload} omits `quantity` when the box is empty, and an omitted
   * `quantity` is **1** on the server. On a row somebody typed that is a
   * convenience nobody has to think about. On a row seeded from a PLANNED figure
   * that could not be expressed — `"2.50"`, `"0.5"` — it is the exact silent
   * substitution {@link seededRowOf} refuses to make on the way in, arriving
   * anyway by the back door: a 201, a well-formed deal, and a quantity nobody
   * chose on it.
   *
   * So the seeded row is refused while the box is empty. That puts it in
   * `blockedKeys`, withholds it from `onDraftLinesChange`, raises the grid's own
   * blocking banner and disables the parent's Create — which is a person being
   * asked one question, rather than a wrong number nobody is ever asked about.
   *
   * ⚠️ **The empty branch is an ADDITION, not an amendment**: there was no
   * empty-box case here at all before the seed existed, because an empty box had
   * no way of being wrong.
   */
  if (draft.quantity === '') {
    if (row.plannedQuantity !== null) {
      // The planned figure is named, because "enter a quantity" over a box that
      // opened empty on a row the person did not create explains nothing.
      fieldErrors.quantity = `Planned ${row.plannedQuantity} — enter a whole number of 1 or more.`;
    }
  } else {
    const quantity = Number(draft.quantity);
    if (!Number.isInteger(quantity) || quantity < 1) {
      fieldErrors.quantity = 'A whole number of 1 or more.';
    }
  }
  /*
   * ⚠️ **The product's step — the server's 422 `QUANTITY_NOT_MULTIPLE`, said
   * first.** Judged only on a row whose product or quantity has MOVED
   * ({@link seedsFromCatalogue}): a stored line from before the step existed is
   * legacy and keeps working until somebody changes it. An empty box on a NEW
   * row is the server's default of 1, so it is judged as 1.
   */
  if (fieldErrors.quantity === undefined && seedsFromCatalogue(row)) {
    const judged = draft.quantity === '' && row.saved === null ? '1' : draft.quantity;
    const stepError = quantityStepError(units, judged);
    if (stepError !== null) fieldErrors.quantity = stepError;
  }
  if (draft.priceMode === 'value' && !PRICE_PATTERN.test(draft.price.trim())) {
    fieldErrors.unitPrice =
      draft.price.trim() === ''
        ? 'Enter a price, or record the line as having none.'
        : 'Up to two decimal places, and no currency symbol.';
  }
  /*
   * ⚠️ ONE percentage on a deal line, not two. The quote and invoice grids loop
   * over `discount` and `taxRate` here; there is no `taxRate` column on
   * `f_deal_products` and no `taxRate` key on {@link Draft}, so there is nothing
   * to loop over.
   */
  /*
   * ⚠️ **Exactly one of the two is checked, because exactly one is live.** The
   * mode decides which control is on screen, and reporting the other's format
   * would name a box nobody can see.
   */
  if (draft.discountAmount === null) {
    const discount = draft.discount.trim();
    if (discount !== '' && (!DISCOUNT_PATTERN.test(discount) || Number(discount) > 100)) {
      fieldErrors.discount = 'A percentage between 0 and 100.';
    }
  } else {
    const amount = draft.discountAmount.trim();
    if (amount !== '' && !isWellFormedAmount(amount)) {
      /*
       * ⚠️ **The negative branch became the likely mistake on 2026-09-12**, when
       * a line PRICE gained a sign (migration 171). Typing `-500` here is the
       * natural way to say "take this off", and the generic sentence answers it
       * by naming decimal places — a rule the figure already keeps.
       *
       * ⛔ A discount may never be negative; the database reports that only as
       * errno 4025, a 422 naming no field, so this sentence is the only place a
       * person learns what to use instead. Worded identically in
       * `deal-convert-lines.ts` and the other two grids.
       */
      fieldErrors.discountAmount = amount.startsWith('-')
        ? 'A discount cannot be negative. To reduce a line below zero, use a negative price.'
        : 'An amount, up to two decimal places and with no currency symbol.';
    }
  }
  /*
   * ⚠️ **Skipped wholesale when the block is hidden**, the same condition the two
   * payload builders use and for the same reason. Nothing below can be true of a
   * key that is not sent, and an error marking a control the person cannot see
   * would leave the row permanently unsaveable over a field they never touched.
   *
   * *SUPERSEDED 11 Sep 2026: this nesting was described as the guard on the
   * at-least-one refusal, which was the one inclusions check that demanded a
   * selection rather than objecting to one. That refusal is gone, so the nesting
   * now protects only the three checks below.*
   */
  if (offersInclusions) {
    /*
     * ⛔ AN EMPTY SET IS NOT REFUSED, AND THAT IS THE RULE RATHER THAN A GAP.
     *
     * *SUPERSEDED, owner's decision 11 Sep 2026: a line whose product offered
     * inclusions had to name one, and an empty set rendered
     * `INCLUSION_REQUIRED_MESSAGE`.* That rule predated per-inclusion
     * requiredness: with no way to say WHICH inclusion mattered, "at least one"
     * was the only thing the client could ask for. Migration 159 lets an admin
     * mark each offer required, and the owner chose PRE-TICK, STILL REMOVABLE —
     * so a refusal here would contradict the removability that was the whole
     * point of the choice.
     *
     * ⚠️ **Nothing on the server ever backed this up.** A line with no
     * inclusions is a clean `2xx` on all three endpoints, and the backend
     * declined enforcement explicitly, because this app hides the inclusions
     * block on lines whose product is not gated for it and an enforced refusal
     * could then name a control the person cannot see — the shape
     * `PROJECT_HAS_NO_CITIES` already has. **Do not reinstate this as a
     * client-side rule; it would be a convention dressed as a contract.**
     *
     * The three checks below all object to something PRESENT in the set, so
     * every one of them is unreachable on an empty set and none needs the
     * removed branch ahead of it.
     */
    if (draft.inclusions.length > MAX_INCLUSIONS_PER_LINE) {
      fieldErrors.inclusions = `At most ${MAX_INCLUSIONS_PER_LINE} inclusions on one line.`;
    } else if (
      draft.inclusions.some(
        (entry) => entry.quantity !== '' && !isValidInclusionQuantity(Number(entry.quantity)),
      )
    ) {
      fieldErrors.inclusions = 'Every count must be a whole number of 1 or more.';
    } else {
      const writingSet =
        row.saved === null
          ? draft.inclusions.length > 0
          : inclusionsKey(draft.inclusions) !== inclusionsKey(draftOf(row.saved).inclusions);
      if (writingSet && draft.inclusions.some((entry) => retiredIds.has(entry.inclusionId))) {
        fieldErrors.inclusions =
          'A retired inclusion can’t be saved. Remove it, or leave this set untouched.';
      }
    }
  }
  const duplicate =
    draft.productId !== '' && duplicatePairs.has(pairKey(draft.productId, draft.projectId));

  /*
   * ⚠️ **A NOTICE AND NEVER A FIELD ERROR, because the seeded price is not
   * wrong.** `10000.00` over 3 is `3333.33` and the line comes to `9999.99`;
   * that is the best a per-unit price can do and a person may accept it. A
   * `fieldErrors.unitPrice` here would land the row in `blockedKeys` and disable
   * the parent's Create over a penny — and a control wrongly disabled is a false
   * statement with nobody watching, where the same figure merely reported costs
   * nothing. So it says what is true and refuses nothing.
   *
   * ⚠️ **It stands down the moment either box moves.** The sentence is about the
   * SEEDED figures; once the price or the quantity changes it no longer
   * describes the row, and a stale total naming an amount the line no longer has
   * is worse than no sentence at all.
   */
  const shortfall = row.plannedShortfall;
  const shortfallNotice =
    shortfall !== null &&
    draft.priceMode === 'value' &&
    draft.price === shortfall.unitPrice &&
    draft.quantity === shortfall.quantity
      ? `Planned ${shortfall.plannedAmount} — ${shortfall.unitPrice} × ${shortfall.quantity} comes to ${shortfall.lineAmount}. The planned amount does not divide evenly.`
      : null;

  /*
   * ⚠️ **BOTH notices render, JOINED — never one `??` or ternary over the
   * other.** A repeated product and a plan that will not divide are two
   * different true things about one row, and this field was a single ternary
   * until the seed arrived: keeping that shape would have silently dropped
   * whichever came second, exactly when the row is under the most scrutiny. It
   * is the `a ?? b` between two things that both need showing, one `if` earlier.
   */
  const notices = [
    shortfallNotice,
    duplicate
      ? 'This product is on more than one line of this deal. That is allowed — check it is deliberate.'
      : null,
  ].filter((line): line is string => line !== null);
  return {
    fieldErrors,
    /*
     * ⚠️ **A REPEATED PRODUCT IS NO LONGER AN ERROR.** *SUPERSEDED: an `error`
     * reading "Already on this deal under that project. Edit that line, or pick
     * another project." and, for the project-less case, "Give one of them a
     * project, or merge them."*
     *
     * Both named an action needed to make the save possible; after migration 129
     * the save succeeds either way, so both would now be instructions to fix
     * something that is not broken.
     */
    error: null,
    notice: notices.length === 0 ? null : notices.join(' '),
  };
}

/**
 * The `(product, project)` pairs claimed by more than one row.
 *
 * ⚠️ **This feeds `RowProblems.notice` and refuses nothing** — migration 129
 * dropped the unique key it used to anticipate. A repeated product is still
 * pointed out, because billing one thing twice by accident is a real mistake,
 * but the save proceeds.
 *
 * Counted across rows rather than by excluding taken products from the picker:
 * the project picker is filtered by the row's own product, so an exclusion
 * deadlocks the second-project case — you could not reach a second project
 * without first choosing the product the exclusion had just removed. That
 * still holds, and there is now nothing to exclude anyway.
 */
function duplicatePairsOf(rows: Row[]): Set<string> {
  const seen = new Map<string, number>();
  for (const row of rows) {
    if (row.draft.productId === '') continue;
    const key = pairKey(row.draft.productId, row.draft.projectId);
    seen.set(key, (seen.get(key) ?? 0) + 1);
  }
  const duplicates = new Set<string>();
  for (const [key, count] of seen) if (count > 1) duplicates.add(key);
  return duplicates;
}

/**
 * Every path a line write can report a field error at. `priceMode` and `price`
 * are ours, not the server's; the wire name is `unitPrice`.
 *
 * ⚠️ Derived from `blankDraft()` so it cannot drift from the controls — and
 * because {@link Draft} has no tax keys, this list cannot claim a 422 naming
 * `taxRate` is markable. Such a path routes to the form-level banner instead,
 * which is right: no control here is bound to it.
 */
const ROW_WRITE_KEYS: readonly string[] = [
  // `discountAmount` arrives here for free, because it is a `Draft` key and a
  // real write key — which is the whole reason it lives on the draft rather
  // than in the screen-state map the conversion used to need.
  ...Object.keys(blankDraft()).filter((key) => key !== 'priceMode' && key !== 'price'),
  'unitPrice',
];

function describeFailure(error: unknown): {
  message: string;
  fieldErrors: Record<string, string>;
  refusal: LineRefusal | null;
} {
  if (error instanceof ApiError) {
    // `inclusions.0.quantity` and friends all belong on the one editor.
    const inclusionPaths = Object.keys(error.fieldErrors).filter(
      (path) => path === 'inclusions' || path.startsWith('inclusions.'),
    );
    const { fields, unroutable } = error.routeFieldErrors([...ROW_WRITE_KEYS, ...inclusionPaths]);
    const inclusionMessages: string[] = [];
    for (const path of inclusionPaths) {
      const message = fields[path];
      if (message === undefined) continue;
      inclusionMessages.push(message);
      delete fields[path];
    }
    if (inclusionMessages.length > 0) fields.inclusions = inclusionMessages.join(' ');
    /*
     * The product's step, refused by the server. It belongs on the Qty box
     * whether or not the refusal carried a path.
     */
    if (error.code === 'QUANTITY_NOT_MULTIPLE') {
      fields.quantity ??= error.message;
      return { message: error.message, fieldErrors: fields, refusal: null };
    }
    return {
      message: unroutable.length > 0 ? unroutable.join(' ') : error.message,
      fieldErrors: fields,
      refusal: lineRefusalOf(error),
    };
  }
  return { message: 'The request did not reach the server.', fieldErrors: {}, refusal: null };
}

/* ── Reference data ───────────────────────────────────────────────────────── */

interface ProductRow {
  id: number;
  productName: string;
  productCode: string | null;
  unitPrice: string | null;
  /**
   * What a line selling this product should START its discount at — migration
   * 172. Carried on the row rather than fetched per line: `/products` already
   * serves it and the grid already holds the list.
   *
   * ⚠️ **`null` is NO RECOMMENDATION and `"0.00"` is a recommendation of nothing
   * off**, the same distinction {@link ProductRow.unitPrice} draws between
   * unpriced and free. A `?? '0.00'` anywhere between the wire and
   * `recommendedDiscountSeed` turns "this product recommends nothing" into "this
   * product recommends zero", which seeds an amount onto every line and puts it
   * in amount mode.
   */
  recommendedDiscountAmount: string | null;
  /**
   * `quantityStep` / `unitLabel`, carried only when the wire sent them — ABSENT
   * is an older API, which `@/lib/product-units` reads as "no step, no unit".
   */
  units: ProductUnitFields;
}

/**
 * True when this product is sold PER UNIT, so its recommended discount is a
 * per-unit figure and its lines take the auto description. Read off the two
 * flag columns only.
 */
function soldPerUnit(units: ProductUnitFields | undefined): boolean {
  const step = units?.quantityStep;
  if (typeof step === 'number' && Number.isInteger(step) && step >= 1) return true;
  return (units?.unitLabel?.trim() ?? '') !== '';
}

/** The product's unit word, or `null` when it names none. */
function unitLabelOf(units: ProductUnitFields | undefined): string | null {
  const label = units?.unitLabel?.trim() ?? '';
  return label === '' ? null : label;
}

/** The product's step as an input attribute, or `undefined` when it has none. */
function quantityStepOf(units: ProductUnitFields | undefined): number | undefined {
  const step = units?.quantityStep;
  return typeof step === 'number' && Number.isInteger(step) && step >= 1 ? step : undefined;
}

/**
 * What `autoLineDescription` is composed from for this row, or `null` when the
 * product names no unit or the quantity is not a whole count.
 *
 * ⚠️ The per-unit discount is the line's AMOUNT spread over the quantity, and
 * only when it divides exactly (`splitAmountOverQuantity`, BigInt) — an
 * inexact or percentage discount prints no "Discounted price" line rather than
 * a rounded one.
 */
function autoDescriptionArgsOf(
  draft: Draft,
  product: ProductRow | undefined,
  currency: string | null,
): AutoLineDescriptionArgs | null {
  if (product === undefined) return null;
  const unitLabel = unitLabelOf(product.units);
  if (unitLabel === null) return null;
  const quantity = draft.quantity.trim() === '' ? '1' : draft.quantity.trim();
  if (!/^\d+$/.test(quantity) || /^0+$/.test(quantity)) return null;
  const typedPrice = draft.price.trim();
  const unitPrice =
    draft.priceMode === 'none'
      ? null
      : draft.priceMode === 'value'
        ? PRICE_PATTERN.test(typedPrice)
          ? typedPrice
          : null
        : product.unitPrice;
  let discountPerUnit: string | null = null;
  const amount = draft.discountAmount?.trim() ?? '';
  if (amount !== '' && DISCOUNT_AMOUNT_PATTERN.test(amount)) {
    const split = splitAmountOverQuantity(amount, Number(quantity));
    if (split !== null && split.exact) discountPerUnit = split.unitPrice;
  }
  return {
    productName: product.productName,
    quantity,
    unitLabel,
    unitPrice,
    discountPerUnit,
    currency: currency ?? '',
  };
}

/** The auto text. A deal with no currency prints none, without a double space. */
function autoTextOf(args: AutoLineDescriptionArgs): string {
  const text = autoLineDescription(args);
  return args.currency.trim() === '' ? text.replace(/ {2,}/g, ' ') : text;
}

/** Whether `text` is still exactly the auto text for `args`. */
function isAutoText(text: string | null, args: AutoLineDescriptionArgs): boolean {
  if (text === null) return false;
  if (isAutoDescription(text, args)) return true;
  return text.replace(/\r\n?/g, '\n').trim() === autoTextOf(args).trim();
}

function useProducts() {
  const [rows, setRows] = useState<ProductRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    api
      .listProducts({ perPage: PRODUCTS_PER_PAGE, active: true }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setRows(
          result.data.map((row) => ({
            id: row.id,
            productName: row.productName,
            productCode: row.productCode,
            unitPrice: row.unitPrice,
            // Straight through. Neither half of the `null` / `"0.00"` pair may
            // be folded into the other — see {@link ProductRow}.
            recommendedDiscountAmount: row.recommendedDiscountAmount,
            units: {
              ...(row.quantityStep !== undefined ? { quantityStep: row.quantityStep } : {}),
              ...(row.unitLabel !== undefined ? { unitLabel: row.unitLabel } : {}),
            },
          })),
        );
        setTruncated(result.meta.totalPages > 1);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setRows([]);
        setFailed(true);
      });
    return () => controller.abort();
  }, []);
  return { rows, truncated, failed };
}

function useCampaignInclusions() {
  const [rows, setRows] = useState<CampaignInclusion[]>([]);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    api
      .listCampaignInclusions({ isActive: true, perPage: INCLUSIONS_PER_PAGE }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setRows(result.data);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setRows([]);
        setFailed(true);
      });
    return () => controller.abort();
  }, []);
  return { rows, failed };
}

/**
 * Each chosen product's campaign-inclusion offer, read once per product id and
 * cached for the life of the grid.
 *
 * A failed read is left ABSENT rather than recorded as an empty offer: absent
 * resolves to `'unknown'`, which shows the editor, and an empty offer is an
 * administrator's real decision. Folding one into the other promotes a load
 * failure into a business rule.
 */
function useProductInclusionOffers(
  productIds: readonly number[],
): ReadonlyMap<number, ProductInclusionOffer> {
  const [offers, setOffers] = useState<ReadonlyMap<number, ProductInclusionOffer>>(
    () => new Map(),
  );
  const requested = useRef<Set<number>>(new Set());
  const controller = useRef<AbortController | null>(null);
  controller.current ??= new AbortController();
  useEffect(() => {
    /*
     * ⚠️ **RE-ARM after a torn-down mount.** One controller per COMPONENT is
     * right — a per-effect one would cancel reads whose ids `requested` has
     * already recorded, and they would never be asked for again — but the
     * cleanup below also runs when the component is NOT gone: in development
     * React invokes setup, cleanup, then setup again, with no render in
     * between, so the `??=` above cannot replace the aborted controller.
     * Without this the offers map stays EMPTY for the life of the grid and
     * every row answers `'unknown'`: editor shown, picker never narrowed to
     * what an administrator linked, at-least-one rule never applied — a
     * working-looking screen that disagrees with production about a set that
     * prints on the customer's document. `QuoteLineItemsTable` carries the
     * full account.
     *
     * ⚠️ Clearing `requested` here does not retry a FAILED read: a rejected
     * fetch never aborts this controller.
     */
    if (controller.current === null || controller.current.signal.aborted) {
      controller.current = new AbortController();
      requested.current = new Set();
    }
    // Through the ref, not a captured copy — the branch above may have swapped it.
    return () => controller.current?.abort();
  }, []);
  useEffect(() => {
    const signal = controller.current?.signal;
    if (signal === undefined) return;
    for (const productId of productIds) {
      if (requested.current.has(productId)) continue;
      requested.current.add(productId);
      api
        .productInclusions(productId, signal)
        .then((result) => {
          if (signal.aborted) return;
          const inclusionIds = result.data.map((inclusion) => inclusion.id);
          setOffers((previous) => new Map(previous).set(productId, { inclusionIds }));
        })
        .catch(() => {
          if (signal.aborted) return;
        });
    }
  }, [productIds]);
  return offers;
}

/**
 * Which inclusions each `(product, quantity)` on screen presets, read once per
 * pair and cached for the life of the grid.
 *
 * ⚠️ **A FAILED OR IN-FLIGHT RESOLVE IS LEFT ABSENT, and absent means SEED
 * NOTHING.** `inclusionPresetSeed` is handed `null` and answers `null`, so the
 * line keeps whatever it holds. That is the safe direction on a whole-set
 * REPLACE: the alternative reading — treating an unanswered resolve as "this
 * quantity presets nothing" — would CLEAR a set somebody chose, with nothing
 * failing. It is the same rule `useProductInclusionOffers` above applies to a
 * failed offer read, for the same reason.
 *
 * ⚠️ **The keys arrive DEBOUNCED**, because one of the two halves is a box
 * somebody types into. Undebounced, `1` → `12` → `120` is three resolves and two
 * cache entries for quantities nobody meant.
 */
function useInclusionPresets(
  wantedKeys: readonly string[],
): ReadonlyMap<string, ResolvedInclusionPreset> {
  const [presets, setPresets] = useState<ReadonlyMap<string, ResolvedInclusionPreset>>(
    () => new Map(),
  );
  const [settled, setSettled] = useState<readonly string[]>([]);
  const requested = useRef<Set<string>>(new Set());
  const controller = useRef<AbortController | null>(null);
  controller.current ??= new AbortController();

  useEffect(() => {
    /*
     * ⚠️ **RE-ARM after a torn-down mount**, exactly as
     * {@link useProductInclusionOffers} does and for the identical reason: in
     * development React runs setup, cleanup, then setup again with no render in
     * between, so the `??=` above cannot replace the aborted controller. Without
     * this the map stays EMPTY for the life of the grid, every resolve is
     * recorded as requested and never sent, and no line is ever preset — a
     * working-looking screen that silently drops a feature.
     */
    if (controller.current === null || controller.current.signal.aborted) {
      controller.current = new AbortController();
      requested.current = new Set();
    }
    return () => controller.current?.abort();
  }, []);

  /*
   * ⚠️ A JOINED STRING, not the array, because the array is rebuilt every render
   * and would re-run this timer on every keystroke anywhere in the grid —
   * restarting the debounce for a box nobody is touching.
   */
  const wantedKey = wantedKeys.join('|');
  useEffect(() => {
    const timer = setTimeout(() => {
      setSettled(wantedKey === '' ? [] : wantedKey.split('|'));
    }, PRESET_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [wantedKey]);

  useEffect(() => {
    const signal = controller.current?.signal;
    if (signal === undefined) return;
    for (const key of settled) {
      if (requested.current.has(key)) continue;
      requested.current.add(key);
      /*
       * The set this request was recorded in. Read through the ref only HERE:
       * the `.finally` below must not consult `requested.current`, which a
       * re-armed mount may have replaced by the time it runs.
       */
      const pending = requested.current;
      const [productPart = '', quantityPart = '', projectPart = ''] = key.split(':');
      const productId = Number(productPart);
      const quantity = Number(quantityPart);
      const projectId = projectPart === '' ? null : Number(projectPart);
      api
        .resolveProductInclusionPreset(productId, quantity, signal, projectId)
        .then((result) => {
          if (signal.aborted) return;
          setPresets((previous) => new Map(previous).set(key, result.data));
        })
        .catch(() => {
          /*
           * Swallowed on purpose and NOT retried. A resolve that failed leaves
           * the key absent, which seeds nothing; retrying on every render would
           * spend the shared rate limit on a pair that is answering 4xx.
           */
          if (signal.aborted) return;
        })
        .finally(() => {
          // An ABORTED read never answered, so its key must not stay recorded —
          // otherwise a re-armed mount would skip a pair it has no answer for.
          if (signal.aborted) pending.delete(key);
        });
    }
  }, [settled]);

  return presets;
}

interface ProjectsState {
  rows: Project[];
  loading: boolean;
  failed: boolean;
  truncated: boolean;
}

/**
 * The active project list, resolved ONCE for the whole grid.
 *
 * ⚠️ The per-line hook in `@/lib/use-line-project` is deliberately not used: it
 * fetches per row, and a grid of twelve rows would issue twelve reads of the
 * same list. Its pure helper `productOfProject()` IS used — it is a function
 * over a list, so it carries no fetch, and it is the one copy of "a project
 * implies its product".
 *
 * ## 🔴 IT PAGES, AND THE CAP IT PAGES PAST WAS BEING HIT IN PRODUCTION
 *
 * ⚠️ *SUPERSEDED: one `listProjects` read of `perPage: 100`, reporting
 * `truncated` when `meta.total` exceeded what arrived.* `/projects` caps
 * `perPage` at **100, the server's own ceiling**, and production held **118
 * active projects on 2026-09-15**. One page arrived 18 short, and because the
 * order is `name` ascending it was **the same 18 every time** — the tail of the
 * alphabet, which reads as a naming convention rather than as a truncation.
 *
 * **All eight projects selling `Virtual Masterclasses` sat at ranks 106-113**,
 * so `projectSellsProduct` below matched nothing and that product's picker was
 * empty on every row of every grid, with nothing on screen saying why.
 *
 * ⛔ **The fix is paging, NEVER a bigger `perPage`** — 101 is a 422 and there is
 * no larger value to reach for. {@link api.listAllProjects} owns the loop, and
 * **throws rather than resolving short**.
 *
 * ⚠️ **`sort` is still sent, and under paging it is load-bearing rather than
 * cosmetic**: an unstable order across pages repeats rows and skips others, so
 * the list comes out right in COUNT and wrong in CONTENT.
 *
 * ⚠️ **`truncated` can no longer be true**, and the callers' handling of it is
 * kept: an incomplete list now arrives as `failed`, and the `true` branch is
 * still the correct treatment of one. **Not evidence that truncation is
 * reachable here.**
 */
function useActiveProjects(): ProjectsState {
  const [rows, setRows] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    api
      .listAllProjects(controller.signal, { isActive: true, sort: 'name', order: 'asc' })
      .then((all) => {
        if (controller.signal.aborted) return;
        setRows(all);
        setFailed(false);
      })
      .catch(() => {
        /*
         * ⛔ A partial list is DISCARDED, never rendered. A project list that
         * stops early looks identical to a complete one and this grid asserts
         * over it, so a flaky second page would become a confident falsehood.
         * The cost: the control locks and nothing says why, the failed-read
         * footnote having been removed on 2026-09-05. The owner's call.
         */
        if (controller.signal.aborted) return;
        setRows([]);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);
  /* Never true: see the doc above. A literal, so it reads as settled. */
  return { rows, loading, failed, truncated: false };
}

/* ── Presentational bits ──────────────────────────────────────────────────── */

function ErrorBlock({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <Notice tone="danger" role="alert" title={title}>
      {children}
    </Notice>
  );
}

function TrashIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
    </svg>
  );
}

/** The Price cell's "no price at all" toggle — a struck-through circle. */
function NoPriceIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3.5 w-3.5"
    >
      <circle cx="12" cy="12" r="8" />
      <path d="m6.5 6.5 11 11" />
    </svg>
  );
}

function LinesIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M4 7h16M4 12h16M4 17h10" />
    </svg>
  );
}

function StatusBadge({ row, dirty }: { row: Row; dirty: boolean }) {
  if (row.state === 'saving') return <Badge tone="info">Saving…</Badge>;
  if (row.state === 'failed') return <Badge tone="danger">Not saved</Badge>;
  if (row.state === 'saved' && !dirty) return <Badge tone="success">Saved</Badge>;
  if (dirty) return <Badge tone="warning">{row.saved === null ? 'New' : 'Unsaved'}</Badge>;
  return <span className="text-xs text-fg-subtle">—</span>;
}

interface ControlProps {
  row: Row;
  problems: RowProblems;
  disabled: boolean;
  /**
   * The desktop grid. Labels are hidden and hints move into `placeholder` or
   * `title` — the reference grids are one control per cell with no sentence in
   * them. The stacked card below `md` is not a grid, so it keeps both.
   */
  compact: boolean;
  onChange: (patch: Partial<Draft>) => void;
}

function ProductControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  options,
}: ControlProps & { options: ComboboxOption[] }) {
  const selectedLabel = options.find((option) => option.value === row.draft.productId)?.label;
  const control = (
    <Field label="Item" hideLabel={compact} error={problems.fieldErrors.productId}>
      {(fieldProps) => (
        <Combobox
          {...fieldProps}
          value={row.draft.productId}
          onChange={(value) => onChange({ productId: value })}
          options={options}
          placeholder="Choose a product"
          searchPlaceholder="Search products"
          disabled={disabled}
          invalid={Boolean(problems.fieldErrors.productId)}
        />
      )}
    </Field>
  );
  if (!compact || selectedLabel === undefined) return control;
  return <div title={selectedLabel}>{control}</div>;
}

/**
 * The Description cell, which types over a LIVE derivation.
 *
 * While `description` is `null` the box IS `composeLineDescription`'s answer and
 * re-derives on every render — choosing a product, moving the project or adding
 * a city rewrites it in front of the person. The moment somebody types, it stops
 * following anything, and `Reset to derived` is the only way back.
 *
 * ⚠️ Typed text is never clobbered by a re-derivation. That is a deliberate
 * override, not an `a ?? b` fallback.
 *
 * ⚠️ **The wording says nothing about Xero or about a customer**, unlike the
 * other two grids'. Nothing consumes a deal line's description: there is no deal
 * push and no deal document. It is carried onto the quote or invoice the deal
 * becomes, and that is the only claim this cell may make.
 */
function DescriptionControl({
  disabled,
  compact,
  derived,
  description,
  saved,
  error,
  onTyped,
  onReset,
  onRestore,
}: {
  disabled: boolean;
  compact: boolean;
  derived: string | null;
  description: string | null;
  /**
   * The wording this line is SAVED with — `row.saved?.description ?? null`.
   *
   * ⚠️ **A different question from `description` above, which is the DRAFT.**
   * The gap between the two is exactly what {@link onRestore} puts back.
   * `null` means the saved line derives, so there is no original to return to.
   */
  saved: string | null;
  error: string | undefined;
  onTyped: (value: string) => void;
  onReset: () => void;
  /** Put the SAVED wording back into the draft. */
  onRestore: () => void;
}) {
  const edited = description !== null;
  const value = edited ? description : (derived ?? '');
  /*
   * ⛔ **THE UNDO FOR `Reset to derived`, ADDED 2026-09-12 ON THE OWNER'S
   * REPORT — AND IT IS AN UNDO, NOT A RECOVERY.**
   *
   * `Reset to derived` sends an explicit `null`, which DESTROYS the stored
   * wording, and until now nothing sat beside it to put that back: a mis-click
   * looked like the cell simply re-deriving. It happened on an invoice.
   *
   * ⚠️ **It reaches back only as far as the LAST SAVE.** Once a
   * `description: null` is saved, `row.saved.description` IS null and there is
   * nothing to restore from. This stops a mis-click becoming permanent; it does
   * not undo one that already has.
   *
   * The test is `description !== saved`, not `edited` — after `Reset to
   * derived` the draft is `null` and the saved value is a string, which is
   * precisely the state this exists for and the one `edited` would miss.
   */
  const restorable = saved !== null && description !== saved;
  return (
    <Field label="Description" hideLabel={compact} error={error}>
      {(fieldProps) => (
        <div className="grid gap-1">
          <Textarea
            {...fieldProps}
            autoGrow={compact ? { minRows: 2, maxRows: 6 } : { minRows: 3, maxRows: 10 }}
            value={value}
            onChange={(event) => onTyped(event.target.value)}
            placeholder={derived ?? 'Choose an item'}
            disabled={disabled}
            invalid={Boolean(error)}
          />
          {(edited || restorable) && !disabled && (
            /* Two ways back, two destinations — one row, so a cell that gains
               the undo does not get taller than its neighbours. */
            <div className="flex flex-wrap items-center gap-1">
              {edited && (
                <Button type="button" variant="ghost" size="sm" onClick={onReset}>
                  Reset to derived
                </Button>
              )}
              {restorable && (
                /* `title` carries the text itself so it can be recognised
                   before it is restored — a value the cell is not currently
                   showing, which is the documented exception rather than
                   advice. */
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onRestore}
                  title={`Put back: ${saved}`}
                >
                  Reset to original
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </Field>
  );
}

/** Everything one row's project and city pickers need, resolved once. */
interface RowProject {
  /**
   * One option list per Current / Previous pill, the row's stored project pinned
   * into **both** — see `projectOptionsByScope`, which is where that invariant
   * lives. The pill is per row and its state sits in {@link ProjectControl}; the
   * `/cycles` read behind the split is once per grid.
   */
  options: Record<ProjectCycleScope, ComboboxOption[]>;
  /** Per pill: "No projects in Current. 5 in Previous." or nothing at all. */
  projectFootnotes: Record<ProjectCycleScope, string | undefined>;
  cityOptions: MultiComboboxOption[];
  /** `null` means "cannot say" — never narrow or refuse on it. */
  declaredCityIds: Set<number> | null;
  /**
   * The regions the LINE's project declares. **`null` means the project could
   * not be resolved** — the list failed, was truncated at 100, or the line holds
   * a retired project an active-only fetch never returns — and is NOT the same
   * as a project that declares none, which is `[]`.
   *
   * ⚠️ **This is what the region picker OFFERS on a project-backed row**, on the
   * owner's decision: a line stays inside the geography its project actually
   * covers rather than being offered all of SEED's regions. A projectless row
   * has no project to take a set from and keeps the `/regions` master.
   *
   * ⚠️ **It never NARROWS what the line already holds.** Regions are validated
   * against the `/regions` master alone server-side, so a region absent from
   * this list is not a refusal — it is pinned back into the options and
   * labelled, exactly as a stray city is.
   */
  projectRegions: { id: number; name: string }[] | null;
  /**
   * ⚠️ **A DEFINITE "this project declares no cities", and nothing weaker.**
   *
   * True only when a project is chosen AND was resolved AND its own city list is
   * empty. Every unresolved state — no project picked yet, the list still
   * loading, failed, truncated, or a retired project — is `false`, because this
   * gates {@link showsRegionsControl} and that gate must fail towards hiding.
   *
   * ⚠️ Read off `selected.cities`, the project's own declaration, and **never
   * off {@link cityOptions}**, which also carries the row's stored cities the
   * project no longer declares. Those are real entries on the line and say
   * nothing about what the project offers today.
   */
  projectHasNoCities: boolean;
  projectHint: string | undefined;
  cityHint: string | undefined;
  citiesUnavailable: boolean;
  /**
   * Whether this row shows the project block at all.
   *
   * ⚠️ *SUPERSEDED, 2026-08-31: "— the project picker and the city picker, which
   * stand or fall together because a city is only ever chosen through a
   * project."* **They no longer stand or fall together.** The premise is still
   * true and no longer decides the render on its own: the city picker has its
   * own gate, {@link showsCitiesControl}, which additionally takes it away on a
   * project that declares none. `false` here still hides both — it is a
   * precondition of the city gate — but `true` no longer implies the city picker
   * is up. ⚠️ **On the WIRE they do still stand or fall together**: this value
   * alone is `offersProjects`, and it gates `projectId` and `cityIds` in
   * {@link createPayload} and {@link updatePayload}. Do not pass the city gate
   * there.
   *
   * ⚠️ **This reproduces a SERVER-side derivation and is therefore always one
   * project late.** "Sold through projects" is not a flag on a product; the
   * guard tests `product_id = ? AND is_active = 1`, which is byte for byte what
   * `GET /projects?productId=N&isActive=true` asks, so the answer here is only
   * as fresh as {@link useActiveProjects}.
   *
   * ⚠️ **So it fails towards SHOWING, on every UNRESOLVED state.** Loading,
   * failed, truncated, and any line that already holds a project all answer
   * `true` — hiding a control on a load failure is unrecoverable from the user's
   * side, and hiding one on a line that HAS a project would make that project
   * invisible and unremovable.
   *
   * 🔴 **THERE ARE NOW TWO WAYS TO GET `false`, AND ONLY ONE OF THEM IS A
   * VERDICT ABOUT THE PRODUCT.** ⚠️ *SUPERSEDED 2026-09-19: "`false` is only
   * ever returned when the list has settled and definitely names no project for
   * this product."* It is also returned for **a row with no product chosen**, on
   * the owner's decision of that date, which retires the project-first entry
   * path. The two are not interchangeable: one says *this item is not sold that
   * way*, the other says *nothing has been asked yet*. Anything reading this
   * field as the first meaning alone — `showsRegionsControl` did — has to test
   * `draft.productId` for itself.
   *
   * ⚠️ **`false` is not a fault and must never be reported as one.** `Meet Up`,
   * `Profile Evaluations` and `Miscellaneous` have no projects and never will.
   * The block is simply absent, with nothing said about it.
   *
   * ⚠️ Hiding is a RENDER branch. It never touches the draft, and the payload
   * builders take it as an explicit parameter — see {@link createPayload}.
   */
  showProject: boolean;
}

/**
 * The fallback for a row the resolver has not caught up with.
 *
 * Everything narrowing is at its widest-or-unknown setting: no declared cities
 * (`null` — "cannot say", which never refuses) and `showProject: true`.
 * ⚠️ **Never default to HIDING a control or NARROWING a set on the strength of
 * not knowing.**
 */
/** Every pill empty. Written as a `Record` so a third scope fails the build here. */
const NO_PROJECT_OPTIONS: Record<ProjectCycleScope, ComboboxOption[]> = {
  current: [],
  previous: [],
};

/** Nothing to say under any pill — see `projectScopeFootnote`. */
const NO_PROJECT_FOOTNOTES: Record<ProjectCycleScope, string | undefined> = {
  current: undefined,
  previous: undefined,
};

const ROW_PROJECT_UNRESOLVED: RowProject = {
  options: NO_PROJECT_OPTIONS,
  projectFootnotes: NO_PROJECT_FOOTNOTES,
  cityOptions: [],
  declaredCityIds: null,
  /*
   * Nothing resolved, so the region picker falls back to the `/regions` master
   * rather than narrowing against a project it could not read — narrowing on an
   * unknown is how a stored region silently leaves a whole-set REPLACE.
   */
  projectRegions: null,
  /*
   * ⚠️ `false`, and it is the ONE default here that reads like a narrowing and
   * is not. Nothing resolved is not "this project declares no cities"; it is
   * "we cannot say", and this gate OPENS a control, so the unknown must not open
   * it. `showProject` above defaults the other way for the same reason read from
   * the other end. See {@link showsRegionsControl}.
   */
  projectHasNoCities: false,
  projectHint: undefined,
  cityHint: undefined,
  citiesUnavailable: true,
  showProject: true,
};

/**
 * ⚠️ **Project and Cities are ONE PATCH however they are rendered.** Choosing a
 * project clears the city selection in the SAME `onChange`, because the server's
 * guard runs on the POST-MERGE state and would otherwise re-validate the STORED
 * cities and refuse naming cities nobody touched. Never split that patch — it
 * stays easy to do because the rendering and the payload are independent.
 */
function ProjectControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  resolved,
}: ControlProps & { resolved: RowProject }) {
  /*
   * ⚠️ **Per ROW, and `current` on every mount — owner's instruction.** It is a
   * filter over what is offered and NOT a value, so it is deliberately local
   * state and is never written anywhere: a saved pill would be a second
   * authority over what a person may pick, and the row's own project already
   * says which half it came from.
   *
   * A shared grid-level pill would mean switching to Previous on row 3 silently
   * re-listing rows 1 and 2, which is a dropdown changing under somebody who
   * never opened it.
   */
  const [scope, setScope] = useState<ProjectCycleScope>(DEFAULT_PROJECT_CYCLE_SCOPE);
  const options = resolved.options[scope];

  return (
    <Field
      label="Project"
      /*
       * ⚠️ Labelled even under `compact`. `hideLabel` is right when the COLUMN
       * HEADER labels the cell; this control lives inside the Item cell and no
       * header names it. A label is not a hint — the no-prose-in-a-grid-cell
       * rule is untouched, and `compact` still drops the hint.
       */
      error={problems.fieldErrors.projectId}
      {...(!compact && resolved.projectHint ? { hint: resolved.projectHint } : {})}
    >
      {(fieldProps) => (
        <Combobox
          {...fieldProps}
          value={row.draft.projectId}
          onChange={(value) => onChange({ projectId: value, cityIds: [] })}
          options={options}
          emptyOptionLabel="No project"
          placeholder="No project"
          searchPlaceholder="Search projects"
          header={<ProjectScopePills value={scope} onChange={setScope} />}
          {...(resolved.projectFootnotes[scope]
            ? { footnote: resolved.projectFootnotes[scope] }
            : {})}
          /*
           * ⚠️ Disabled on the WHOLE row's project list, never on this pill's.
           * An empty Current with rows under Previous must still open, or the
           * only control that could reach them is behind a disabled trigger.
           */
          disabled={disabled || PROJECT_CYCLE_SCOPES.every((s) => resolved.options[s].length === 0)}
          invalid={Boolean(problems.fieldErrors.projectId)}
        />
      )}
    </Field>
  );
}

/**
 * The Current / Previous pills, inside the project dropdown.
 *
 * ⚠️ **Current means current AND UPCOMING** — the owner's own clarification, and
 * the reason the rule is one `endDate >= today` comparison rather than two. See
 * `src/lib/project-cycle-scope.ts`.
 *
 * ⛔ **No hint, no sentence, no tooltip. The two labels are the whole
 * explanation**, which is what keeps this inside the no-helper-text rule while
 * rendering in a control that lives inside a desktop grid cell. The only prose
 * the popover may carry is the `footnote`, which states a fact about the data.
 *
 * `SegmentedControl` rather than hand-rolled pills: it already gives the group
 * label, `aria-pressed` per segment, a tick so selection is not colour alone,
 * and a constant width across selections. Its segments size to their content
 * and the popover is `width: max-content` with a 240px floor, so two short
 * labels fit at 320px.
 */
function ProjectScopePills({
  value,
  onChange,
}: {
  value: ProjectCycleScope;
  onChange: (next: ProjectCycleScope) => void;
}) {
  return (
    <SegmentedControl
      label="Show projects from"
      value={value}
      onChange={onChange}
      options={PROJECT_CYCLE_SCOPES.map((scope) => ({
        value: scope,
        label: PROJECT_CYCLE_SCOPE_LABELS[scope],
      }))}
    />
  );
}

/**
 * The line's price tier (owner 2026-10-27), directly under the project.
 *
 * `null` on the draft IS the default tier, so choosing the default writes
 * `null` and a line nobody tiered stays untouched. Tiers are matched by id and
 * the default by its `isDefault` flag, never by name.
 */
function PriceTierControl({
  row,
  problems,
  disabled,
  onChange,
  options,
  defaultTierId,
}: ControlProps & { options: PriceTier[]; defaultTierId: number | null }) {
  const value = row.draft.priceTierId ?? defaultTierId;
  return (
    <Field label="Price" error={problems.fieldErrors.priceTierId}>
      {(fieldProps) => (
        <Select
          {...fieldProps}
          value={value === null ? '' : String(value)}
          onChange={(event) => {
            const next = Number(event.target.value);
            onChange({ priceTierId: next === defaultTierId ? null : next });
          }}
          disabled={disabled}
          invalid={Boolean(problems.fieldErrors.priceTierId)}
        >
          {options.map((tier) => (
            <option key={tier.id} value={String(tier.id)}>
              {tier.name}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

/**
 * The Cities cell — the explicit set this line runs in.
 *
 * ⚠️ **There is no "all cities" tick box here, and adding one would be a 422.**
 * `f_deal_products` has no `all_project_cities` column — the flag is a quote and
 * invoice feature. See this file's header.
 */
function CitiesControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  resolved,
}: ControlProps & { resolved: RowProject }) {
  const refusal = row.refusal;
  const invalidCityValues =
    refusal !== null && refusal.path === 'cityIds' ? refusal.offendingCityIds.map(String) : [];
  const fixIsOnTheProject =
    refusal !== null && refusal.path === 'cityIds' && refusal.fixedOn === 'the-project';
  /*
   * Refusal 6 — "that project declares no cities" — arrives on `cityIds` and is
   * fixed on `/projects/{id}`. Marking the control and saying no more sends the
   * person to a picker that is empty by definition, i.e. to advice they cannot
   * follow.
   */
  const fixLocation = fixIsOnTheProject
    ? 'Add cities on the project.'
    : null;
  const cityHint = fixLocation ?? resolved.cityHint;

  const control = (
    <Field
      label="Cities"
      /* Labelled under `compact` too — see {@link ProjectControl}: no column
         header names this control. */
      {...(problems.fieldErrors.cityIds ? { error: problems.fieldErrors.cityIds } : {})}
      {...(!compact && cityHint ? { hint: cityHint } : {})}
    >
      {(fieldProps) => (
        <MultiCombobox
          {...fieldProps}
          values={row.draft.cityIds.map(String)}
          invalidValues={invalidCityValues}
          onChange={(values) => onChange({ cityIds: values.map(Number) })}
          options={resolved.cityOptions}
          placeholder="Not city-specific"
          searchPlaceholder="Search this project’s cities"
          disabled={disabled || resolved.citiesUnavailable}
          invalid={Boolean(problems.fieldErrors.cityIds)}
          maxSelected={MAX_CITIES_PER_LINE}
          /*
           * ⚠️ **OFFERED HERE AND DELIBERATELY NOT ON THE QUOTE OR INVOICE
           * GRIDS, and the asymmetry is this file's own `allCities` note.**
           * Those two carry a real flag meaning *every city its project runs
           * in, re-resolved as the project changes*; a client-side select-all
           * beside it would be a SNAPSHOT of today's cities wearing the same
           * word, and the two diverge silently the moment the project moves.
           * A deal line has no such flag, so here the row is the only way to
           * say "all of them" and cannot be confused with one.
           *
           * `maxSelected` above still binds: the component refuses the row
           * outright when selecting all would overshoot the cap, rather than
           * applying part of it.
           */
          selectAll
          {...(compact
            ? { maxVisibleChips: 0, summaryNoun: { singular: 'city', plural: 'cities' } }
            : {})}
        />
      )}
    </Field>
  );
  const withFixLocation =
    fixLocation === null ? (
      control
    ) : (
      <div className="grid gap-1">
        {control}
        <p className="text-2xs text-fg-muted">{fixLocation}</p>
      </div>
    );
  if (!compact || !cityHint) return withFixLocation;
  return <div title={cityHint}>{withFixLocation}</div>;
}

/**
 * Whether the cities block is on screen for this row.
 *
 * ⚠️ **THIS GATE IS NEW, 2026-08-31, AND IT IS THE OWNER'S — asked for four
 * times.** Until it existed the city picker rode entirely on
 * {@link RowProject.showProject}, so a line whose project declares no cities —
 * `Virtual Masterclasses`, `LAE`, the products he names — got an **empty, greyed
 * picker** and the hint *"Add cities on the project."*, which is refusal 6
 * (`project-has-no-cities`) whose own documented fix is **on the project and not
 * on this line**. A control that can only be satisfied somewhere else is not a
 * control; it is a dead end with a label.
 *
 * ⚠️ **It reads the SAME `projectHasNoCities` {@link showsRegionsControl} opens
 * the regions block on**, off the same resolved row, so the swap is one value
 * and one render: cities out, regions in. A second test of that idea here would
 * be a second answer, and the two would come apart on exactly the row that has
 * both.
 *
 * ⚠️ **`projectHasNoCities` is a DEFINITE no and never an unresolved one**, so
 * **unknown keeps the control up**: a project still loading, a failed or
 * truncated list, and a retired project an active-only fetch never returns all
 * answer `false`. See {@link RowProject.projectHasNoCities} for the three
 * conditions and {@link ROW_PROJECT_UNRESOLVED} for the default.
 *
 * ⚠️ **NEVER-HIDE-DATA, ABOVE the new test.** A row that already names cities
 * keeps its picker on a project declaring none, and that pair is **reachable,
 * not defensive**: a project whose cities were removed after the line was
 * written leaves `draft.cityIds` full while `selected.cities` is empty, and
 * those chips are pinned back into {@link RowProject.cityOptions} labelled "no
 * longer on this project". Hidden, they would be invisible and unremovable while
 * {@link updatePayload} still carried them.
 *
 * ⚠️ **RENDER ONLY — and on THIS grid that distinction has teeth.** `cityIds`
 * and `projectId` are gated on the wire by `offersProjects`, which is
 * {@link RowProject.showProject} and **not this**. That is deliberate: this gate
 * hides a picker whose options are empty, it does not assert the line has no
 * cities, and routing it into {@link createPayload} or {@link updatePayload}
 * would turn "there was nothing to pick" into `cityIds: []` — a full REPLACE
 * that destroys a stored set with a `200`.
 *
 * ⚠️ **There is no "all cities" box on a deal line to hide with it** — see
 * {@link CitiesControl}. On the quote and invoice grids the flag and the picker
 * go together; here there is only the picker.
 */
function showsCitiesControl(
  row: Row,
  showsProject: boolean,
  projectHasNoCities: boolean,
): boolean {
  if (!showsProject) return false;
  if (row.draft.cityIds.length > 0) return true;
  return !projectHasNoCities;
}

/**
 * Whether the LINE's own regions block is on screen for this row.
 *
 * ⚠️ **Two cases reach it, and neither is "the complement of the project
 * block".** A line carries regions when it has no CITIES to carry a geography:
 *
 * 1. the product is **not sold through projects at all** — `EMBA`, `LAE`,
 *    `Virtual Masterclass`, which carried no geography anywhere before migration
 *    112;
 * 2. the line's **project declares no cities**, which is the owner's addition of
 *    2026-08-31. Such a row previously got an empty city picker and nothing else
 *    — refusal 6, `project-has-no-cities`, whose own documented fix is **on the
 *    project and not on this line**, so the person was shown a control they could
 *    not act on and no way to record the line's geography at all.
 *
 * ⚠️ **`showProject` fails towards SHOWING, so both of this function's own
 * inputs fail towards HIDING.** A loading, failed or truncated project list
 * keeps the project block up and leaves this one down, and
 * `projectHasNoCities` is `false` for every merely-UNRESOLVED state. That is the
 * right direction: a region set offered on a row that turns out to be
 * city-bearing invites somebody to record a geography that is not how the line
 * is tracked, where a block briefly absent costs one re-render.
 *
 * ⚠️ **A row that already HOLDS regions keeps its block whatever else is true**
 * — the same never-hide-data rule the project block follows.
 *
 * Hiding is a render branch and nothing else. It never touches
 * `draft.regionIds`: a regions write REPLACES the set, so a block that tidied
 * its own draft on the way to being hidden would clear a line's stored regions
 * on the next unrelated save, with a `200` and nothing to restore from.
 */
function showsRegionsControl(
  row: Row,
  showsProject: boolean,
  projectHasNoCities: boolean,
): boolean {
  /*
   * 🔴 **A ROW WITH NO ITEM OPENS NOTHING — added 2026-09-19 WITH the change
   * that hides the project block on a productless row, and it is not optional
   * tidying.** `!showsProject` used to mean one thing, "this product is not sold
   * through projects", and now means two. Left as it was, the empty row that just
   * lost its project picker would have GAINED a regions picker in the same render
   * — one control swapped for another on a line that has not said what it sells.
   *
   * That is the direction this function's own doc calls wrong: a region set
   * offered on a row that turns out to be city-bearing invites somebody to
   * record a geography that is not how the line is tracked. A row with no item
   * is the purest case of "turns out to be" — nothing is known yet.
   *
   * ⚠️ It reads `draft.productId` directly rather than taking a fourth boolean:
   * "has an item been chosen" is a one-field read off the row this function
   * already has, not a second derivation of "is this sold through projects",
   * which must keep coming from `showsProject` alone.
   *
   * The never-hide-data rule still outranks it — a row already holding regions
   * keeps them, or a stored set becomes invisible and unremovable.
   */
  if (row.draft.productId === '') {
    return row.draft.regionIds.length > 0 || (row.saved?.regions.length ?? 0) > 0;
  }
  if (!showsProject) return true;
  if (projectHasNoCities) return true;
  return row.draft.regionIds.length > 0 || (row.saved?.regions.length ?? 0) > 0;
}

/**
 * SEED's regions of focus for this line — the geography a line carries when it
 * has no cities to carry one.
 *
 * ⚠️ **This is the control a DEAL LINE shipped without entirely.** `regionIds`
 * has been accepted on write and returned on every read since migration 112,
 * while the only deal picker for it sat in `LineItemFormModal`. The quote and
 * invoice grids gained theirs first; this one landed 2026-08-31 with the owner's
 * second case, so all three grids now agree about a line's geography — which
 * matters because a deal's lines are copied verbatim into the quote or invoice
 * it converts into, and a control on one document and not the other produces two
 * different answers about one sale.
 *
 * ## ⚠️ TWO rows reach it, and they offer DIFFERENT LISTS
 *
 * | the row | the picker offers | “All regions” |
 * | --- | --- | --- |
 * | no project at all | the `/regions` **master** | **absent** |
 * | a project declaring **no cities** | that **project's own** regions | shown |
 *
 * A projectless line has nothing to be "all" OF — the only bounded set available
 * would be SEED's whole master, and a box meaning *every region SEED sells in*
 * is a claim about the business, not about the line. So the checkbox renders
 * only where a project supplies the bound.
 *
 * ## ⛔ “All regions” IS A SELECT-ALL, AND THERE IS NO FLAG BEHIND IT
 *
 * ⚠️ **There is no `allRegions` key on the wire and none may be invented** —
 * `deal-products.schema.ts` is `.strict()` and declares `regionIds` alone. So
 * this box writes an EXPLICIT set of ids and there is nothing else it could
 * write.
 *
 * ⚠️ **On a DEAL line there is not even an `allCities` to be confused with** —
 * `f_deal_products` has no `all_project_cities` column, so this grid's city cell
 * carries no tick box at all. The hazard is one document over: the quote and
 * invoice grids DO have both boxes side by side, where `allCities` is a live
 * rule the server re-resolves and this one is a snapshot. Keep the two spelled
 * the same way across all three grids so the difference is learned once.
 *
 * ⚠️ **The tick is DERIVED, never stored.** {@link Draft} gains no flag: the box
 * is checked when the draft equals the project's set and unchecks itself when
 * the project gains a region — the snapshot going stale in public, which is the
 * visible wrong answer rather than the silent one.
 *
 * ⚠️ **Rendered only by {@link showsRegionsControl}.** Hiding never touches
 * `draft.regionIds`.
 */
function RegionsControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  options,
  loading,
  projectRegions,
}: ControlProps & {
  options: MultiComboboxOption[];
  loading: boolean;
  /** The resolved project's own regions, or `null` when there is no project to take a set from. See {@link RowProject.projectRegions}. */
  projectRegions: { id: number; name: string }[] | null;
}) {
  /*
   * One key, one control. Unlike the city cell there is no second path a 422 can
   * arrive on — `regionIds` is the only region key this grid sends, and the
   * checkbox below writes that same key — so there is nothing to join and no
   * coalesce that could drop half of a true statement.
   */
  const error = problems.fieldErrors.regionIds;

  /*
   * ⚠️ Offered only when the project declares regions. A box reading "All
   * regions" over a project that has none can only ever select nothing, which is
   * the affordance-with-one-outcome this codebase refuses everywhere else.
   */
  const bounded = projectRegions !== null && projectRegions.length > 0 ? projectRegions : null;
  /*
   * ⚠️ Set equality, both directions. `length` alone would tick the box for a
   * line holding the right NUMBER of the wrong regions — including one the
   * project no longer declares, which is exactly the pinned-back option.
   */
  const chosen = new Set(row.draft.regionIds);
  const isAll =
    bounded !== null &&
    chosen.size === bounded.length &&
    bounded.every((region) => chosen.has(region.id));

  const control = (
    <Field
      label="Regions of focus"
      /* Labelled under `compact` too — see {@link ProjectControl}: no column
         header names this control. */
      {...(error ? { error } : {})}
      {...(!compact && projectRegions === null
        ? // True only on the projectless row. On a project-backed one the cities
          // control directly above already says to add cities on the project,
          // and repeating it here would be prose for its own sake.
          { hint: 'Not sold through projects, so no cities.' }
        : {})}
    >
      {(fieldProps) => (
        <div className="grid gap-1.5">
          {bounded !== null && (
            <Checkbox
              label="All regions"
              checked={isAll}
              disabled={disabled}
              onChange={(event) =>
                /*
                 * ⚠️ ONE key, and it writes the ids OUT — there is no flag to
                 * set. Unticking clears rather than handing a parked set back,
                 * because ticking never parked one: it replaced the selection
                 * with the project's, and re-ticking rebuilds exactly the same
                 * set.
                 */
                onChange({
                  regionIds: event.target.checked ? bounded.map((region) => region.id) : [],
                })
              }
            />
          )}
          <MultiCombobox
            {...fieldProps}
            values={row.draft.regionIds.map(String)}
            /*
             * The order handed back is kept as it comes. It is not stored and
             * {@link regionsKey} ignores it, so re-sorting here would be churn
             * that changes nothing — and sorting a set the user just arranged is
             * the kind of tidying that reads as a bug.
             */
            onChange={(values) => onChange({ regionIds: values.map(Number) })}
            options={options}
            placeholder="No regions"
            searchPlaceholder="Filter regions"
            disabled={disabled}
            invalid={Boolean(error)}
            loading={loading}
            // Six regions, all active. No flag twin to be confused with here.
            selectAll
            {...(compact
              ? { maxVisibleChips: 0, summaryNoun: { singular: 'region', plural: 'regions' } }
              : {})}
          />
        </div>
      )}
    </Field>
  );

  /*
   * ⚠️ **The one sentence that keeps this box from being read as the quote
   * grid's `allCities`.** It states the fact a reader cannot otherwise see — that
   * the set is frozen — and prevents a specific wrong conclusion about a value
   * that decides how the converted document is tracked in Xero, which is the
   * category the no-helper-text rule protects rather than the one it bans. It
   * renders only while the box is ticked, and never inside a desktop grid cell.
   */
  if (compact || !isAll) return control;
  return (
    <div className="grid gap-1">
      {control}
      <p className="text-2xs leading-snug text-fg-muted">
        This line’s own set, as it stands today. A region added to the project
        later is not added here.
      </p>
    </div>
  );
}

function CityChips({ cities }: { cities: LineCity[] }) {
  if (cities.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1">
      {cities.map((city) => (
        <li
          key={city.id}
          className="rounded-sm border border-border bg-surface-sunken px-1.5 py-0.5 text-2xs text-fg-muted"
        >
          {cityLabel(city)}
        </li>
      ))}
    </ul>
  );
}

/**
 * The Qty box.
 *
 * ⚠️ **`field` is `'quantity'` and nothing else.** The other two grids' copy of
 * this control also serves `taxRate`; there is no second numeric column on a
 * deal line, and the Discount cell has its own control below.
 */
function QuantityControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  step,
}: ControlProps & { step: number | undefined }) {
  return (
    <Field label="Qty" hideLabel={compact} error={problems.fieldErrors.quantity}>
      {(fieldProps) => (
        <Input
          {...fieldProps}
          inputMode="numeric"
          value={row.draft.quantity}
          onChange={(event) => onChange({ quantity: event.target.value })}
          min={step ?? 1}
          step={step ?? 1}
          placeholder="1"
          disabled={disabled}
          invalid={Boolean(problems.fieldErrors.quantity)}
          className="text-right tabular-nums"
        />
      )}
    </Field>
  );
}

/**
 * Discount — a PERCENTAGE or an exact money AMOUNT, whichever the row is set to.
 *
 * ⚠️ **NOTHING IS CONVERTED ANY MORE.** `f_deal_products` carries
 * `discount_amount` beside `discount`, mutually exclusive, so an amount is
 * stored as the amount. What was here — a `lossy` warning class, and a
 * paragraph under the box explaining that this line stores a percentage which
 * takes a DIFFERENT figure off than the one just typed — was the apology for a
 * conversion that no longer happens. Do not reintroduce either.
 *
 * ⚠️ **The mode IS part of the draft now**, unlike the screen-state map it
 * replaced: `draft.discountAmount === null` is the percentage, a string is the
 * amount. That is what puts it in `ROW_WRITE_KEYS` and in both payload
 * builders, which is exactly where it now belongs.
 *
 * ⚠️ **Switching mode is not a save and rewrites neither box.** Both figures
 * survive the flip; `discountWriteOf` decides which one the wire carries, and
 * sends the pair so the server never sees two live discounts.
 */
function DiscountControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  onModeChange,
}: ControlProps & {
  onModeChange: (toAmount: boolean) => void;
}) {
  const amountText = row.draft.discountAmount;
  const inAmount = amountText !== null;
  /*
   * Exactly one of the two paths can be wrong at a time, because exactly one is
   * on screen — `validateRow` reports the percentage at `discount` and the
   * amount at `discountAmount`, and a server 422 names whichever key was sent.
   * Reading both is what keeps a stale refusal on the hidden half from
   * colouring the control the person is actually using.
   */
  const error = inAmount ? problems.fieldErrors.discountAmount : problems.fieldErrors.discount;

  /*
   * ⚠️ **The mode switch is a SEGMENTED CONTROL under the box, not two loose
   * buttons beside each other and not a glyph inside the input.** It stays out
   * of the input because `Input`'s trailing reservation is calibrated to a 32px
   * icon-only Button and these labels are words; it stopped being two `sm`
   * buttons labelled `%` and the document's CURRENCY CODE because that made the
   * same choice say `EUR`, `GBP` or `Amount` depending on the document and on
   * whether a company had been picked yet — and because a small button beside a
   * bare label does not read as a pair of choices at all.
   *
   * The two words are the same two words on all three grids: they come from
   * `@/lib/discount-mode`, which is wording and nothing else.
   */
  const modeSwitch = disabled ? null : (
    <SegmentedControl
      label={DISCOUNT_MODE_GROUP_LABEL}
      value={discountModeOf(amountText)}
      options={DISCOUNT_MODE_OPTIONS}
      onChange={(mode) => onModeChange(mode === 'amount')}
    />
  );

  return (
    <Field
      /*
       * ⚠️ The accessible name names the KIND of figure, never the currency. It
       * read `Discount (EUR)`, so it announced itself differently on every
       * document; the unit belongs on the figure, not on the field.
       */
      label={inAmount ? 'Discount (amount)' : 'Discount (percentage)'}
      hideLabel={compact}
      {...(error ? { error } : {})}
    >
      {(fieldProps) => (
        <div className="grid gap-1.5">
          <Input
            {...fieldProps}
            inputMode="decimal"
            value={inAmount ? amountText : row.draft.discount}
            onChange={(event) => {
              // ⚠️ Writes only the box that is on screen. Touching the other one
              // here is how a percentage silently changes while somebody types
              // an amount, which is the defect the second column removed.
              if (inAmount) onChange({ discountAmount: event.target.value });
              else onChange({ discount: event.target.value });
            }}
            placeholder={inAmount ? '0.00' : '0'}
            disabled={disabled}
            invalid={Boolean(error)}
            className="text-right tabular-nums"
          />
          {modeSwitch}
        </div>
      )}
    </Field>
  );
}

function PriceControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  inheritedPrice,
  keepsStored,
  unitLabel,
}: ControlProps & {
  /** What `inherit` resolves to — the city pro-rata of the catalogue price. */
  inheritedPrice: string | null | undefined;
  /** A saved line whose blank box keeps the stored price rather than repricing. */
  keepsStored: boolean;
  unitLabel: string | null;
}) {
  const mode = row.draft.priceMode;
  const noPrice = mode === 'none';
  const toggleLabel = noPrice
    ? 'Record a price on this line'
    : 'Record no price on this line';
  const field = (
    <Field label="Price" hideLabel={compact} error={problems.fieldErrors.unitPrice}>
      {(fieldProps) => (
        <Input
          {...fieldProps}
          inputMode="decimal"
          value={noPrice ? '' : row.draft.price}
          onChange={(event) => {
            const next = event.target.value;
            /*
             * ⚠️ Emptying the box returns to `inherit` — the key is OMITTED, so
             * a new line snapshots the catalogue and an existing one keeps what
             * it has. It must never become `value` with an empty string.
             */
            onChange({ price: next, priceMode: next === '' ? 'inherit' : 'value' });
          }}
          /*
           * ⚠️ The FIGURE when it is known, so somebody deciding whether to
           * override the catalogue can see what they would be overriding.
           */
          placeholder={
            noPrice
              ? 'No price'
              : keepsStored
                ? 'Unchanged'
                : typeof inheritedPrice === 'string'
                  ? formatDecimalString(inheritedPrice)
                  : inheritedPrice === null
                    ? 'No catalogue price'
                    : 'Catalogue'
          }
          disabled={disabled}
          invalid={Boolean(problems.fieldErrors.unitPrice)}
          className="text-right tabular-nums"
          {...(disabled
            ? {}
            : {
                trailingSlot: (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    iconOnly
                    leadingIcon={<NoPriceIcon />}
                    aria-pressed={noPrice}
                    aria-label={toggleLabel}
                    title={toggleLabel}
                    onClick={() =>
                      onChange(
                        noPrice
                          ? { priceMode: 'inherit', price: '' }
                          : { priceMode: 'none', price: '' },
                      )
                    }
                  />
                ),
              })}
        />
      )}
    </Field>
  );
  /*
   * The unit sits BESIDE the box, as the currency code does on the Amount
   * cell — the figure in the box is per one of it.
   */
  return unitLabel === null ? field : (
    <div className="grid gap-1">
      {field}
      <span className="text-2xs text-fg-muted">per {unitLabel}</span>
    </div>
  );
}

/**
 * Amount: the server's `lineTotal` when the row is clean, the preview while it
 * is dirty — and **typed into**, since 2026-09-12.
 *
 * Owner's instruction, the same three-way rule the quote grid carries: *fill any
 * two of price, discount and amount and the third follows.*
 *
 * ⛔ **THE AMOUNT IS A GENERATED COLUMN AND IS NEVER SENT.** `line_total` is
 * `GENERATED STORED`, which is why {@link ROW_WRITE_KEYS} has never carried it
 * and why naming it in a write is a 422. So typing here is not a write — it is a
 * QUESTION: *which writable field, changed to what, makes this line come out at
 * this figure?* `solveLineAmount` in `@/lib/decimal` is the one place that is
 * answered, and it answers by WHICH FIELD IS MISSING:
 *
 *   no price yet  ->  the amount SETS the price      (amount ÷ quantity)
 *   a price set   ->  the amount sets the DISCOUNT   (quantity × price − amount)
 *
 * ## ⚠️ THE ONE PLACE THIS DIFFERS FROM THE QUOTE CELL, AND IT IS THE TAX HALF
 *
 * `solveLineAmount` documents its target as the **pre-tax subtotal**, because on
 * a quote or an invoice tax is charged on the already-rounded subtotal and a
 * tax-INCLUSIVE total is not always reachable to the penny. **On a deal line
 * that distinction does not exist**: `f_deal_products` has no `tax_rate`, no
 * `line_subtotal` and no `line_tax`, and its one generated column `line_total`
 * **IS** that subtotal expression. So the box is simply the line total and every
 * answer here is exact.
 *
 * ⛔ **Do NOT port the quote cell's second "with tax" line**, and do not read
 * `lineSubtotal` / `lineTax` off a deal line to build one — neither column
 * exists, so both would be figures this file invented. That is the
 * harmonisation this file's header forbids, arriving through the Amount cell
 * instead of through the `<colgroup>`.
 *
 * ## 🔴 THE MODE IS PRESERVED — owner's instruction, 13 Sep 2026
 *
 * *"on line item amount change the discount type should not change and be
 * calculated in the type it is in."*
 *
 * ⚠️ *SUPERSEDED: "**The discount is solved as an AMOUNT and never as a
 * percentage**, and the line's mode is flipped to match, saying so … **Never
 * write a rounded percentage here.**"* A line somebody has deliberately put in
 * percentage mode now solves a PERCENTAGE and stays there.
 *
 * ⚠️ **THE ARITHMETIC THAT MOTIVATED THE OLD RULE IS UNCHANGED, WHICH IS WHY
 * THE INEXACT CASE IS SAID OUT LOUD RATHER THAN REFUSED.** `discount` is
 * `DECIMAL(5,2)`, so the subtotals a percentage can express are
 * `quantity × unitPrice × 0.0001` apart — **£1.80 on an 18,000 line**. Type
 * 17,000 against an 18,000 gross and the nearest percentage is 5.56%, a
 * discount of 1,000.80 and a line of **16,999.20**. `solveLineAmount` returns
 * both the percentage and the figure the line will ACTUALLY come to, and
 * `exact: false` is **SAID, never rounded away and never quietly refused** —
 * that silent loss is what migration 111 exists to have removed and what
 * `percentageForAmount` was deleted for. ⛔ **Never write a percentage without
 * reading `exact`.**
 *
 * ⚠️ **A LINE WITH NO DISCOUNT STILL SOLVES AN EXACT AMOUNT, AND THAT IS THE
 * DECISION RATHER THAN AN OVERSIGHT.** `discountAmount === null` is what
 * percentage mode looks like, so it is also what an untouched row looks like —
 * treating every such row as "in percentage mode" would make the commonest edit
 * in this grid lossy for no reason anybody asked for. Owner's decision, the same
 * day: **a line nobody has discounted has no mode to preserve.** That is the
 * whole job of `percentageModeIsChosen`, and the old "mode flipped" note below
 * still fires on exactly those rows, because on them the mode really does flip.
 *
 * ⛔ **`percentage-unreachable` IS A REFUSAL AND MUST NOT FALL BACK TO THE
 * AMOUNT COLUMN.** A gross of zero or below cannot be discounted by any
 * percentage — `discount` is `CHECK (0..100)` — and solving it into
 * `discountAmount` instead would change the mode, which is the one thing this
 * whole change exists to stop. It is reported, naming the gross.
 *
 * ⚠️ **It commits on BLUR and on Enter, never on the keystroke.** Solving
 * mid-type would write a discount off `6`, then `60`, then `600` on the way to
 * `6000`, and each of those is a real edit to a real money column that the row's
 * Unsaved badge would report. Emptying the box hands the cell back to the
 * derived figure — it is not a figure of zero.
 *
 * ⚠️ *SUPERSEDED: the **"Calculated on save" / "Recalculated on save"** text this
 * cell rendered when a dirty row had no computable preview.* That state is now an
 * EMPTY BOX carrying the `Amount` placeholder, and it is the state the box is
 * MOST useful in — a line with no price is exactly the one where a typed amount
 * sets one, so the old text sat where the control now has to be reachable. What
 * it said is not lost: the row's own badge reads "New" or "Unsaved" whenever the
 * figure here is a preview and cannot read anything else, because `dirty` drives
 * both.
 */
function AmountCell({
  row,
  dirty,
  currency,
  preview,
  input,
  disabled,
  onChange,
}: {
  row: Row;
  dirty: boolean;
  currency: string | null;
  /**
   * What the database will store in `line_total`, or `null` on a row with no
   * price — never `"0.00"`. **ONE figure, not the other two grids' triple**; see
   * {@link lineTotalPreview}.
   */
  preview: string | null;
  /**
   * The SAME resolved figures {@link lineTotalPreview} read for this row.
   *
   * ⚠️ **Passed in rather than re-derived, and that is the point.** The box
   * solves backwards from the figure the preview solves forwards to; resolving
   * the price twice is two chances to disagree, and the disagreement would be a
   * discount computed against a price the row is not actually using.
   */
  input: GeneratedLineInput;
  disabled: boolean;
  onChange: (patch: Partial<Draft>) => void;
}) {
  /** `null` means the box is showing the derived figure rather than a typed one. */
  const [typed, setTyped] = useState<string | null>(null);
  /**
   * What the last commit did, or why it could not.
   *
   * ⚠️ **NOT helper text.** It renders only after somebody has typed, and only
   * where the outcome is otherwise invisible — a refusal, a mode flip, or a
   * division that does not come out even. The no-helper-text rule forbids prose
   * attached to a control; this names a state the reader cannot otherwise see,
   * which is the documented exception.
   */
  const [note, setNote] = useState<{ tone: 'error' | 'warn'; text: string } | null>(null);

  /*
   * One figure, from the one generated column. There is no second `derived` to
   * pick between here the way the quote cell chooses subtotal over total.
   */
  const derived = dirty ? preview : (row.saved?.lineTotal ?? null);
  const shown = typed ?? (derived === null ? '' : derived);

  /*
   * ⚠️ **READ-ONLY IS PLAIN TEXT, NEVER A DISABLED BOX.** The house rule, and it
   * is about what the two SAY: a disabled control reads as something the person
   * has not earned the right to use, where text reads as a fact. A `viewer`
   * meets this cell on every line of every deal, and a greyed-out input on all
   * of them would report a permissions problem that does not exist.
   *
   * It also covers the transient `saving` state, where an input would invite a
   * keystroke that goes nowhere.
   *
   * ⚠️ The currency rides BESIDE the figure here rather than under it — the
   * prop's own contract in this file is "rendered beside every figure", and
   * there is no second line on a deal row to carry it.
   */
  if (disabled) {
    return (
      <span
        className="tabular-nums text-sm text-fg"
        {...(dirty ? { title: 'Preview — recalculated by the server on save.' } : {})}
      >
        {formatDecimalString(derived)}
        {derived !== null && currency && (
          <span className="ml-1 text-xs text-fg-muted">{currency}</span>
        )}
      </span>
    );
  }

  const commit = () => {
    if (typed === null) return;
    const amount = typed.trim();
    /*
     * Emptying the box hands the cell back to the derivation, exactly as "Reset
     * to derived" does on the description. It is not a figure of zero, and it
     * must not be solved as one: solving `0` would silently discount the line to
     * nothing.
     */
    if (amount === '') {
      setTyped(null);
      setNote(null);
      return;
    }

    const solution = solveLineAmount({
      // A blank quantity box means the column default of 1, which is what the
      // preview beside this already assumes.
      quantity: input.quantity.trim() === '' ? '1' : input.quantity,
      unitPrice: input.unitPrice,
      amount,
      /*
       * ⚠️ **READ OFF THE DRAFT, NOT OFF `input`.** `input` is the RESOLVED pair
       * `previewInputOf` builds, where amount mode writes the percentage as
       * `'0'` and a blank draft percentage falls back to the STORED one — both
       * correct for previewing the figure, both wrong for asking *did this
       * reader choose a mode?* The draft is where that choice lives, and
       * `draftOf` seeds `discount` from the saved line, so a blank here really
       * does mean a row nobody has discounted.
       */
      keepPercentage: percentageModeIsChosen(row.draft.discountAmount, row.draft.discount),
    });

    if (solution === null) {
      setNote({ tone: 'error', text: 'A figure, up to two decimal places.' });
      return;
    }

    if (solution.kind === 'above-gross') {
      /*
       * `discount_amount` is non-negative by CHECK, so an amount above
       * quantity × price cannot be reached by discounting — the price has to
       * move. The gross is NAMED because "too large" without it is a refusal
       * nobody can act on.
       */
      setNote({
        tone: 'error',
        text: `More than this line’s ${formatDecimalString(solution.gross)}${
          currency ? ` ${currency}` : ''
        } — raise the price, or clear it and let the amount set one.`,
      });
      return;
    }

    if (solution.kind === 'percentage-unreachable') {
      /*
       * ⛔ **NOT A ROUNDING FAILURE — A COLUMN THAT CANNOT EXPRESS THE ANSWER.**
       * `discount` is `CHECK (0..100)`, so on a gross of zero or below no
       * percentage takes anything off at all; on a DEDUCTION line (a negative
       * price, migration 171) a percentage would make the line LESS negative,
       * which `discount_amount`'s own non-negative CHECK forbids in the other
       * direction too.
       *
       * ⛔ **It must NOT quietly solve into `discountAmount` instead.** That
       * would flip the mode — the exact thing the owner's 13 Sep instruction
       * stops — and it would do it on the one row where nothing on screen
       * explains why. Reported in the voice of `above-gross`: name the gross,
       * then name a control that DOES exist rather than asserting an absence.
       */
      setNote({
        tone: 'error',
        text: `A percentage takes nothing off this line’s ${formatDecimalString(
          solution.gross,
        )}${
          currency ? ` ${currency}` : ''
        } — switch the discount to an amount, or change the price.`,
      });
      return;
    }

    if (solution.kind === 'unit-price') {
      onChange({ price: solution.unitPrice, priceMode: 'value' });
      setTyped(null);
      setNote(
        solution.exact
          ? null
          : {
              tone: 'warn',
              // ⚠️ `exact: false` is SAID, never rounded away. 10,000 over 3 is
              // 3,333.33 a unit and a line of 9,999.99 — a penny under what
              // somebody typed, on a figure that becomes a customer's quote.
              text: `Doesn’t divide evenly — priced at ${formatDecimalString(
                solution.unitPrice,
              )} a unit, so the line is a little under.`,
            },
      );
      return;
    }

    if (solution.kind === 'discount-percentage') {
      /*
       * ⚠️ **WRITES ONLY THE BOX THAT IS ON SCREEN, exactly as `DiscountControl`
       * does.** We are only here because `percentageModeIsChosen` said so, which
       * requires `draft.discountAmount === null` — so the mode is already
       * percentage and `discountWriteOf` will resolve this to
       * `{discount, discountAmount: null}` on its own. Writing a
       * `discountAmount` alongside it would be the flip this branch exists to
       * prevent, and writing one as `null` would state a mode change that is not
       * happening.
       */
      onChange({ discount: solution.discountPercent });
      setTyped(null);
      setNote(
        solution.exact
          ? // Nothing happened that the figure in the box does not already say.
            null
          : {
              tone: 'warn',
              /*
               * ⚠️ **`exact: false` is SAID, never rounded away.** `DECIMAL(5,2)`
               * puts the reachable subtotals `quantity × unitPrice × 0.0001`
               * apart — £1.80 on an 18,000 line — so the typed figure often sits
               * between two of them. BOTH figures are named: the percentage that
               * was written, because it is what the row now stores and what the
               * customer's document will print, and the figure the line actually
               * comes to, because that is the one the reader typed and is about
               * to disagree with. Naming only the percentage would leave them to
               * notice the pounds themselves.
               */
              text: `Discount set to ${solution.discountPercent}% — the nearest this line can take, so it comes to ${formatDecimalString(
                solution.amount,
              )}${currency ? ` ${currency}` : ''}.`,
            },
      );
      return;
    }

    // The line was in percentage mode: flipping it is what makes the typed
    // figure exact, and a mode change nobody asked for has to be stated.
    //
    // ⚠️ **This still fires, and only on a row with no CHOSEN mode to keep** —
    // `keepPercentage` was false, so either the row is already in amount mode
    // (nothing flips, `flipped` is false, no note) or nobody has discounted it
    // at all (the mode genuinely flips, and the note says so). The test is
    // unchanged because the question it asks is unchanged: *is the row leaving
    // percentage mode?*
    const flipped = row.draft.discountAmount === null;
    onChange({ discountAmount: solution.discountAmount });
    setTyped(null);
    setNote(
      flipped
        ? {
            tone: 'warn',
            text: `Discount set to ${formatDecimalString(
              solution.discountAmount,
            )} as an amount, not a percentage.`,
          }
        : null,
    );
  };

  return (
    <div className="grid justify-items-end gap-0.5">
      {/*
        ⚠️ The accessible name is **`Amount`** and deliberately not the quote
        cell's "Amount before tax": there is no tax on a deal line, so the
        qualifier would name a distinction this table does not have.
      */}
      <Input
        inputMode="decimal"
        aria-label="Amount"
        value={shown}
        onChange={(event) => {
          setTyped(event.target.value);
          setNote(null);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
        }}
        placeholder={derived === null ? 'Amount' : ''}
        disabled={disabled}
        invalid={note?.tone === 'error'}
        className="text-right tabular-nums"
        title={
          dirty
            ? 'Preview — recalculated on save. Type a figure to set the discount, or the price on a line that has none.'
            : 'Type a figure to set the discount, or the price on a line that has none.'
        }
      />
      {/*
        ⚠️ **The currency code, which used to sit INSIDE the figure.** An input
        cannot carry it, and the quote cell gets away with dropping it only
        because its "with tax" line carries it instead — a line this grid has no
        second figure for and must not invent. Without this the code would
        disappear from the row entirely, against the `currency` prop's own
        contract further up this file. It is the UNIT of the one figure in the
        box, never a second figure.
      */}
      {currency && shown.trim() !== '' && (
        <span className="text-2xs text-fg-muted">{currency}</span>
      )}
      {note && (
        <span
          className={cx('text-xs', note.tone === 'error' ? 'text-danger' : 'text-fg-muted')}
          role={note.tone === 'error' ? 'alert' : undefined}
        >
          {note.text}
        </span>
      )}
    </div>
  );
}

/**
 * The campaign-inclusions editor, shown only on a product an administrator has
 * linked inclusions to.
 *
 * ⚠️ **The picker is NARROWED to the linked set, and that is the check rather
 * than a tidy-up** — writing `inclusions` takes any active inclusion id, so an
 * unnarrowed picker lets somebody tick one the administrator never linked and it
 * saves cleanly. Nothing downstream refuses it.
 *
 * ⚠️ **Narrowing the OPTIONS never narrows the DRAFT.** A write REPLACES the
 * whole set, so an entry filtered off the screen would be an entry deleted on
 * the next save, with a 200 and no warning. Such entries get their own block.
 */
function InclusionsEditor({
  row,
  problems,
  disabled,
  master,
  masterFailed,
  offeredIds,
  onChange,
}: {
  row: Row;
  problems: RowProblems;
  disabled: boolean;
  master: CampaignInclusion[];
  masterFailed: boolean;
  offeredIds: ReadonlySet<number> | null;
  onChange: (patch: Partial<Draft>) => void;
}) {
  const chosen = new Map(row.draft.inclusions.map((entry) => [entry.inclusionId, entry.quantity]));
  const retired: LineInclusion[] = (row.saved?.inclusions ?? []).filter(
    (entry) => chosen.has(entry.id) && !master.some((candidate) => candidate.id === entry.id),
  );
  const offered = master.filter((candidate) => offeredIds === null || offeredIds.has(candidate.id));
  const unlinked = master.filter(
    (candidate) => offeredIds !== null && !offeredIds.has(candidate.id) && chosen.has(candidate.id),
  );
  /*
   * The REGION refusal — `422 INCLUSION_NOT_OFFERED_IN_REGION`, migration 096.
   *
   * ⚠️ Read off `refusal.path`, not off `fieldErrors`. Both carry the server's
   * sentence, but only the refusal says WHERE the fix is, and this is the one
   * refusal in the guard with THREE destinations — the line, the product, and
   * the project. `fieldErrors.inclusions` alone would print "not offered in
   * India" beside a picker that will never list it, and the reader would take
   * that for a fault in the app.
   */
  const rowRefusal = row.refusal;
  const regionRefusal =
    rowRefusal !== null && rowRefusal.kind === 'inclusion-not-offered-in-region'
      ? rowRefusal
      : null;
  /*
   * ⚠️ The server names POSITIONS in the array we sent, and the payload is built
   * from `draft.inclusions` in order, so index N is entry N of the draft. An
   * index the draft no longer has yields `undefined` and is dropped — a refusal
   * left over from before an edit marks nothing rather than marking the wrong
   * chip. Empty is not "nothing is wrong": the sentence above still stands.
   */
  const refusedIds = new Set(
    (regionRefusal?.offendingInclusionIndexes ?? [])
      .map((index) => row.draft.inclusions[index]?.inclusionId)
      .filter((id): id is number => id !== undefined),
  );

  function toggle(inclusionId: number, checked: boolean) {
    if (checked) {
      onChange({ inclusions: [...row.draft.inclusions, { inclusionId, quantity: '1' }] });
    } else {
      onChange({
        inclusions: row.draft.inclusions.filter((entry) => entry.inclusionId !== inclusionId),
      });
    }
  }
  function setQuantity(inclusionId: number, quantity: string) {
    onChange({
      inclusions: row.draft.inclusions.map((entry) =>
        entry.inclusionId === inclusionId ? { ...entry, quantity } : entry,
      ),
    });
  }

  return (
    <div className="grid gap-3">
      <p className="text-xs font-medium text-fg-secondary">
        Campaign inclusions{' '}
        <span className="font-normal text-fg-muted">— saving replaces the whole set</span>
      </p>
      {masterFailed && <ErrorBlock title="The campaign-inclusions list could not be loaded." />}
      {problems.fieldErrors.inclusions && (
        <ErrorBlock title={problems.fieldErrors.inclusions}>
          {/*
            ⚠️ The server's own sentence is the TITLE and this sits BENEATH it,
            never in front. It names ids, regions and products this app cannot
            derive; ours only says where to go.

            ⚠️ Three destinations, and two of them are not on this line — which
            is the whole reason this refusal carries a code. Naming only the line
            would send somebody to a picker that will never list the inclusion
            they came for.
          */}
          {regionRefusal !== null && (
            <ul className="grid list-disc gap-0.5 pl-4">
              <li>Choose another inclusion, or a project in a region that offers it.</li>
              <li>Widen it on the Products screen (administrator).</li>
              <li>Add the region to this line’s project.</li>
            </ul>
          )}
        </ErrorBlock>
      )}
      {!masterFailed && offered.length === 0 && unlinked.length === 0 && retired.length === 0 && (
        <p className="text-xs text-fg-muted">
          {master.length === 0
            ? 'No campaign inclusions are available.'
            : 'Those linked to this item are no longer active.'}
        </p>
      )}
      <ul className="grid gap-2 sm:grid-cols-2">
        {offered.map((inclusion) => {
          const quantityText = chosen.get(inclusion.id);
          const picked = quantityText !== undefined;
          const quantity = Number(quantityText ?? '1');
          return (
            <li
              key={inclusion.id}
              className={cx(
                'flex items-start gap-2',
                // The chip the server actually named, so all of them are visible
                // in one pass rather than one refusal at a time.
                refusedIds.has(inclusion.id) &&
                  'rounded-md bg-danger-soft px-2 py-1 ring-1 ring-danger-border',
              )}
            >
              <Checkbox
                label={inclusion.name}
                description={
                  picked && isValidInclusionQuantity(quantity)
                    ? renderInclusion(inclusion, quantity)
                    : undefined
                }
                checked={picked}
                disabled={disabled}
                onChange={(event) => toggle(inclusion.id, event.target.checked)}
              />
              {picked && (
                <Input
                  aria-label={`Count for ${inclusion.name}`}
                  inputMode="numeric"
                  value={quantityText ?? ''}
                  onChange={(event) => setQuantity(inclusion.id, event.target.value)}
                  disabled={disabled}
                  className="w-16 shrink-0 text-right tabular-nums"
                />
              )}
            </li>
          );
        })}
      </ul>
      {unlinked.length > 0 && (
        <div className="grid gap-1.5 rounded-md border border-border bg-surface px-3 py-2">
          {/* ⚠️ NOT the "retired" wording. A retired id cannot be re-sent at all;
              one of these saves. One label for both would be false about a master
              row and misleading about a document. */}
          <p className="text-xs font-medium text-fg-secondary">
            On this line, but not offered on this item
          </p>
          <p className="text-2xs text-fg-muted">
            Kept as they are. Removing one takes it off.
          </p>
          {unlinked.map((inclusion) => {
            const quantity = Number(chosen.get(inclusion.id) ?? '1');
            return (
              <div key={inclusion.id} className="flex items-center justify-between gap-2">
                <span className="text-xs text-fg-secondary">
                  {isValidInclusionQuantity(quantity)
                    ? renderInclusion(inclusion, quantity)
                    : inclusion.name}
                </span>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => toggle(inclusion.id, false)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {retired.length > 0 && (
        <Notice tone="warning" title="Retired — kept until you change this set">
          <div className="grid gap-1.5">
            {retired.map((entry) => (
              <div key={entry.id} className="flex items-center justify-between gap-2">
                <span className="text-xs text-fg-secondary">
                  {renderInclusion(entry, Number(chosen.get(entry.id) ?? entry.quantity))}
                </span>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => toggle(entry.id, false)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Notice>
      )}
    </div>
  );
}

/**
 * The line's inclusions in words. The wording comes from `@/lib/inclusions` and
 * is never re-implemented: `unitSingular` at exactly 1, `unitPlural` otherwise,
 * both forms stored, nothing pluralised in code.
 *
 * A count of zero never renders.
 *
 * ## ⚠️ `error` is here because a count of zero renders NOTHING
 *
 * The inclusions editor lives inside the collapsed **Details** panel, so
 * `problems.fieldErrors.inclusions` has no home on the row itself — and the one
 * refusal that fires on a set of zero is precisely the one this component would
 * otherwise say nothing about. Without it, choosing an offering item disables Save
 * and flags the row while the row shows no reason at all: the unnameable blocked
 * state CLAUDE.md forbids.
 *
 * ⚠️ **This is a FIELD ERROR, which is an explicit exception to both the
 * no-helper-text prohibition and the no-prose-in-a-desktop-cell rule.** It renders
 * only while the row is in breach and never as standing advice; there is no `hint`
 * here and none may be added.
 *
 * It is rendered whether or not Details is open, so it is briefly duplicated by the
 * editor's own `ErrorBlock`. That is deliberate: on a wide grid the cell says
 * **which row**, the editor says **where to fix it**, and a message that vanished
 * on expanding would be the jumpier of the two behaviours.
 */
function InclusionSummary({
  row,
  master,
  variant = 'chips',
  source = 'draft',
  error,
}: {
  row: Row;
  master: CampaignInclusion[];
  variant?: 'chips' | 'count';
  source?: 'draft' | 'saved';
  error?: string | undefined;
}) {
  const note =
    error === undefined ? null : (
      <p className="mt-1 text-2xs leading-snug text-danger">{error}</p>
    );
  const entries =
    source === 'draft'
      ? row.draft.inclusions
      : row.saved === null
        ? []
        : draftOf(row.saved).inclusions;
  if (entries.length === 0) return note;
  function wordingOf(inclusionId: number, quantity: number): string | null {
    const fromMaster = master.find((candidate) => candidate.id === inclusionId);
    if (fromMaster) return renderInclusion(fromMaster, quantity);
    const fromLine = (row.saved?.inclusions ?? []).find((entry) => entry.id === inclusionId);
    return fromLine ? renderInclusion(fromLine, quantity) : null;
  }
  const named = entries
    .map((entry) => ({
      id: entry.inclusionId,
      text: wordingOf(entry.inclusionId, Number(entry.quantity || '1')),
    }))
    .filter((entry): entry is { id: number; text: string } => entry.text !== null);
  if (named.length === 0) return note;
  return (
    <>
      {variant === 'count' ? (
        <p
          className="mt-1 text-2xs text-fg-muted"
          title={named.map((entry) => entry.text).join(' · ')}
        >
          {named.length === 1 ? '1 inclusion' : `${named.length} inclusions`}
        </p>
      ) : (
        <ul className="mt-1 flex flex-wrap gap-1">
          {named.map((entry) => (
            <li
              key={entry.id}
              className="rounded-sm border border-border bg-surface-sunken px-1.5 py-0.5 text-2xs text-fg-muted"
            >
              {entry.text}
            </li>
          ))}
        </ul>
      )}
      {note}
    </>
  );
}

/**
 * What stands where the editor would be on a line whose product does not offer
 * inclusions.
 *
 * A non-qualifying line that HOLDS inclusions shows them rather than nothing:
 * the owner chose *uneditable*, not *invisible*, and they are carried onto the
 * document the deal converts into.
 */
function InclusionsNotOffered({
  row,
  master,
  denialReason,
  orphaned,
}: {
  row: Row;
  master: CampaignInclusion[];
  denialReason: string | null;
  orphaned: boolean;
}) {
  if (!orphaned && denialReason === null) return null;
  if (!orphaned) {
    return (
      <div className="grid gap-1">
        <p className="text-xs font-medium text-fg-secondary">Campaign inclusions</p>
        <p className="text-xs text-fg-muted">{denialReason}</p>
      </div>
    );
  }
  const savedEntries = row.saved === null ? [] : draftOf(row.saved).inclusions;
  const preserved = savedEntries.length > 0;
  const draftDiverged =
    preserved && inclusionsKey(row.draft.inclusions) !== inclusionsKey(savedEntries);
  return (
    <div className="grid gap-1">
      <p className="text-xs font-medium text-fg-secondary">
        Campaign inclusions{' '}
        <span className="font-normal text-fg-muted">— on this line, and read-only</span>
      </p>
      <InclusionSummary row={row} master={master} source={preserved ? 'saved' : 'draft'} />
      <p className="text-2xs text-fg-muted">
        {denialReason !== null && `${denialReason} `}
        {preserved
          ? 'Saving leaves them untouched.'
          : 'Not saved — this item carries no inclusions. Switch back to an item that offers them.'}
      </p>
      {draftDiverged && (
        <p className="text-2xs text-fg-muted">
          Changes made after switching item won’t be saved.
        </p>
      )}
    </div>
  );
}

/** The outcome of one save run. Partial success is an ordinary outcome here. */
interface RunSummary {
  saved: number;
  failed: number;
  halted: boolean;
}

/* ── The grid ─────────────────────────────────────────────────────────────── */

export function DealLineItemsTable(props: DealLineItemsTableProps) {
  const {
    cities,
    currency,
    canEdit,
    canDelete,
    deleteDenialReason = DEFAULT_DELETE_DENIAL,
    contextNote,
  } = props;

  /*
   * `mode` is deliberately NOT destructured above: the union narrows on
   * `props.mode`, and destructuring it would sever that link and make every
   * mode-only prop optional at every call site inside.
   */
  const draftMode = props.mode === 'draft';
  /**
   * `null` in draft mode, and it is read as the "issue no request" switch
   * throughout rather than as a missing value — there is no deal to address, so
   * there is no id, and every request path guards on it.
   */
  const dealId = props.mode === 'draft' ? null : props.dealId;
  const onLinesChanged = props.mode === 'draft' ? undefined : props.onLinesChanged;
  const onUnsavedChange = props.mode === 'draft' ? undefined : props.onUnsavedChange;
  const registerSave = props.mode === 'draft' ? undefined : props.registerSave;
  const onDraftLinesChange = props.mode === 'draft' ? props.onDraftLinesChange : undefined;
  const onDraftBlockedChange = props.mode === 'draft' ? props.onDraftBlockedChange : undefined;
  /*
   * ⚠️ **READ ONCE, IN THE `useState` INITIALISER BELOW, AND NOWHERE ELSE.** It
   * is deliberately NOT in any dependency array: an effect that re-ran would put
   * back rows the person had deleted, and a parent building the array inline —
   * which is what the modal does — would re-seed on every keystroke in its own
   * header fields.
   */
  const initialDraftRows = props.mode === 'draft' ? props.initialDraftRows : undefined;

  /*
   * ⚠️ **SEEDED IN THE INITIALISER, NEVER IN AN EFFECT.** A lazy initialiser
   * runs exactly once per mount, which is the only place a seed can be applied
   * without ever fighting the person using the grid. The saved-mode load effect
   * returns early while `dealId` is null, so nothing downstream can overwrite
   * these rows.
   */
  const [rows, setRows] = useState<Row[]>(() => {
    const seeded = (initialDraftRows ?? []).map(seededRowOf);
    // A new deal opens with one blank row to fill in (owner, 2026-10-12).
    return draftMode && seeded.length === 0 ? [blankRowOf(DEFAULT_BLANK_ROW_KEY)] : seeded;
  });
  /**
   * What the seeded products are CALLED, captured at mount beside the rows.
   *
   * ⚠️ **`useProducts()` fetches `active: true`, so a product retired since the
   * plan was written is NOT in the option list** — and a seeded row is the first
   * draft row that can hold a `productId` its owner never picked from that list.
   * Without this the Item cell renders BLANK over a real id: Create stays
   * enabled, nothing is refused here, and the line is POSTed naming a product
   * the person was never shown. {@link productOptionsFor} already keeps exactly
   * this promise for a SAVED row, and this is that same rule extended to the
   * population that can now reach it.
   *
   * The name costs no lookup: `PlannedProductLine` carries it.
   *
   * ⚠️ **One-shot, like the rows.** No dependency array, because the parent maps
   * the seed inline and a memo over it would rebuild on every keystroke.
   */
  const [seededProducts] = useState<ReadonlyMap<number, ProductRow>>(
    () =>
      new Map(
        (initialDraftRows ?? []).map((line) => [
          line.productId,
          {
            id: line.productId,
            productName: line.productName,
            productCode: line.productCode,
            /*
             * ⚠️ `null` — a PLAN CARRIES NO PRICE, and this must never be read
             * as one. It reaches the option list only, never a payload and never
             * {@link previewInputOf}'s `inheritedPrice`, which resolves off
             * `products.rows`. A figure invented here would preview a total the
             * catalogue is going to contradict.
             */
            unitPrice: null,
            /*
             * ⚠️ `null` for the same reason, and it is inert rather than merely
             * empty: **the seeder never reads this map.** A recommendation is
             * resolved off `products.rows` through `recommendedDiscounts`, where
             * a product absent from the catalogue is UNKNOWN and is asked again
             * when the read lands. Were this map consulted, a plan carrying no
             * recommendation would answer "recommends nothing" — a decision,
             * final, and wrong — for every seeded line.
             */
            recommendedDiscountAmount: null,
            // Inert for the same reason: units resolve off `products.rows` only.
            units: {},
          },
        ]),
      ),
  );
  /**
   * What the seeded PROJECTS are called, captured at mount beside the rows.
   *
   * ⚠️ **THE EXACT COUNTERPART OF {@link seededProducts}, AND FOR A WIDER
   * FAILURE.** A `Combobox` falls back to its placeholder when `value` is absent
   * from `options`, and this picker's list is narrower than the catalogue's in
   * four ways at once: ONE page of 100 projects, ACTIVE only, filtered to the
   * row's product, then **split by cycle scope and opened on `current`**. So a
   * seeded project is missing from what the row can see whenever it sits on the
   * `previous` pill, or has been retired, or the fetch has simply not landed —
   * which is true on every first paint. Without a name to pin, the control
   * renders BLANK over a real `draft.projectId` that {@link createPayload} still
   * sends. **The screen would say no project and the wire would say one**, which
   * is the whole failure the seed exists to prevent, arriving one layer down.
   *
   * ⚠️ **One-shot, like the rows and like {@link seededProducts}.**
   */
  const [seededProjects] = useState<ReadonlyMap<number, string>>(
    () =>
      new Map(
        (initialDraftRows ?? [])
          .map((line) => line.project)
          .filter((project) => project !== undefined)
          .map((project) => [project.id, project.name]),
      ),
  );
  /*
   * Nothing is fetched in draft mode, so there is nothing to wait for — starting
   * `true` would render the skeleton for ever over a grid that is already ready.
   */
  const [loading, setLoading] = useState(!draftMode);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState<RunSummary | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Row | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [newRowSeq, setNewRowSeq] = useState(0);

  const products = useProducts();
  const inclusions = useCampaignInclusions();
  const projects = useActiveProjects();
  /*
   * Which cycles sit under Current and which under Previous. ONE read for the
   * whole grid -- the pill is per row, the /cycles fetch behind it is not, and
   * a hook per row is a request per row against a shared rate limit.
   *
   * A null member means the cycles could not be read and EVERY project stays on
   * offer under that pill -- see useCycleScopeIds. Never an empty set.
   */
  const cycleScopeIds = useCycleScopeIds();
  /*
   * One read of the `/regions` master for the whole grid, not one per row — the
   * same reason `useActiveProjects` is hoisted here. Six rows exist today and
   * every line picker offers the same list.
   *
   * ⚠️ It is still needed on a grid whose region pickers are narrowed to a
   * project: it supplies the projectless rows' whole option list, and it is what
   * makes "retired in the master" separable from "not on this project" in
   * `regionOptionsFor`.
   */
  const regions = useReferenceOptions('regions');

  const load = useCallback(
    (signal: AbortSignal) => {
      /*
       * ⚠️ Draft mode reads NOTHING. There is no deal and therefore no basket to
       * load; the rows are whatever the person has typed and nothing else.
       */
      if (dealId === null) return Promise.resolve();
      return api
        .listDealProducts(dealId, signal)
        .then((result) => {
          if (signal.aborted) return;
          setRows(result.data.map(rowOf));
          setLoadError(null);
        })
        .catch((error: unknown) => {
          if (signal.aborted) return;
          setRows([]);
          setLoadError(describeFailure(error).message);
        })
        .finally(() => {
          if (!signal.aborted) setLoading(false);
        });
    },
    [dealId],
  );

  useEffect(() => {
    if (dealId === null) return;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, reloadToken, dealId]);

  /*
   * The name maps below all read the reference list FIRST and the saved lines
   * SECOND, so a product, project or city the active list no longer offers is
   * still nameable from the record that holds it.
   */
  const productNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const row of products.rows) map.set(row.id, row.productName);
    for (const row of rows) {
      if (row.saved) map.set(row.saved.product.id, row.saved.product.productName);
    }
    return map;
  }, [products.rows, rows]);

  const cityNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const city of cities) map.set(city.id, city.name);
    for (const project of projects.rows) {
      for (const city of project.cities) map.set(city.id, city.name);
    }
    for (const row of rows) {
      for (const city of row.saved?.cities ?? []) map.set(city.id, city.name);
    }
    return map;
  }, [cities, projects.rows, rows]);

  const projectNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const project of projects.rows) map.set(project.id, project.name);
    for (const row of rows) {
      const saved = row.saved?.project;
      if (saved) map.set(saved.id, saved.name);
    }
    return map;
  }, [projects.rows, rows]);

  /**
   * Every region name this table can honestly resolve.
   *
   * ⚠️ **On a PROJECTLESS line this is the only geography the document a deal
   * becomes will state** — `geographySegment` in `@/lib/line-description` is
   * cities-else-regions, and convert copies the line's regions across.
   *
   * The saved row goes in LAST: `useReferenceOptions('regions')` queries
   * `isActive: true`, so a line sitting on a RETIRED region resolves through
   * nothing else, while the read that loaded the line names it perfectly.
   */
  const regionNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const record of regions.records) map.set(record.id, record.name);
    for (const project of projects.rows) {
      for (const region of project.regions) map.set(region.id, region.name);
    }
    for (const row of rows) {
      for (const region of row.saved?.regions ?? []) map.set(region.id, region.name);
    }
    return map;
  }, [regions.records, projects.rows, rows]);

  /**
   * Every campaign inclusion this table can name and give a unit for.
   *
   * ⚠️ **Read only as input to the pinned composer.** Convert copies the line's
   * inclusions across, and BOTH document builders now compose the bracketed
   * segment, so this is what the customer will read either way.
   *
   * Saved entries go in AFTER the master's list: the master fetches
   * `?isActive=true`, so an inclusion RETIRED since the line was written
   * resolves through nothing else, and it still prints on the document.
   */
  const inclusionMasters = useMemo(() => {
    const map = new Map<number, Pick<LineInclusion, 'name' | 'unitSingular' | 'unitPlural'>>();
    for (const entry of inclusions.rows) map.set(entry.id, entry);
    for (const row of rows) {
      for (const entry of row.saved?.inclusions ?? []) map.set(entry.id, entry);
    }
    return map;
  }, [inclusions.rows, rows]);

  /*
   * ⚠️ The item CODE is a join key in its own right: it is the guard on the
   * project-name substitution in `composeLineDescription`, so a missing code
   * heads the sentence with the product name where the composer heads it with
   * the project's. See `hasXeroItemCode` in `@/lib/line-description`.
   */
  const productCodes = useMemo(() => {
    const map = new Map<number, string | null>();
    for (const row of products.rows) map.set(row.id, row.productCode);
    for (const row of rows) {
      if (row.saved) map.set(row.saved.product.id, row.saved.product.productCode);
    }
    return map;
  }, [products.rows, rows]);

  const rowProjects = useMemo(() => {
    const map = new Map<string, RowProject>();
    for (const row of rows) {
      const productId = row.draft.productId === '' ? null : Number(row.draft.productId);
      /*
       * ⚠️ **The test is the project's whole product SET, via
       * `projectSellsProduct`** — migration 156, and the singular key it
       * replaced was only the primary. Matching the primary excluded a project
       * selling this product as a SECONDARY, which is exactly the case 156 was
       * built for: a travel line under a festival project. The exclusion was
       * silent — the project was simply absent from the list, reading as one
       * nobody had set up. One helper for all three grids; three copies of this
       * predicate drift and nothing on screen shows it.
       */
      const forProduct =
        productId === null
          ? projects.rows
          : projects.rows.filter((project) => projectSellsProduct(project, productId));
      const toProjectOption = (project: (typeof forProduct)[number]): ComboboxOption => {
        const period = project.cycle
          ? project.intake
            ? `${project.cycle.name} · ${project.intake.name}`
            : project.cycle.name
          : null;
        // ⚠️ The PRIMARY is the right read here, and `products[0]` IS the
        // primary — first by the read's own `ORDER BY`, an API guarantee rather
        // than an incidental ordering. The hint names what picking this project
        // will put in the item box, and that is `productOfProject`, which seeds
        // the primary.
        const productName = productId === null ? (project.products[0]?.name ?? null) : null;
        const hint = [productName, period].filter((part) => part !== null).join(' · ');
        return {
          value: String(project.id),
          label: project.name,
          ...(hint !== '' ? { hint } : {}),
        };
      };
      const savedProject = row.saved?.project ?? null;
      /*
       * ✅ **THE SAME PIN FOR A SEEDED ROW, which has no `saved` to pin from.**
       * {@link productOptionsFor} carries this branch verbatim for the Item
       * cell; this is it for the project cell, and the reason is stronger here
       * because this list is narrowed FOUR ways (see {@link seededProjects})
       * where the catalogue's is narrowed one.
       *
       * ⚠️ **Keyed on the row's CURRENT draft project, never on the seed.**
       * Changing the item clears `projectId` — `patchRow` does it so refusal 5
       * cannot be built out of two choices that each looked right — and a pin
       * keyed on the seed would go on offering a project the row no longer
       * holds, under a product that no longer sells it.
       *
       * `projectOptionsByScope` unshifts only into a scope that does not already
       * list it, so passing this when the project IS on offer changes nothing
       * and shows no label.
       */
      const seededProjectName =
        row.draft.projectId === ''
          ? undefined
          : seededProjects.get(Number(row.draft.projectId));
      const pinnedProject =
        savedProject !== null
          ? {
              value: String(savedProject.id),
              label: savedProject.name,
              hint: 'no longer offered',
            }
          : seededProjectName !== undefined
            ? {
                value: row.draft.projectId,
                label: seededProjectName,
                hint: 'no longer offered',
              }
            : null;
      /*
       * Current / Previous, owner request 2026-09-11 — Current (which means
       * current AND upcoming) is the default and reproduces the 2026-09-09
       * open-cycles filter exactly. Previous is what makes the closed-cycle
       * projects that filter hid reachable again.
       *
       * SPLIT HERE AND NOT INTO forProduct, WHICH IS THE WHOLE POINT.
       * `selected` below still resolves against the unfiltered list, so a line
       * already on a closed cycle keeps its project, its cities and its
       * regions -- and `savedProject` is pinned into BOTH pills by
       * projectOptionsByScope, so the control can always render the value the
       * row holds. Filter forProduct instead and 113 historical lines lose all
       * of it.
       */
      const { options, counts: scopeCounts } = projectOptionsByScope(
        forProduct,
        cycleScopeIds,
        toProjectOption,
        pinnedProject,
      );
      const projectFootnotes: Record<ProjectCycleScope, string | undefined> = {
        current: projectScopeFootnote('current', scopeCounts),
        previous: projectScopeFootnote('previous', scopeCounts),
      };
      const selected =
        row.draft.projectId === ''
          ? null
          : (forProduct.find((project) => String(project.id) === row.draft.projectId) ?? null);
      const resolvable = !projects.loading && !projects.failed && !projects.truncated;
      const declaredCityIds =
        selected !== null
          ? new Set(selected.cities.map((city) => city.id))
          : // No project chosen means no cities are declared — a definite answer,
            // but only once the list has settled. Otherwise `null`: cannot say.
            row.draft.projectId === '' && resolvable
            ? new Set<number>()
            : null;
      const cityOptions: MultiComboboxOption[] = (selected?.cities ?? []).map((city) => ({
        value: String(city.id),
        label: cityLabel(city),
      }));
      for (const city of row.saved?.cities ?? []) {
        if (!row.draft.cityIds.includes(city.id)) continue;
        if (cityOptions.some((option) => option.value === String(city.id))) continue;
        cityOptions.push({
          value: String(city.id),
          label: cityLabel(city),
          hint: 'no longer on this project',
        });
      }
      /*
       * The project's OWN region set, which is what the region picker offers on
       * a project-backed row — see {@link RowProject.projectRegions}. `null`
       * when the project could not be resolved at all, which is a different fact
       * from a project that declares none.
       */
      const projectRegions =
        selected === null
          ? null
          : selected.regions.map((region) => ({ id: region.id, name: region.name }));
      /*
       * ⚠️ **Three conditions, and all three are load-bearing.** A project must
       * be CHOSEN (an empty box says nothing about a project nobody has picked),
       * RESOLVED (`selected !== null`, so a truncated or failed list cannot be
       * read as "declares nothing"), and its own list empty. Anything short of
       * that is `false`, because this opens a control and the gate above it
       * fails towards hiding.
       */
      const projectHasNoCities =
        row.draft.projectId !== '' && selected !== null && selected.cities.length === 0;
      const citiesUnavailable = row.draft.projectId === '' || cityOptions.length === 0;
      const cityHint =
        row.draft.projectId === ''
          ? // Said plainly rather than as a fault. Most lines name no cities.
            'Pick a project first.'
          : cityOptions.length === 0
            ? selected === null
              ? 'This project’s cities are not listed here.'
              : 'Add cities on the project.'
            : undefined;
      const settled = !projects.loading && !projects.failed;
      const projectHint =
        productId === null
          ? undefined
          : settled && forProduct.length === 0
            ? projects.truncated
              ? undefined
              : 'No active project for this item.'
            : undefined;
      /*
       * ⚠️ **The server's own predicate, reproduced — and it FAILS TOWARDS
       * SHOWING** (see {@link RowProject.showProject}). `forProduct` is exactly
       * `GET /projects?productId=N&isActive=true`, which is exactly the guard's
       * `product_id = ? AND is_active = 1`, so the only case that HIDES is a
       * settled, complete list naming no active project for this row's product.
       * `!settled` covers loading and a failed read; `truncated` covers a 101st
       * project we have not seen; and a line that already names a project keeps
       * its block or that project becomes invisible and unremovable.
       *
       * 🔴 **A ROW WITH NO PRODUCT NOW HIDES IT — owner's decision, 2026-09-19.**
       * ⚠️ *SUPERSEDED: "and a row with no product keeps it because choosing a
       * project there SETS the item."*
       *
       * ⛔ **THE ACCEPTED COST, RECORDED AS A DECISION AND NOT AS A TIDY-UP:
       * THAT PROJECT-FIRST PATH IS GONE.** A line could be resolved from EITHER
       * end; there is now one direction, item first. Nothing errors and nothing
       * is refused — the second way in simply is not offered, so somebody who
       * worked project-first meets a row that looks like it has lost a control
       * rather than one waiting for an item. **Do not "restore" it as a bug
       * fix**: it was put to the owner with this cost named and taken anyway.
       *
       * ⚠️ **The two UNRESOLVED terms are untouched and must stay that way.**
       * `!settled` and `truncated` are not evidence a product has no projects,
       * and the line whose product IS sold through projects and names none is
       * refused at write time with nothing on screen to satisfy it. Only the
       * "no product yet" term moved; the definite-no term below is unchanged.
       *
       * ⚠️ `holdsProject` stays FIRST, so a row that already names a project is
       * never hidden by the new term — the change reaches only rows where
       * nothing has been chosen at either end.
       */
      const holdsProject = row.draft.projectId !== '' || (row.saved?.project ?? null) !== null;
      const showProject = holdsProject
        ? true
        : productId === null
          ? false
          : !settled || projects.truncated || forProduct.length > 0;
      map.set(row.key, {
        options,
        projectFootnotes,
        cityOptions,
        declaredCityIds,
        projectRegions,
        projectHasNoCities,
        projectHint,
        cityHint,
        citiesUnavailable,
        showProject,
      });
    }
    return map;
  }, [
    rows,
    projects.rows,
    projects.loading,
    projects.failed,
    projects.truncated,
    /*
     * Never changes after mount — it is `useState` with no setter — so this
     * costs nothing at runtime and is declared because the memo READS it.
     * Leaving a read out of a dep array is how a memo goes on serving an answer
     * computed from a value it can no longer see.
     */
    seededProjects,
    /*
     * cycleScopeIds ARRIVES LATE, AND LEAVING IT OUT MAKES THE SPLIT INERT.
     * useCycleScopeIds answers UNKNOWN_CYCLE_SCOPES while /cycles is in flight
     * and a real index once it lands, so a memo that does not depend on it
     * computes the UNFILTERED list on first paint and never recomputes -- both
     * pills then show everything, with nothing failing and nothing on screen to
     * show it. Caught by lint, not by review, the last time this changed.
     */
    cycleScopeIds,
  ]);

  const retiredInclusionIds = useMemo(() => {
    const active = new Set(inclusions.rows.map((row) => row.id));
    const retired = new Set<number>();
    for (const row of rows) {
      for (const entry of row.saved?.inclusions ?? []) {
        if (!active.has(entry.id)) retired.add(entry.id);
      }
    }
    return retired;
  }, [inclusions.rows, rows]);

  // A stable identity for the set of chosen products, so the offers effect does
  // not re-run on every keystroke elsewhere in the grid.
  const productIdKey = rows.map((row) => row.draft.productId).join(',');
  const inclusionProductIds = useMemo(() => {
    const ids = new Set<number>();
    for (const id of productIdKey.split(',')) {
      if (id !== '') ids.add(Number(id));
    }
    return [...ids].sort((a, b) => a - b);
  }, [productIdKey]);
  const inclusionOffers = useProductInclusionOffers(inclusionProductIds);

  const inclusionsByKey = useMemo(() => {
    const map = new Map<string, RowInclusions>();
    for (const row of rows) {
      const offer = inclusionsOfferOf(row, inclusionOffers);
      map.set(row.key, {
        offers: productOffersInclusions(offer),
        offerUnread: offer === null,
        offeredIds: offeredInclusionIds(offer),
        denialReason: inclusionsDenialReason(offer),
        orphaned: hasOrphanedInclusions(offer, row.draft.inclusions.length),
      });
    }
    return map;
  }, [rows, inclusionOffers]);

  const inclusionsFor = useCallback(
    (key: string): RowInclusions => inclusionsByKey.get(key) ?? INCLUSIONS_SHOWN,
    [inclusionsByKey],
  );

  /* ── What a line starts with ────────────────────────────────────────────── */

  /**
   * Each read product's recommended discount.
   *
   * ⚠️ **Two kinds of miss, and they are not the same fact** — the same pair
   * {@link catalogPrices} draws below. A stored `null` is "this product
   * recommends nothing"; `undefined` is "nothing has read this product", because
   * the list is capped at 100 rows and can fail outright. `recommendedDiscountSeed`
   * is only ever handed the first of those.
   */
  const recommendedDiscounts = useMemo(() => {
    const map = new Map<number, string | null>();
    for (const product of products.rows) map.set(product.id, product.recommendedDiscountAmount);
    return map;
  }, [products.rows]);

  /**
   * Each read product's `quantityStep` / `unitLabel`. A miss is an unread
   * product, which `@/lib/product-units` reads as "no step, no unit" — so a
   * refusal is only ever said first, never invented; the server stays the
   * boundary.
   */
  const productById = useMemo(() => {
    const map = new Map<number, ProductRow>();
    for (const product of products.rows) map.set(product.id, product);
    return map;
  }, [products.rows]);

  const unitsOfProduct = useCallback(
    (productId: string): ProductUnitFields | undefined =>
      productId === '' ? undefined : productById.get(Number(productId))?.units,
    [productById],
  );

  const unitsFor = useCallback(
    (row: Row): ProductUnitFields | undefined => unitsOfProduct(row.draft.productId),
    [unitsOfProduct],
  );

  /**
   * The `(product, quantity)` pairs worth resolving a preset for.
   *
   * ⚠️ **Three gates, and each one is a request NOT sent against a shared
   * budget**: the row must be one the catalogue may still seed into, its
   * inclusions block must be on screen, and its quantity must be askable. The
   * second is the load-bearing one — resolving a preset for a row whose editor
   * is hidden would fetch a set this grid is structurally forbidden from
   * writing.
   *
   * ⚠️ *SUPERSEDED: "a shared 120/60s budget."* It is **12,000/60s** (corrected
   * 2026-09-17). ⛔ **Read that as confirming which gate matters rather than as
   * licence to relax any of them**: the paragraph already says the second is
   * the load-bearing one, and its reason is CORRECTNESS — a set this grid may
   * not write — never the ceiling. The arithmetic was only ever the framing.
   */
  const presetRequestKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const row of rows) {
      if (row.draft.productId === '') continue;
      if (!seedsFromCatalogue(row)) continue;
      if (!inclusionsFor(row.key).offers) continue;
      const quantity = presetQuantityOf(row.draft);
      if (quantity === null) continue;
      keys.add(presetCacheKey(Number(row.draft.productId), quantity, row.draft.projectId));
    }
    // Sorted so the joined key inside the hook is stable across re-orders.
    return [...keys].sort();
  }, [rows, inclusionsFor]);

  const inclusionPresets = useInclusionPresets(presetRequestKeys);

  /**
   * What this grid last seeded onto each row, so a re-apply can tell its own
   * figure from somebody's.
   *
   * ⚠️ **REFS, not state, and that is deliberate.** They are bookkeeping rather
   * than anything rendered: putting them in state would re-render the whole grid
   * for a value no cell reads, and — worse — would make the seeding effect
   * depend on something it writes, which is the loop. The effect below already
   * re-runs when `rows` moves, which is the only time these are consulted.
   */
  const seededDiscounts = useRef<Map<string, string>>(new Map());
  const seededInclusions = useRef<Map<string, LineInclusionDraft[]>>(new Map());
  const seededDescriptions = useRef<Map<string, string>>(new Map());

  /*
   * Seed the recommended discount and the inclusion preset into the DRAFT.
   *
   * ⛔ **EVERY "should this change" ANSWER COMES FROM `@/lib/line-presets`.** Both
   * functions return `null` for "change nothing", and a `null` is honoured
   * without a second opinion: the module is where the owner's *re-apply only if
   * untouched* rule lives, and re-deriving any part of it here would put a second
   * answer beside it that drifts on the first correction.
   *
   * ⚠️ **A seeded AMOUNT puts the row in amount mode**, because `discountAmount`
   * IS the mode switch — see {@link Draft.discountAmount}. That is why
   * `recommendedDiscountSeed` refuses a row carrying a real percentage: the two
   * columns are mutually exclusive and both non-zero is a 422. The percentage box
   * is left exactly as it was; {@link discountWriteOf} resolves which half the
   * wire carries.
   *
   * ⚠️ **It CONVERGES rather than looping.** Once a patch lands, the draft holds
   * what was seeded and the refs agree with it, so the next pass gets `null` from
   * both functions and stops. React's development double-invoke costs one extra
   * pass and no extra request.
   */
  useEffect(() => {
    const patches = new Map<string, Partial<Draft>>();
    for (const row of rows) {
      if (row.draft.productId === '') continue;
      if (!seedsFromCatalogue(row)) continue;
      const productId = Number(row.draft.productId);
      const patch: Partial<Draft> = {};

      /*
       * `undefined` is an unread product, not a product recommending nothing —
       * so nothing is asked and nothing is seeded until the list lands.
       */
      const recommendedRaw = recommendedDiscounts.get(productId);
      const product = productById.get(productId);
      const perUnit = soldPerUnit(product?.units);
      /*
       * ⚠️ **A per-unit product recommends a PER-UNIT discount**, and a line's
       * `discountAmount` comes off the WHOLE line — so it is scaled by the
       * quantity first (`perUnitDiscountTotal`). Because the seed is re-asked on
       * every pass, an untouched figure follows the quantity; a typed one stays.
       * An unaskable quantity seeds nothing rather than a guess.
       */
      let recommended = recommendedRaw;
      let seededDiscountAmount = seededDiscounts.current.get(row.key) ?? null;
      if (perUnit && recommendedRaw !== undefined && recommendedRaw !== null) {
        const seedQuantity = presetQuantityOf(row.draft);
        recommended =
          seedQuantity === null ? undefined : perUnitDiscountTotal(recommendedRaw, String(seedQuantity));
        /*
         * A STORED line whose discount is exactly the scaled recommendation at
         * its stored quantity is still following it, so it rescales too. Any
         * other stored figure is somebody's and is left alone.
         */
        if (
          seededDiscountAmount === null &&
          row.saved !== null &&
          row.saved.discountAmount !== null &&
          row.saved.discountAmount === perUnitDiscountTotal(recommendedRaw, String(row.saved.quantity))
        ) {
          seededDiscountAmount = row.saved.discountAmount;
        }
      }
      if (recommended !== undefined) {
        const nextAmount = recommendedDiscountSeed(recommended, {
          discountAmount: row.draft.discountAmount,
          discount: row.draft.discount,
          seededDiscountAmount,
        });
        if (nextAmount !== null) {
          patch.discountAmount = nextAmount;
          seededDiscounts.current.set(row.key, nextAmount);
        }
      }

      /*
       * ⛔ **The hidden-block gate, again.** `presetRequestKeys` already declines
       * to ask, so `resolved` would be absent anyway — but this is the test both
       * payload builders run, and a set reaching a hidden editor is a whole-set
       * REPLACE nobody can see. It is written out rather than left true by
       * accident one refactor away from the cache being warmed elsewhere.
       */
      const quantity = presetQuantityOf(row.draft);
      if (inclusionsFor(row.key).offers && quantity !== null) {
        const resolved =
          inclusionPresets.get(presetCacheKey(productId, quantity, row.draft.projectId)) ?? null;
        const nextInclusions = inclusionPresetSeed(
          resolved,
          lineInclusionDraftsOf(row.draft.inclusions),
          seededInclusions.current.get(row.key) ?? null,
        );
        if (nextInclusions !== null) {
          patch.inclusions = inclusionDraftsOf(nextInclusions);
          seededInclusions.current.set(row.key, nextInclusions);
        }
      }

      /*
       * ⚠️ **The per-unit auto description** ("AI Visa Tutor: 50 licenses …"),
       * composed by `@/lib/product-units` off the row AS PATCHED above, so it
       * names the seeded discount. Written only while the description is still
       * DERIVED (`null`) or still exactly the auto text — the one this grid last
       * wrote, or, on a stored line, the one its stored figures compose. Wording
       * somebody typed is theirs and is never replaced.
       */
      if (product !== undefined) {
        const next = { ...row.draft, ...patch };
        const current = next.description;
        const lastAuto = seededDescriptions.current.get(row.key) ?? null;
        const savedArgs =
          row.saved === null
            ? null
            : autoDescriptionArgsOf(draftOf(row.saved), productById.get(row.saved.product.id), currency);
        const followsAuto =
          current === null ||
          (lastAuto !== null && current === lastAuto) ||
          (savedArgs !== null && isAutoText(current, savedArgs));
        const args = autoDescriptionArgsOf(next, product, currency);
        if (args !== null) {
          const text = autoTextOf(args);
          if (followsAuto && current !== text) {
            patch.description = text;
            seededDescriptions.current.set(row.key, text);
          }
        } else if (lastAuto !== null && current === lastAuto) {
          // The row left the per-unit product: its auto text goes with it.
          patch.description = null;
          seededDescriptions.current.delete(row.key);
        }
      }

      if (Object.keys(patch).length > 0) patches.set(row.key, patch);
    }

    // Rows come and go; their bookkeeping goes with them, or a recycled key
    // would inherit another line's idea of what it had seeded.
    const live = new Set(rows.map((row) => row.key));
    for (const key of [...seededDiscounts.current.keys()]) {
      if (!live.has(key)) seededDiscounts.current.delete(key);
    }
    for (const key of [...seededInclusions.current.keys()]) {
      if (!live.has(key)) seededInclusions.current.delete(key);
    }
    for (const key of [...seededDescriptions.current.keys()]) {
      if (!live.has(key)) seededDescriptions.current.delete(key);
    }

    if (patches.size === 0) return;
    /*
     * ⚠️ `setRows` directly rather than `patchRow`: this is not an edit somebody
     * made. `patchRow` clears the save summary and resets a row's error state,
     * which would wipe a failure report the person is still reading the moment a
     * product list landed.
     *
     * ⚠️ **The rule's own exemption is what this is** — synchronising React with
     * an EXTERNAL system. Neither input is available during the render that
     * created the row: the product list and the resolve both arrive later, so
     * there is nothing to derive during render and no event to fold this into.
     * The house convention for exactly this case is the disable plus the reason
     * (`AuthProvider`, `DealDetailPage`, `use-table-filters`).
     *
     * It cannot cascade: a landed patch makes both `@/lib/line-presets`
     * functions answer `null` on the next pass, which returns above without
     * calling this.
     */
    // oxlint-disable-next-line react/set-state-in-effect
    setRows((current) =>
      current.map((row) => {
        const patch = patches.get(row.key);
        return patch === undefined ? row : { ...row, draft: { ...row.draft, ...patch } };
      }),
    );
  }, [rows, recommendedDiscounts, productById, currency, inclusionPresets, inclusionsFor]);

  const projectsFor = useCallback(
    (key: string): RowProject => rowProjects.get(key) ?? ROW_PROJECT_UNRESOLVED,
    [rowProjects],
  );

  /**
   * Whether this row's regions block is on screen — and therefore whether a save
   * may carry `regionIds` at all.
   *
   * ⚠️ **ONE answer, read by the render AND by both payload builders**, the same
   * arrangement `projectsFor` and `inclusionsFor` have and for the same reason. A
   * second test here would be a second answer, and the two would diverge on
   * exactly the row where it mattered: a hidden control emitting a full REPLACE.
   */
  const showsRegions = useCallback(
    (row: Row): boolean =>
      showsRegionsControl(
        row,
        projectsFor(row.key).showProject,
        /*
         * ⚠️ Both come from the SAME resolved row, and they default in opposite
         * directions — `showProject` is `true` when nothing is known, this is
         * `false`. `ROW_PROJECT_UNRESOLVED` is where that pair is set.
         */
        projectsFor(row.key).projectHasNoCities,
      ),
    [projectsFor],
  );

  const duplicatePairs = useMemo(() => duplicatePairsOf(rows), [rows]);

  /**
   * Each read product's own `unitPrice`.
   *
   * ⚠️ **A `Map` lookup misses in two ways and the caller must tell them
   * apart**: a stored `null` is "this product records no price", `undefined` is
   * "nothing has read this product" — the list is capped at 100 rows and can
   * fail outright.
   */
  const catalogPrices = useMemo(() => {
    const map = new Map<number, string | null>();
    for (const product of products.rows) map.set(product.id, product.unitPrice);
    return map;
  }, [products.rows]);

  /*
   * ⚠️ **A row with no item prices to `undefined`, not `null`** ({@link
   * computeFor}). `null` is the claim "this product records no price", and a
   * row that has not chosen a product has made no such claim about anything.
   */

  /** `N` per project for the city pro-rata, off the list the city picker reads. */
  const projectCityCounts = useMemo(() => {
    const map = new Map<number, number>();
    for (const project of projects.rows) map.set(project.id, project.cities.length);
    return map;
  }, [projects.rows]);

  /*
   * Project price entries (owner 2026-10-27) for every project a row names or
   * was saved under — one batch read, cached across the page.
   */
  const pricedProjectIds = useMemo(() => {
    const ids = new Set<number>();
    for (const row of rows) {
      if (row.draft.projectId !== '') ids.add(Number(row.draft.projectId));
      if (row.saved?.project) ids.add(row.saved.project.id);
    }
    return [...ids];
  }, [rows]);
  const prices = useProjectPrices(pricedProjectIds);

  const pricing = useMemo<PricingContext>(
    () => ({
      entriesByProject: prices.entriesByProject,
      pricesFailed: prices.error !== null,
      defaultTierId: prices.defaultTierId,
      currency,
      catalogPrices,
      counts: projectCityCounts,
    }),
    [prices.entriesByProject, prices.error, prices.defaultTierId, currency, catalogPrices, projectCityCounts],
  );

  /**
   * True while an `inherit` on this row means "keep the stored price": a saved
   * line whose price was typed over (or is not the computed price of its saved
   * selection). Such a line is not repriced by a selection change.
   */
  const inheritKeepsStored = useCallback(
    (row: Row): boolean => row.saved !== null && !savedPriceFollows(row.saved, pricing),
    [pricing],
  );

  /**
   * What an `inherit` row will be priced at once saved. A new line, or a saved
   * line whose stored price still follows its selection, takes
   * {@link computeLinePrice} over the DRAFT's product, project, tier, cities and
   * quantity; any other saved line keeps its stored price. `undefined` = a figure
   * the rule reads is unread.
   */
  const inheritedFor = useCallback(
    (row: Row): InheritedPrice | undefined => {
      const { saved, draft } = row;
      if (saved !== null && inheritKeepsStored(row)) {
        return { unitPrice: saved.unitPrice, discountAmount: null, discountFollows: false, source: 'stored' };
      }
      const computed = computeFor(pricing, draftSelectionOf(row));
      if (computed === undefined) return undefined;
      /*
       * The resolver's rounding discount replaces only a discount nobody set: a
       * blank or zero percentage, or a saved amount that is exactly the
       * rounding discount of the saved selection.
       */
      const savedRounding =
        saved === null ? null : (computeFor(pricing, savedSelectionOf(saved))?.discountAmount ?? null);
      const discountFollows =
        (draft.discountAmount === null && isBlankOrZero(draft.discount)) ||
        (saved !== null &&
          saved.discountAmount !== null &&
          draft.discountAmount !== null &&
          savedRounding !== null &&
          sameDecimal(draft.discountAmount, saved.discountAmount) &&
          sameDecimal(saved.discountAmount, savedRounding));
      return {
        unitPrice: computed.unitPrice,
        discountAmount: computed.discountAmount,
        discountFollows,
        source: computed.source,
      };
    },
    [inheritKeepsStored, pricing],
  );

  const previewInputs = useMemo(() => {
    const map = new Map<string, GeneratedLineInput>();
    for (const row of rows) {
      map.set(row.key, previewInputOf(row, inheritedFor(row)));
    }
    return map;
  }, [rows, inheritedFor]);

  const previewByKey = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const row of rows) {
      const input = previewInputs.get(row.key);
      map.set(row.key, input === undefined ? null : lineTotalPreview(input));
    }
    return map;
  }, [rows, previewInputs]);

  /*
   * ⚠️ **THE DISCOUNT-AMOUNT SCREEN STATE IS GONE, AND THAT IS THE POINT.**
   * A `Record<string, string>` of typed amounts lived here, outside {@link
   * Draft}, precisely because the API had no column to put it in — every
   * keystroke was converted into `draft.discount` and the amount itself was
   * never sent. `discount_amount` exists now, so the value is draft state like
   * any other field and the map, its derived `DiscountAmount` resolution, and
   * the conversion under both are all deleted.
   */
  const localProblems = useMemo(() => {
    const map = new Map<string, RowProblems>();
    for (const row of rows) {
      /*
       * A new deal opens with one blank row. Left untouched it is not judged,
       * so it neither blocks the create nor shows "Choose a product" before
       * anybody has started; the draft payload already skips a row with no product.
       */
      if (draftMode && row.saved === null && isUntouchedBlankDraft(row.draft)) {
        map.set(row.key, { fieldErrors: {}, error: null, notice: null });
        continue;
      }
      map.set(
        row.key,
        validateRow(
          row,
          duplicatePairs,
          retiredInclusionIds,
          projectsFor(row.key).declaredCityIds,
          inclusionsFor(row.key).offers,
          unitsFor(row),
        ),
      );
    }
    return map;
  }, [draftMode, rows, duplicatePairs, retiredInclusionIds, projectsFor, inclusionsFor, unitsFor]);

  // The server's own answer wins over ours: it saw the post-merge state.
  const problemsByKey = useMemo(() => {
    const map = new Map<string, RowProblems>();
    for (const row of rows) {
      const local = localProblems.get(row.key) ?? { fieldErrors: {}, error: null, notice: null };
      map.set(row.key, {
        fieldErrors: { ...local.fieldErrors, ...row.fieldErrors },
        error: row.error ?? local.error,
        /*
         * ⚠️ Carried through, never folded into `error`. Both can be true at
         * once, and an `error ?? notice` would drop the notice precisely when
         * the row is under the most scrutiny.
         */
        notice: local.notice,
      });
    }
    return map;
  }, [rows, localProblems]);

  const dirtyKeys = useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) {
      if (
        isDirty(
          row,
          inclusionsFor(row.key).offers,
          projectsFor(row.key).showProject,
          showsRegions(row),
          inheritedFor(row),
        )
      ) {
        set.add(row.key);
      }
    }
    return set;
  }, [rows, inclusionsFor, projectsFor, showsRegions, inheritedFor]);

  const blockedKeys = useMemo(() => {
    const set = new Set<string>();
    for (const key of dirtyKeys) {
      const local = localProblems.get(key);
      if (!local) continue;
      if (Object.keys(local.fieldErrors).length > 0 || local.error !== null) set.add(key);
    }
    return set;
  }, [dirtyKeys, localProblems]);

  /* ── Draft mode: what goes upward ─────────────────────────────────────── */

  /**
   * The rows as POST bodies, in grid order. `null` in saved mode.
   *
   * ⚠️ **Blocked rows are WITHHELD, not emitted with their faults.** The parent
   * creates the deal first and writes these afterwards, so a line the server
   * would refuse becomes a 422 arriving after a real record exists — against a
   * row the person can no longer see, on a screen that has already closed. The
   * count of what is being held back goes up separately, so the parent refuses
   * to submit instead.
   *
   * `sortOrder` is the row's index in the WHOLE grid, gaps included: it is a
   * display order, not a sequence.
   */
  const draftLines = useMemo(() => {
    if (!draftMode) return null;
    const lines: DealProductCreateInput[] = [];
    rows.forEach((row, index) => {
      if (row.draft.productId === '' || blockedKeys.has(row.key)) return;
      // The same builder the saved POST uses. There is no second one — which is
      // also what keeps `unitPrice` OMITTED under `inherit` on this path.
      lines.push(
        createPayload(
          row.draft,
          index,
          inclusionsFor(row.key).offers,
          projectsFor(row.key).showProject,
          showsRegions(row),
          inheritedFor(row),
        ),
      );
    });
    return lines;
  }, [draftMode, rows, blockedKeys, inclusionsFor, projectsFor, showsRegions, inheritedFor]);

  const draftBlockedCount = draftMode ? blockedKeys.size : 0;

  /*
   * The callbacks live in refs so the emission depends on the CONTENT and never
   * on the parent's render. An inline arrow — which is what a modal will pass —
   * has a new identity every render, and in the deps it would re-emit on every
   * keystroke in the deal's own header fields.
   */
  const emitLines = useRef(onDraftLinesChange);
  const emitBlocked = useRef(onDraftBlockedChange);
  useEffect(() => {
    emitLines.current = onDraftLinesChange;
    emitBlocked.current = onDraftBlockedChange;
  });

  useEffect(() => {
    if (draftLines === null) return;
    emitLines.current?.(draftLines);
  }, [draftLines]);

  useEffect(() => {
    if (!draftMode) return;
    emitBlocked.current?.(draftBlockedCount);
  }, [draftMode, draftBlockedCount]);

  /**
   * The basket total, summed from the SERVER's own per-line `lineTotal` values.
   *
   * ⚠️ **There is no server-side basket total on a deal and this is not one.**
   * `docs/api/deals.md` is explicit that no such field exists, so this is a
   * client-side sum for the reader. It is **not** `deal.amount` and must never
   * be written to it: `amount` is a hand-entered figure with its own meaning,
   * and the basket does not maintain it.
   *
   * Summed from the stored per-line figures rather than re-derived from prices,
   * because the database rounds per line and re-deriving gives a different
   * answer.
   */
  const savedTotal = useMemo(
    () => sumDecimalStrings(rows.map((row) => row.saved?.lineTotal ?? null)),
    [rows],
  );

  /**
   * The draft footer total — the sum of the PREVIEWS, and `null` in saved mode.
   *
   * ⚠️ **This is our arithmetic, not the server's, and the footer says so.** A
   * row with no price contributes nothing rather than zero: its preview is
   * `null`, which is "no figure", not "0.00".
   */
  const draftPreviewTotal = useMemo(
    () =>
      draftMode
        ? sumDecimalStrings(rows.map((row) => previewByKey.get(row.key) ?? null))
        : null,
    [draftMode, rows, previewByKey],
  );

  const readOnly = !canEdit;
  const dirtyCount = dirtyKeys.size;

  /*
   * ⚠️ **Placed HERE rather than beside the render, because two early returns
   * sit between this point and the JSX** — loading, and a failed line read. A
   * hook below either would run on some renders and not others, which React
   * refuses; and a grid that stopped reporting the moment it hit a loading
   * state would hand the parent a stale count of unsaved rows and let it close
   * over them.
   *
   * Both are inert in draft mode: neither prop exists there, so `undefined?.()`
   * is the whole of it and no branch is needed.
   */
  const emitUnsaved = useRef(onUnsavedChange);
  useEffect(() => {
    emitUnsaved.current = onUnsavedChange;
  });
  useEffect(() => {
    emitUnsaved.current?.({ dirty: dirtyCount, blocked: blockedKeys.size });
  }, [dirtyCount, blockedKeys.size]);
  /*
   * ⚠️ **REFRESHED ON EVERY RENDER, deliberately, and this is the whole reason
   * the ref exists.** `saveAll` closes over `rows`, `dirtyKeys` and
   * `blockedKeys` as they were at the render that made it. Handing the parent
   * the function itself once would freeze the row set at first mount, so a save
   * pressed from up there would write whatever was on screen when the modal
   * opened — silently, with a 200 per row.
   */
  const latestSaveAll = useRef(saveAll);
  useEffect(() => {
    latestSaveAll.current = saveAll;
  });
  useEffect(() => {
    if (registerSave === undefined) return;
    registerSave(() => latestSaveAll.current());
    /*
     * ⚠️ Cleared on unmount, so a parent that outlives this grid cannot call a
     * save into a component that is gone. The parent must treat `null` as "no
     * grid", never as "nothing to save".
     */
    return () => registerSave(null);
  }, [registerSave]);

  /**
   * This row's derived description — the string the cell shows while
   * `description` is `null`.
   *
   * Every input is gathered here and the composition itself is
   * `composeLineDescription`'s. Nothing in this file joins names together.
   *
   * ## 🔴 THE `inclusions: []` REASON "UNIQUE TO DEALS" NO LONGER HOLDS
   *
   * *SUPERSEDED: "**`inclusions: []`, DELIBERATELY, AND FOR A REASON UNIQUE TO
   * DEALS** … The backend's QUOTE builder composes it; its INVOICE builder does
   * not. A deal is composed by **neither** — its `null` description is composed,
   * if ever, by whichever builder belongs to the document it is CONVERTED into,
   * and this grid cannot know which. Passing the row's inclusions would show a
   * bracketed promise on a deal bound for an invoice … **This is not an omission
   * to 'complete' by copying the quote grid.**"*
   *
   * ⚠️ **The argument was never about deals — it was entirely a claim about the
   * OTHER TWO composers disagreeing, and they no longer do.**
   * `xero-invoice.payload.ts` imports `inclusionsSegment` and `geographySegment`
   * from `xero-quote.payload.ts` (`3cd2e1c`, `6d17eea`), so "this grid cannot
   * know which document it becomes" stopped mattering: both print the same
   * sentence. The premise moved in another repository and nothing here could see
   * it — no build, no lint, no type — while the comment went on forbidding by
   * name the change that had become correct.
   *
   * So this row passes its real cities, **regions** and inclusions, exactly as
   * the quote and invoice grids do. `productCode` was always passed and still
   * is: the project-name substitution is live on both composers and a missing
   * code silently heads the sentence with the wrong name.
   */
  function descriptionOf(row: Row): string | null {
    const productId = row.draft.productId === '' ? null : Number(row.draft.productId);
    const productName =
      productId === null
        ? null
        : (productNames.get(productId) ?? row.saved?.product.productName ?? null);
    if (productName === null) return null;
    const named = row.draft.cityIds
      .map((id) => cityNames.get(id))
      .filter((name): name is string => name !== undefined);
    const projectId = row.draft.projectId === '' ? null : Number(row.draft.projectId);
    const projectName = projectId === null ? null : (projectNames.get(projectId) ?? null);
    const productCode = productId === null ? null : (productCodes.get(productId) ?? null);
    /*
     * The line's own regions, resolved to names in the DRAFT's order.
     *
     * ⚠️ **Consumed only on a line with NO cities** — `geographySegment` is
     * cities-else-regions, an EITHER, so on a project-backed row this array is
     * built and never printed. Passed unconditionally anyway: the member is
     * OPTIONAL, so omitting it reads as "this line names no regions", and a
     * projectless deal line would preview a bare product name while the document
     * it converts into carries the geography.
     *
     * An id we cannot name is DROPPED, the same rule the cities follow above.
     */
    const namedRegions = row.draft.regionIds
      .map((id) => regionNames.get(id))
      .filter((name): name is string => name !== undefined);
    /*
     * The line's inclusions, in the DRAFT's own order — the order that becomes
     * each entry's `sortOrder` on save, which convert carries across, which is
     * the order the customer's document prints in. Never re-sorted here.
     *
     * The quantity box holds a STRING and `''` means "omit the key", stored as
     * 1 — `Number('')` is `0`, which would preview `0 campuses` for a line that
     * will carry one. `effectiveInclusionQuantity` is that rule.
     */
    const lineInclusions = row.draft.inclusions
      .map((entry) => {
        const master = inclusionMasters.get(entry.inclusionId);
        return master === undefined
          ? null
          : { ...master, quantity: effectiveInclusionQuantity(entry.quantity) };
      })
      .filter((entry): entry is LineDescriptionInclusion => entry !== null);
    return composeLineDescription({
      productName,
      projectName,
      productCode,
      cityNames: named,
      regionNames: namedRegions,
      inclusions: lineInclusions,
    });
  }

  /*
   * ⚠️ The product picker deliberately does NOT exclude products already on the
   * deal, and the rule changed under migration 067: identity is the (product,
   * project) PAIR now, so the same product may legitimately appear twice. The
   * project picker is filtered by the row's own product, so an exclusion would
   * also deadlock the second-project case.
   */
  function productOptionsFor(row: Row): ComboboxOption[] {
    const list = products.rows.map((product) => ({
      value: String(product.id),
      label: product.productName,
      ...(product.productCode ? { hint: product.productCode } : {}),
    }));
    const saved = row.saved;
    if (saved !== null && !list.some((option) => option.value === String(saved.product.id))) {
      list.unshift({
        value: String(saved.product.id),
        label: saved.product.productName,
        ...(saved.product.productCode ? { hint: saved.product.productCode } : {}),
      });
    }
    /*
     * ⚠️ **THE SAME PIN FOR A SEEDED ROW, which has no `row.saved` to pin
     * from.** A planned product retired since the plan was written is absent
     * from the active catalogue, and an unmatched value renders as an EMPTY Item
     * cell over a real `productId` — a line POSTed naming a product nobody was
     * shown. See {@link seededProducts}.
     *
     * Guarded on `!list.some` like the branch above, so a planned product that
     * is still active is not listed twice; and keyed on the row's CURRENT
     * product, never on the seed, so a row switched to another item stops
     * matching and gets no pin.
     */
    const seeded = seededProducts.get(Number(row.draft.productId));
    if (
      row.draft.productId !== '' &&
      seeded !== undefined &&
      !list.some((option) => option.value === row.draft.productId)
    ) {
      list.unshift({
        value: row.draft.productId,
        label: seeded.productName,
        ...(seeded.productCode ? { hint: seeded.productCode } : {}),
      });
    }
    return list;
  }

  /**
   * Options for one row's region multiselect.
   *
   * ⚠️ **Two sources, and which one is in play is the owner's decision.** A row
   * whose project resolved offers THAT PROJECT'S regions — the line stays inside
   * the geography the project actually covers. A row with no project, or one
   * whose project could not be read, keeps the `/regions` master, because there
   * is no set to narrow against and narrowing on an unknown is how a stored value
   * silently leaves a whole-set REPLACE.
   *
   * ⚠️ **A region the LINE holds is pinned back in either way, under the name
   * the READ gave us.** Reads carry no `is_active` filter, so a line resolves a
   * retired region perfectly; dropping it from the options would render a bare id
   * AND — far worse — silently remove it from the set the next save writes,
   * because `regionIds` is a full REPLACE. Narrowing to a project makes that true
   * of every region the project does not declare as well, which is a far more
   * reachable state than a retired master row.
   */
  function regionOptionsFor(row: Row, resolved: RowProject): MultiComboboxOption[] {
    const settled = !regions.loading && !regions.failed;
    const narrowed = resolved.projectRegions !== null;
    const source = narrowed
      ? (resolved.projectRegions ?? [])
      : regions.records.map((record) => ({ id: record.id, name: record.name }));

    const list: MultiComboboxOption[] = source.map((region) => ({
      value: String(region.id),
      label: region.name,
    }));

    // Master first, the line's own read LAST so the name it was GIVEN wins.
    const nameOf = new Map<number, string>();
    for (const record of regions.records) nameOf.set(record.id, record.name);
    for (const region of row.saved?.regions ?? []) nameOf.set(region.id, region.name);

    const held = new Set<number>(row.draft.regionIds);
    for (const region of row.saved?.regions ?? []) held.add(region.id);
    for (const id of held) {
      if (list.some((option) => option.value === String(id))) continue;
      /*
       * ⚠️ Only ever CALLED retired once the master has settled — an in-flight or
       * failed fetch has empty `records`, where every region looks absent and none
       * is actually retired. Under a narrowed picker the honest fallback is the
       * fact we do know for certain: the resolved project does not declare it.
       */
      const retired = settled && !nameOf.has(id);
      const hint = retired ? 'No longer offered' : narrowed ? 'Not on this project' : undefined;
      list.push({
        value: String(id),
        /*
         * A bare id rather than dropping the option. An unnameable region is a
         * visible oddity somebody reports; a missing one is a silent deletion on
         * the next save.
         */
        label: nameOf.get(id) ?? `Region #${id}`,
        ...(hint ? { hint } : {}),
      });
    }
    return list;
  }

  function patchRow(key: string, patch: Partial<Draft>) {
    setSummary(null);
    setRows((current) =>
      current.map((row) => {
        if (row.key !== key) return row;
        /*
         * Changing the item invalidates the project (a project sells exactly one
         * product) and therefore the cities read off it.
         */
        const invalidated =
          patch.productId !== undefined && patch.productId !== row.draft.productId
            ? { projectId: '', cityIds: [], priceTierId: null }
            : {};
        // Project-first: choosing a project on an item-less row SETS the item,
        // so "this project sells X but this line sells Y" is unreachable.
        const impliedProduct =
          patch.projectId !== undefined &&
          patch.projectId !== '' &&
          row.draft.productId === '' &&
          patch.productId === undefined
            ? (productOfProject(projects.rows, Number(patch.projectId))?.id ?? null)
            : null;
        /*
         * A new pick starts at the product's step (`defaultQuantityFor`) — but
         * only while the box still holds the previous product's starting
         * quantity, so a typed quantity survives a change of item.
         */
        const nextProductId =
          impliedProduct !== null
            ? String(impliedProduct)
            : patch.productId !== undefined && patch.productId !== row.draft.productId
              ? patch.productId
              : null;
        const requantified =
          nextProductId !== null &&
          patch.quantity === undefined &&
          // An unstatable PLANNED quantity stays a question for the person.
          row.plannedQuantity === null &&
          (row.draft.quantity === '' ||
            row.draft.quantity === defaultQuantityFor(unitsOfProduct(row.draft.productId)))
            ? { quantity: defaultQuantityFor(unitsOfProduct(nextProductId)) }
            : {};
        const nextDraft: Draft = {
          ...row.draft,
          ...patch,
          ...invalidated,
          ...(impliedProduct !== null ? { productId: String(impliedProduct) } : {}),
          ...requantified,
        };
        /*
         * Untouched-price repricing (owner 2026-10-18, widened 2026-10-27): a
         * saved line still showing its untouched stored price — the computed
         * price of its saved selection — moves to `inherit` when its product,
         * project, tier, cities or quantity change, so the box previews the new
         * figure and the PATCH sends it. A price somebody typed is left exactly
         * as it is. A new line is `inherit` already and follows on its own.
         */
        const selectionMoved =
          nextDraft.productId !== row.draft.productId ||
          nextDraft.projectId !== row.draft.projectId ||
          nextDraft.priceTierId !== row.draft.priceTierId ||
          nextDraft.quantity !== row.draft.quantity ||
          citiesKey(nextDraft.cityIds) !== citiesKey(row.draft.cityIds);
        const repriced =
          row.saved !== null &&
          selectionMoved &&
          patch.price === undefined &&
          patch.priceMode === undefined &&
          row.draft.priceMode === 'value' &&
          row.draft.price === row.saved.unitPrice &&
          savedPriceFollows(row.saved, pricing)
            ? { priceMode: 'inherit' as const, price: '' }
            : {};
        return {
          ...row,
          draft: { ...nextDraft, ...repriced },
          state: row.state === 'saving' ? row.state : ('idle' as RowState),
          error: null,
          fieldErrors: {},
          refusal: null,
        };
      }),
    );
  }

  /**
   * Switching the Discount box between a percentage and a money amount.
   *
   * ⚠️ **NEITHER DIRECTION CONVERTS ANYTHING, AND NEITHER MAY.** The switch
   * moves `draft.discountAmount` between `null` and `''` and leaves the other
   * box exactly as it was, so flipping back and forth is free and loses
   * nothing. Seeding the amount from the percentage — which is what this used
   * to do — is the lossy conversion in the other direction, and the column
   * exists so that neither has to happen.
   *
   * ⚠️ **A switch on its own is not a save.** Both boxes still hold what they
   * held; `discountWriteOf` resolves which one the wire carries, and
   * `updatePayload` emits nothing until a figure actually moves.
   */
  function setDiscountMode(key: string, toAmount: boolean) {
    patchRow(key, { discountAmount: toAmount ? '' : null });
  }

  function addRow() {
    const key = `new-${newRowSeq}`;
    setNewRowSeq((value) => value + 1);
    setSummary(null);
    setRows((current) => [...current, blankRowOf(key)]);
    setExpanded((current) => new Set(current).add(key));
  }

  function toggleExpanded(key: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function discardRow(key: string) {
    // The typed amount goes with the row now: it is a `Draft` field rather than
    // a separate screen-state map, so there is nothing left to forget.
    setRows((current) => current.filter((row) => row.key !== key));
  }

  function revertRow(key: string) {
    setRows((current) =>
      current.map((row) =>
        row.key === key && row.saved !== null
          ? {
              ...row,
              draft: draftOf(row.saved),
              state: 'idle',
              error: null,
              fieldErrors: {},
              refusal: null,
            }
          : row,
      ),
    );
    /*
     * The typed amount needs no second step: it is a `Draft` field, so
     * `draftOf(row.saved)` above puts back the stored amount AND the stored
     * mode along with everything else. It used to live in a screen-state map
     * that Revert had to clear by hand — one more thing the real column removed.
     */
  }

  /**
   * Save every dirty, unblocked row.
   *
   * ⚠️ **N requests, one at a time, with no transaction across them**, so a
   * partial failure is an ordinary outcome — see this file's header.
   *
   * ⚠️ **A retry never re-posts a row that already landed.** A successful POST
   * turns its row into a saved row with a real line id, so the next run either
   * skips it or PATCHes it. Re-posting would 409 on the pairs that clash and,
   * worse, quietly create a genuine duplicate for any row whose project differs.
   *
   * ⚠️ **`onLinesChanged` fires ONCE, after the whole run** — and repairs no
   * header totals and queues no Xero push, because a deal has neither.
   */
  async function saveAll(): Promise<boolean> {
    /*
     * ⚠️ **The boolean is "did everything the run targeted land", and NOTHING
     * ELSE.** It exists for `registerSave`, so a parent can decide whether to
     * close; it is not a summary and it is not what a person reads — the run
     * report below is, row by row.
     *
     * Both early returns answer `true` deliberately. "There was nothing to
     * save" is not a failure, and returning `false` there would leave a caller
     * holding a form open over a grid with nothing wrong with it.
     */
    /*
     * Draft mode never reaches this — the Save control is not rendered — but the
     * guard is here rather than only in the markup, because "no request is
     * issued" is the contract of that mode and a contract enforced only by a
     * hidden button is enforced by nothing.
     */
    if (dealId === null) return true;
    const targets = rows.filter((row) => dirtyKeys.has(row.key) && !blockedKeys.has(row.key));
    if (targets.length === 0) return true;
    setSaving(true);
    setSummary(null);
    setRows((current) =>
      current.map((row) =>
        targets.some((target) => target.key === row.key)
          ? { ...row, state: 'saving', error: null, fieldErrors: {}, refusal: null }
          : row,
      ),
    );
    let saved = 0;
    let failed = 0;
    let halted = false;
    for (const target of targets) {
      try {
        const offersInclusions = inclusionsFor(target.key).offers;
        const offersProjects = projectsFor(target.key).showProject;
        /*
         * ⚠️ The SAME gate the render reads — a hidden regions block must not
         * emit `regionIds`, because the key present is a full REPLACE and `[]`
         * clears a set nobody on this screen can see.
         */
        const offersRegions = showsRegions(target);
        const inherited = inheritedFor(target);
        const result =
          target.saved === null
            ? await api.createDealProduct(
                dealId,
                createPayload(
                  target.draft,
                  undefined,
                  offersInclusions,
                  offersProjects,
                  offersRegions,
                  inherited,
                ),
              )
            : await api.updateDealProduct(
                dealId,
                target.saved.id,
                updatePayload(
                  target.draft,
                  target.saved,
                  offersInclusions,
                  offersProjects,
                  offersRegions,
                  inherited,
                ),
              );
        saved += 1;
        setRows((current) =>
          current.map((row) =>
            row.key === target.key ? { ...rowOf(result.data), key: row.key, state: 'saved' } : row,
          ),
        );
      } catch (error: unknown) {
        failed += 1;
        const described = describeFailure(error);
        setRows((current) =>
          current.map((row) =>
            row.key === target.key
              ? {
                  ...row,
                  state: 'failed',
                  error: described.message,
                  fieldErrors: described.fieldErrors,
                  refusal: described.refusal,
                }
              : row,
          ),
        );
        // A 401/403 will refuse every remaining row identically. Stop rather
        // than spend N requests reproducing one sentence.
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          halted = true;
          setRows((current) =>
            current.map((row) => (row.state === 'saving' ? { ...row, state: 'idle' } : row)),
          );
          break;
        }
      }
    }
    setSaving(false);
    setSummary({ saved, failed, halted });
    if (saved > 0) onLinesChanged?.();
    /*
     * ⚠️ **`blockedKeys.size` is part of the answer, not a separate question.**
     * A blocked row is never a target, so a run can save every row it attempted
     * and still leave work on screen that cannot be written. Answering `true`
     * there would let a parent close over it.
     */
    return failed === 0 && !halted && blockedKeys.size === 0;
  }

  async function confirmDelete() {
    const target = pendingDelete;
    // Draft rows are discarded locally and never reach here; see `deleteControl`.
    if (dealId === null || target === null || target.saved === null) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteDealProduct(dealId, target.saved.id);
      setRows((current) => current.filter((row) => row.key !== target.key));
      setPendingDelete(null);
      onLinesChanged?.();
    } catch (error: unknown) {
      setDeleteError(describeFailure(error).message);
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="grid min-w-0 gap-2" aria-busy="true">
        <Skeleton className="h-8 w-40" />
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} className="h-12 w-full" />
        ))}
        <span className="sr-only">Loading line items</span>
      </div>
    );
  }

  if (loadError !== null) {
    return (
      <div className="grid min-w-0 gap-2">
        <ErrorBlock title="This deal’s line items could not be loaded.">
          <p>{loadError}</p>
        </ErrorBlock>
        <div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setLoading(true);
              setReloadToken((n) => n + 1);
            }}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  /**
   * Item · Description · Qty · Price · Discount · Amount · actions.
   *
   * ⚠️ **SEVEN, not the other two grids' eight — there is no Tax rate column
   * because there is no tax column on the table.** It drives `colSpan` on the
   * two full-width rows, so it and the `<colgroup>` must move together.
   */
  const columnCount = 7;

  /**
   * The line's internal note (owner 2026-10-18), or `null` when there is
   * nothing to render.
   *
   * ⚠️ **A SAVED line's note is its own write, immediately, as a notes-only
   * PATCH** — it never joins the row's draft edits, never lights up "Save" and
   * never fires `onLinesChanged` (nothing a parent totals has moved). Only the
   * note is merged back, into BOTH `saved` and `draft`, so the row's other
   * unsaved edits are left exactly as they were. An UNSAVED row has no line to
   * PATCH, so its note goes into the draft and travels in the create payload.
   *
   * Hidden when the server sent no `notes` key (an older API), and on a
   * read-only grid when the line has no note to show.
   */
  function noteField(row: Row): ReactNode {
    const saved = row.saved;
    const value = saved === null ? row.draft.notes : saved.notes;
    if (value === undefined) return null;
    if (readOnly && (value === null || value === '')) return null;
    if (saved === null) {
      return (
        <LineNoteField
          value={value}
          disabled={readOnly || row.state === 'saving' || saving}
          onSave={async (next) => {
            patchRow(row.key, { notes: next });
          }}
        />
      );
    }
    return (
      <LineNoteField
        value={value}
        disabled={readOnly || dealId === null}
        onSave={async (next) => {
          if (dealId === null) return;
          const result = await api.updateDealProduct(dealId, saved.id, { notes: next });
          const stored = result.data.notes === undefined ? next : result.data.notes;
          setRows((current) =>
            current.map((candidate) =>
              candidate.key === row.key && candidate.saved !== null
                ? {
                    ...candidate,
                    saved: { ...candidate.saved, notes: stored },
                    draft: { ...candidate.draft, notes: stored },
                  }
                : candidate,
            ),
          );
        }}
      />
    );
  }

  /**
   * The tiers a row's Price picker offers: priced ones plus the default
   * (`tierOptionsFor`), with the tier the row already holds pinned back in.
   */
  function tierOptionsOf(row: Row): PriceTier[] {
    const projectId = row.draft.projectId === '' ? null : Number(row.draft.projectId);
    const entries = projectId === null ? [] : (prices.entriesByProject.get(projectId) ?? []);
    const productId = row.draft.productId === '' ? null : Number(row.draft.productId);
    const options = tierOptionsFor(entries, prices.tiers, productId, currency);
    const held = row.draft.priceTierId;
    if (held === null || options.some((tier) => tier.id === held)) return options;
    const pinned = prices.tiers.find((tier) => tier.id === held);
    return pinned === undefined ? options : [...options, pinned];
  }

  function rowControls(row: Row, compact: boolean) {
    const problems = problemsByKey.get(row.key) ?? { fieldErrors: {}, error: null, notice: null };
    const disabled = readOnly || row.state === 'saving' || saving;
    const onChange = (patch: Partial<Draft>) => patchRow(row.key, patch);
    const shared = { row, problems, disabled, compact, onChange };
    const resolved = projectsFor(row.key);
    return {
      problems,
      disabled,
      /*
       * ⚠️ A RENDER decision only. Both layouts read it to decide whether the
       * project block is in the stack; nothing in it reaches the draft, and the
       * payload builders take the same value as their own explicit argument.
       */
      showProject: resolved.showProject,
      /*
       * ⚠️ **A RENDER decision only, and it is NOT `offersProjects`.** The
       * payload builders keep gating `projectId`/`cityIds` on `showProject`
       * above; this one hides a picker with no options on a project that
       * declares none — see {@link showsCitiesControl}, which argues why those
       * must stay two values.
       */
      showCities: showsCitiesControl(row, resolved.showProject, resolved.projectHasNoCities),
      /*
       * ⚠️ Read through `showsRegions` rather than recomputed here, so this
       * render and the two payload builders cannot disagree about whether a
       * hidden control may write. It is NOT the complement of `showProject` —
       * see {@link showsRegionsControl}. ⚠️ *SUPERSEDED: "a row whose project
       * declares no cities shows both."* It shows the PROJECT and the REGIONS;
       * the CITY picker is gone there, as of `showsCitiesControl`.
       */
      showRegions: showsRegions(row),
      product: <ProductControl {...shared} options={productOptionsFor(row)} />,
      description: (
        <DescriptionControl
          disabled={disabled}
          compact={compact}
          derived={descriptionOf(row)}
          description={row.draft.description}
          /* `row.saved` only — a row that has never been written has no
             original to go back to, so the control correctly does not appear. */
          saved={row.saved?.description ?? null}
          error={problems.fieldErrors.description}
          onTyped={(value) => onChange({ description: value })}
          onReset={() => onChange({ description: null })}
          onRestore={() => onChange({ description: row.saved?.description ?? null })}
        />
      ),
      project: <ProjectControl {...shared} resolved={resolved} />,
      /* Directly under the project; only once the line names one. */
      showTier: row.draft.projectId !== '' && prices.tiers.length > 0,
      tier: (
        <PriceTierControl
          {...shared}
          options={tierOptionsOf(row)}
          defaultTierId={prices.defaultTierId}
        />
      ),
      cities: <CitiesControl {...shared} resolved={resolved} />,
      /*
       * `loading` only while the list is genuinely empty: once the six rows are
       * in, a background refetch must not blank a picker somebody is using.
       * `regions.failed` deliberately does NOT hide the control — a row that
       * holds regions still renders them from `regionOptionsFor`'s pinned
       * entries, and a control that vanishes on a failed read is
       * indistinguishable from a bug.
       */
      regions: (
        <RegionsControl
          {...shared}
          options={regionOptionsFor(row, resolved)}
          loading={regions.loading && regions.records.length === 0}
          /*
           * ⚠️ The SAME `resolved` the project and city controls read, so the
           * three cells cannot disagree about what this line's project declares.
           * It decides both which list the picker offers and whether "All
           * regions" has a bound to mean anything — see {@link RegionsControl}.
           */
          projectRegions={resolved.projectRegions}
        />
      ),
      quantity: <QuantityControl {...shared} step={quantityStepOf(unitsFor(row))} />,
      /*
       * ⚠️ **Built HERE rather than at the two JSX call sites** so it takes the
       * same `disabled` and the same `onChange` as every other control on the
       * row. The wide grid and the sub-`md` card list both render it, and a cell
       * that could be edited in one layout and not the other is the
       * "indistinguishable from a removed feature" failure this repo records.
       *
       * ⚠️ **`previewInputs` is READ, never recomputed.** It is the identical
       * map `previewByKey` is built from, so the figure the box solves backwards
       * from and the figure the preview solves forwards to cannot come from two
       * different resolutions of the same row. The fallback is the same call
       * with the same arguments — present only because `Map.get` is typed
       * `| undefined`, and deliberately not a different resolution.
       */
      amount: (
        <AmountCell
          row={row}
          dirty={dirtyKeys.has(row.key)}
          currency={currency}
          preview={previewByKey.get(row.key) ?? null}
          input={previewInputs.get(row.key) ?? previewInputOf(row, inheritedFor(row))}
          disabled={disabled}
          onChange={onChange}
        />
      ),
      price: (
        <PriceControl
          {...shared}
          inheritedPrice={inheritedFor(row)?.unitPrice}
          keepsStored={inheritKeepsStored(row)}
          unitLabel={unitLabelOf(unitsFor(row))}
        />
      ),
      discount: (
        <DiscountControl {...shared} onModeChange={(toAmount) => setDiscountMode(row.key, toAmount)} />
      ),
    };
  }

  function deleteControl(row: Row) {
    if (row.saved === null) {
      return readOnly ? null : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          iconOnly
          leadingIcon={<TrashIcon />}
          aria-label="Discard this row"
          onClick={() => discardRow(row.key)}
        />
      );
    }
    if (!canDelete) {
      // Disabled and explained rather than hidden: a missing control does not
      // explain why the same account may add and edit a line but not remove it.
      return (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          iconOnly
          leadingIcon={<TrashIcon />}
          disabled
          title={deleteDenialReason}
          aria-label={`Delete this line — ${deleteDenialReason}`}
        />
      );
    }
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        iconOnly
        leadingIcon={<TrashIcon />}
        aria-label={`Delete the ${row.saved.product.productName} line`}
        onClick={() => {
          setDeleteError(null);
          setPendingDelete(row);
        }}
      />
    );
  }

  function rowActions(row: Row) {
    return (
      <div className="flex flex-wrap items-center justify-end gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={expanded.has(row.key)}
          onClick={() => toggleExpanded(row.key)}
        >
          {expanded.has(row.key) ? 'Hide' : 'Details'}
        </Button>
        {!readOnly && dirtyKeys.has(row.key) && row.saved !== null && (
          <Button type="button" variant="ghost" size="sm" onClick={() => revertRow(row.key)}>
            Revert
          </Button>
        )}
        {deleteControl(row)}
      </div>
    );
  }

  /**
   * The expanded panel: the inclusions editor, and nothing else.
   *
   * ⚠️ **NO Subtotal / Tax figures block, and its absence is the contract.** The
   * other two grids show `lineSubtotal` and `lineTax` here; `f_deal_products`
   * has neither column, so there would be nothing to read and any figure shown
   * would have been invented by this file. The one generated figure a deal line
   * has is `lineTotal`, and the Amount cell already shows it.
   */
  function detailPanel(row: Row) {
    const { problems, disabled } = rowControls(row, true);
    const { offers, offeredIds, denialReason, orphaned } = inclusionsFor(row.key);
    return (
      <div className="grid gap-4 border-t border-border bg-surface-sunken px-3 py-3">
        {offers ? (
          <InclusionsEditor
            row={row}
            problems={problems}
            disabled={disabled}
            master={inclusions.rows}
            masterFailed={inclusions.failed}
            offeredIds={offeredIds}
            onChange={(patch) => patchRow(row.key, patch)}
          />
        ) : (
          <InclusionsNotOffered
            row={row}
            master={inclusions.rows}
            denialReason={denialReason}
            orphaned={orphaned}
          />
        )}
      </div>
    );
  }

  const runReport =
    summary === null ? null : summary.failed === 0 ? (
      <Notice tone="success" role="status">
        {summary.saved} of {summary.saved} {summary.saved === 1 ? 'line' : 'lines'} saved.
      </Notice>
    ) : (
      <ErrorBlock
        title={`${summary.saved} of ${summary.saved + summary.failed} lines saved. Fix the ${
          summary.failed === 1 ? 'row' : 'rows'
        } marked below and save again.`}
      >
        <p>
          The saved {summary.saved === 1 ? 'line is' : 'lines are'} already stored — nothing needs
          re-entering.
          {summary.halted ? ' The run stopped early; the rest were not attempted.' : ''}
        </p>
      </ErrorBlock>
    );

  return (
    <div className="grid min-w-0 gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-fg">Line items</h3>
          <p className="text-xs text-fg-muted">
            {rows.length === 0
              ? 'None yet.'
              : /*
                 * Never "unsaved" in draft mode — nothing on this grid has
                 * anywhere to be saved to yet, and calling it unsaved would imply
                 * a Save control the person is failing to find.
                 */
                `${rows.length} ${rows.length === 1 ? 'line' : 'lines'}${
                  !draftMode && dirtyCount > 0 ? ` · ${dirtyCount} unsaved` : ''
                }`}
          </p>
        </div>
        {!readOnly && (
          <div className="flex shrink-0 items-center gap-2">
            {/* Add row lives below the rows, full width — owner's request, 2026-10-12. */}
            {/*
              ⚠️ No Save control in draft mode, and not a disabled one either. A
              line cannot be written before the deal has an id, so there is no
              request this button could make — the deal form's own submit is what
              writes these rows, and a second save-looking control beside it
              would read as a step the person has to take first.

              🔴 **AND NONE WHEN A PARENT HAS TAKEN THE SAVE** (`registerSave`),
              which is the same reason one step out: the deal form's footer
              "Save changes" runs this grid's own `saveAll` before its PATCH, so
              a button here would be a SECOND control performing the identical
              run. Owner's instruction, 10 September 2026 — one button, and it
              says "Save changes".

              ⚠️ **The label no longer counts rows.** It read `Save ${n} rows`
              while dirty and "Save changes" while clean, so the one control
              renamed itself under the person using it — and on a form it sat
              beside a footer control of the *other* name, reading as two
              different jobs. It is one job on every screen and says so.
            */}
            {!draftMode && registerSave === undefined && (
              <Button
                type="button"
                variant="primary"
                loading={saving}
                disabled={saving || dirtyCount === 0 || blockedKeys.size > 0}
                onClick={() => {
                  void saveAll();
                }}
              >
                Save changes
              </Button>
            )}
          </div>
        )}
      </div>

      {/*
        🔴 The line that answered "will closing this modal lose what I have
        typed?" ("These lines are written when the deal is created.") was
        removed on the owner's stricter no-helper-text ruling (2026-09-05).
        Nothing on screen answers it now, and the two-phase create is unchanged.
      */}
      {contextNote}
      {runReport}

      {blockedKeys.size > 0 && (
        <ErrorBlock
          title={
            draftMode
              ? `${blockedKeys.size} ${
                  blockedKeys.size === 1 ? 'row is' : 'rows are'
                } not ready and would not be written with the deal.`
              : `${blockedKeys.size} ${
                  blockedKeys.size === 1 ? 'row needs' : 'rows need'
                } attention before anything can be saved.`
          }
        >
          {draftMode && (
            <p>Fix or discard {blockedKeys.size === 1 ? 'it' : 'them'} before creating the deal.</p>
          )}
        </ErrorBlock>
      )}

      {rows.length === 0 ? (
        <EmptyState
          icon={<LinesIcon />}
          title={draftMode ? 'No line items yet' : 'No line items on this deal'}
          description={
            readOnly
              ? 'Nothing itemised on this deal.'
              : draftMode
                ? 'Add a row for what this deal will sell. Lines are written with the deal.'
                : 'Add a row for what this deal will sell.'
          }
          action={
            readOnly ? undefined : (
              <Button type="button" variant="secondary" onClick={addRow}>
                Add row
              </Button>
            )
          }
        />
      ) : (
        <>
          {/*
            The wide grid, and the stacked cards below `md`. Two renderings of
            one row set, never two sources of truth: both call `rowControls`, and
            only `compact` differs.
          */}
          <div className="hidden min-w-0 md:block">
            {/* A section on the deal page, not the whole page, so the scroll
                area is capped rather than sized off the viewport. */}
            <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
              <Table className="min-w-[1350px] table-fixed">
                {/*
                  ⚠️ **The `<colgroup>` is the ONE place a column width lives**,
                  and under `table-fixed` these values are authoritative — no
                  `<th>` or `<td>` below carries a `w-`, so nothing can disagree
                  with them. Each body cell instead carries a content `min-w-` of
                  its column MINUS 24px, which is `TD`'s `px-3` twice; a content
                  minimum is what the algorithm may not go under. **Those two
                  lists are the one thing here that can silently drift apart —
                  move them together, always.**

                  Item 285 · Description 377 · Qty 84 · Price 148 ·
                  Discount 240 · Amount 122 · actions 94        = 1350

                  ⚠️ **1350, not the other two grids' 1492, and the difference is
                  exactly their Tax rate column (142).** Every other width
                  matches the invoice grid's on purpose. Do NOT redistribute the
                  142 to "use the space" — keeping the shared widths is what lets
                  the two tables be compared.

                  ⚠️ **Both totals moved on 2026-08-31 (1258 → 1350 here,
                  1400 → 1492 there) and ONLY Discount moved: 148 → 240, +92 on
                  all three grids.** Its mode switch stopped being a `%` button
                  beside a currency-code label and became a segmented control
                  reading **Percentage** and **Amount**, and two words plus a
                  reserved tick do not fit in 124px of content. **The pinning
                  geometry is untouched** — PIN_AMOUNT's `right-[94px]` is the
                  ACTIONS column's width, not the table's total.

                  ⚠️ **92 IS A MEASUREMENT AND THE FIRST GUESS WAS 56, WHICH
                  WOULD HAVE SHIPPED A CLIPPED CONTROL.** Cut from the type
                  scale the control "should" have been ~170 wide; driven in
                  Chrome at 768 and 1440 it is **193** — `Percentage` 101 +
                  `Amount` 84 + a 2px inter-segment gap + `p-0.5` + a 2px
                  border. The reserved 12px tick on each button is what the
                  arithmetic kept forgetting. **Measure this cell; do not
                  derive it.**

                  Widths are cut at `--text-sm` (13px, tabular-nums) from each
                  cell's WORST case, never today's data.

                  ⚠️ *SUPERSEDED: "`formatDecimalString` renders thousands
                  separators AND the currency code, so Amount holds
                  `123,456.00 GBP` (≈96px)."* **Measured false, and on both
                  halves.** `formatDecimalString` in `src/lib/format.ts` is
                  `(value: string | null): string` — ONE argument — and has
                  never printed a currency code; every call site in this file
                  appends `currency` itself, as a sibling `<span>`. **Amount
                  holds an INPUT anyway, so the budget is the input's own
                  content**: 122 − 24 = 98px, of which `Input`'s `px-2.5` plus a
                  1px border spends 22, leaving 76px for the figure against the
                  ~70px the STORED string `123456.00` needs — no thousands
                  separators and no code inside the box. That arithmetic is
                  written out again at the Amount `TD` itself, further down this
                  file, which is the comment this paragraph contradicted.

                  ⚠️ **The currency code costs a line of HEIGHT, not width.** It
                  is a stacked `text-2xs` span UNDER the box (see `AmountCell`),
                  so the row grows taller and the column does not grow wider.
                  **Do not re-cut Amount from `123,456.00 GBP`**: nothing renders
                  that string, and budgeting for it takes width off a neighbour
                  to hold a figure this grid never draws.

                  ⚠️ **Discount is the one column NOT cut from its figure** —
                  216px of content is the segmented control, MEASURED at 193,
                  leaving 23px for a font that renders wider than
                  Chrome-on-Windows does. The last value is the actions column's
                  width and must equal PIN_AMOUNT's `right-[94px]`.
                */}
                <colgroup>
                  <col className="w-[285px]" />
                  <col className="w-[377px]" />
                  <col className="w-[84px]" />
                  <col className="w-[148px]" />
                  {/* Discount — sized by the SEGMENTED CONTROL, not the figure. */}
                  <col className="w-[240px]" />
                  <col className="w-[122px]" />
                  <col className="w-[94px]" />
                </colgroup>
                <THead>
                  <TR>
                    {/* The Project and Cities controls sit under this one and
                        carry their own labels, because no header names them. */}
                    <TH>Item</TH>
                    <TH>Description</TH>
                    <TH numeric>Qty</TH>
                    <TH numeric>Price</TH>
                    {/* Not "Disc %": the box takes either, and the control's own
                        label says which it is currently taking. */}
                    <TH numeric>Discount</TH>
                    {/* ⚠️ The two pinned HEADER cells go through `TH`'s own
                        `frozen` / `pinned` props rather than `PIN_AMOUNT` /
                        `PIN_ACTIONS`: under `frozenHeader` a header cell needs
                        its top AND right offsets and both hairlines in one
                        literal, which only the primitive writes. 94 is the
                        actions column's width, as in `PIN_AMOUNT`. The body
                        cells keep the local literals (z-10, under the header). */}
                    <TH numeric frozen="right" frozenOffset={94}>
                      Amount
                    </TH>
                    <TH pinned>
                      <span className="sr-only">Status and actions</span>
                    </TH>
                  </TR>
                </THead>
                <TBody>
                  {rows.map((row) => {
                    const controls = rowControls(row, true);
                    const dirty = dirtyKeys.has(row.key);
                    const note = noteField(row);
                    return (
                      <Fragment key={row.key}>
                        <TR className={cx(row.state === 'failed' && 'bg-danger-soft/40')}>
                          {/* The stack: item, then the project it is sold
                              through, then that project's cities. Ordered the
                              way somebody fills it in, and each part is a
                              separate control emitting its own patch — see
                              `ProjectControl` for the one patch that carries two
                              of them. */}
                          <TD className="align-top">
                            <div className="grid min-w-[261px] gap-2">
                              <div>
                                {controls.product}
                                <InclusionSummary
                                  row={row}
                                  master={inclusions.rows}
                                  variant="count"
                                  error={controls.problems.fieldErrors.inclusions}
                                />
                              </div>
                              {/* ⚠️ TWO gates now, not one. The project block
                                  and the city picker no longer stand or fall
                                  together: a project that declares no cities
                                  keeps its picker (so the project is still
                                  editable) and loses the city one, which could
                                  only ever have rendered empty. See
                                  `showsCitiesControl`. **Neither gate touches
                                  state** — `cityIds: []` CLEARS a stored set —
                                  and neither is the `offersProjects` the payload
                                  builders take. */}
                              {controls.showProject && controls.project}
                              {controls.showProject && controls.showTier && controls.tier}
                              {controls.showCities && controls.cities}
                              {/* ⚠️ The other half of the same decision: the
                                  geography a line carries when it has no CITIES
                                  to carry one — either because the product is
                                  not sold through projects at all, or because
                                  its project declares none. ⚠️ *SUPERSEDED: "So
                                  it can sit BESIDE the two controls above rather
                                  than only in place of them."* It sits beside
                                  the PROJECT control; it replaces the CITY one,
                                  which reads the same `projectHasNoCities`. The
                                  two appear together only on a row that already
                                  holds cities. Gated by `showsRegionsControl`,
                                  which never touches state on the way to hiding:
                                  `regionIds: []` CLEARS a stored set. */}
                              {controls.showRegions && controls.regions}
                            </div>
                          </TD>
                          <TD className="align-top">
                            <div className="min-w-[353px]">{controls.description}</div>
                          </TD>
                          <TD className="align-top">
                            <div className="min-w-[60px]">{controls.quantity}</div>
                          </TD>
                          <TD className="align-top">
                            <div className="min-w-[124px]">{controls.price}</div>
                          </TD>
                          <TD className="align-top">
                            <div className="min-w-[216px]">{controls.discount}</div>
                          </TD>
                          {/* The pinned pair. The background is chosen by a
                              ternary rather than by two `bg-` utilities on one
                              element: same-property Tailwind utilities resolve
                              by stylesheet order, not by the order written. */}
                          <TD
                            numeric
                            className={cx(
                              'align-top',
                              PIN_AMOUNT,
                              row.state === 'failed' ? PIN_ROW_BG_FAILED : PIN_ROW_BG,
                            )}
                          >
                            {/*
                              122 − 24 = 98px of content, and the cell holds an
                              INPUT now rather than text. `Input` is `px-2.5`
                              plus a 1px border, so 22px goes on the chrome and
                              76px is left for the figure — against a box that
                              shows the STORED string, `123456.00` (~70px at
                              13px tabular-nums), with neither thousands
                              separators nor the currency code inside it. It
                              fits, so **no width moved**; the colgroup's 1350
                              and every content `min-w-` here are untouched.
                              Beyond that an input SCROLLS rather than clipping,
                              which is the degradation a text cell did not have.
                            */}
                            <div className="min-w-[98px]">{controls.amount}</div>
                          </TD>
                          <TD
                            className={cx(
                              'align-top',
                              PIN_ACTIONS,
                              row.state === 'failed' ? PIN_ROW_BG_FAILED : PIN_ROW_BG,
                            )}
                          >
                            <div className="grid min-w-[70px] justify-items-end gap-1">
                              <StatusBadge row={row} dirty={dirty} />
                              {rowActions(row)}
                            </div>
                          </TD>
                        </TR>
                        {note !== null && (
                          <TR>
                            <TD colSpan={columnCount} className="pt-0">
                              <div className="max-w-3xl">{note}</div>
                            </TD>
                          </TR>
                        )}
                        {/*
                          `text-warning`, not an `ErrorBlock`: nothing here is
                          refused. A danger tone beside a real refusal would make
                          the two indistinguishable. Rendered BEFORE the error
                          block and never instead of it — a row can carry both.
                        */}
                        {controls.problems.notice !== null && (
                          <TR>
                            <TD colSpan={columnCount} className="pt-0">
                              <p className="text-xs break-words text-warning">
                                {controls.problems.notice}
                              </p>
                            </TD>
                          </TR>
                        )}
                        {controls.problems.error !== null && (
                          <TR>
                            <TD colSpan={columnCount} className="pt-0">
                              <ErrorBlock title={controls.problems.error} />
                            </TD>
                          </TR>
                        )}
                        {expanded.has(row.key) && (
                          <TR>
                            <TD colSpan={columnCount} className="p-0">
                              {detailPanel(row)}
                            </TD>
                          </TR>
                        )}
                      </Fragment>
                    );
                  })}
                </TBody>
              </Table>
            </TableWrap>
          </div>

          <ul className="grid gap-3 md:hidden">
            {rows.map((row) => {
              const controls = rowControls(row, false);
              const dirty = dirtyKeys.has(row.key);
              return (
                <li
                  key={row.key}
                  className={cx(
                    'rounded-lg border border-border bg-surface',
                    row.state === 'failed' && 'border-danger-border',
                  )}
                >
                  <div className="grid gap-3 px-3 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-1">
                        <StatusBadge row={row} dirty={dirty} />
                      </div>
                      {/*
                        ⚠️ **`w-36` because the cell is a CONTROL now, not a
                        figure.** In the card layout this sits in a `flex …
                        justify-between` beside the status badge, where a bare
                        `text-right` let the old text shrink to its content —
                        which an input cannot do without becoming untypeable at
                        320. The label above it is kept: nothing in the box
                        names itself, and `aria-label` is not visible text.
                      */}
                      <div className="w-36 text-right">
                        <span className="block text-2xs text-fg-muted">
                          Amount
                        </span>
                        {controls.amount}
                      </div>
                    </div>
                    {controls.product}
                    <InclusionSummary
                      row={row}
                      master={inclusions.rows}
                      error={controls.problems.fieldErrors.inclusions}
                    />
                    {/* The same stack as the wide grid's Item cell, in the same
                        order and behind the same TWO flags — ⚠️ *SUPERSEDED:
                        "behind the same flag"*, singular, which was true while
                        the project block and the city picker stood or fell
                        together. They do not: a project declaring no cities
                        keeps the project picker and loses the city one. This is
                        a card, not a grid: `compact` is false, so every control
                        keeps its label and its hint, and `table-fixed` never
                        reaches it. */}
                    {controls.showProject && controls.project}
                    {controls.showProject && controls.showTier && controls.tier}
                    {controls.showCities && controls.cities}
                    {/* The same block as the wide grid's Item cell, behind the
                        same predicate. `compact` is false here, so the control
                        keeps its label, the projectless sentence, and the note
                        that says "All regions" is a snapshot rather than a live
                        rule. */}
                    {controls.showRegions && controls.regions}
                    {controls.description}
                    <div className="grid gap-3 sm:grid-cols-2">
                      {controls.quantity}
                      {controls.discount}
                    </div>
                    {controls.price}
                    {noteField(row)}
                    {controls.problems.notice !== null && (
                      <p className="text-xs break-words text-warning">
                        {controls.problems.notice}
                      </p>
                    )}
                    {controls.problems.error !== null && (
                      <ErrorBlock title={controls.problems.error} />
                    )}
                    {rowActions(row)}
                  </div>
                  {expanded.has(row.key) && detailPanel(row)}
                </li>
              );
            })}
          </ul>

          {/*
            The label says WHICH figure this is, in three words, and that is the
            load-bearing half — a previewed total reading as a total is the
            number somebody quotes down the phone.

            ⚠️ **None of these is `deal.amount`.** A deal's amount is a
            hand-entered figure with its own meaning and the basket does not
            maintain it; there is no server-side basket total on a deal at all.
            This is a sum of the lines, for the reader, and nothing writes it.
          */}
          {!readOnly && (
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={addRow}
              disabled={saving}
            >
              Add row
            </Button>
          )}
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-t border-border pt-2">
            <span className="text-xs text-fg-muted">
              {draftMode ? 'Preview total' : dirtyCount > 0 ? 'Total of saved lines' : 'Total'}
            </span>
            <span className="tabular-nums text-sm font-semibold text-fg">
              {formatDecimalString(draftMode ? draftPreviewTotal : savedTotal)}
              {(draftMode ? draftPreviewTotal : savedTotal) !== null && currency && (
                <span className="ml-1 text-xs font-normal text-fg-muted">{currency}</span>
              )}
            </span>
          </div>
        </>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={() => {
          void confirmDelete();
        }}
        title={
          pendingDelete?.saved
            ? `Delete the ${pendingDelete.saved.product.productName} line?`
            : 'Delete this line item?'
        }
        tone="danger"
        loading={deleting}
        {...(deleteError ? { error: deleteError } : {})}
        confirmLabel="Delete line"
        description={
          pendingDelete?.saved ? (
            <>
              <p>
                <strong>{pendingDelete.saved.product.productName}</strong>
                {pendingDelete.saved.project ? ` under ${pendingDelete.saved.project.name}` : ''}{' '}
                will be removed from this deal.
              </p>
              {pendingDelete.saved.cities.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-fg-muted">
                    It runs in {pendingDelete.saved.cities.length}{' '}
                    {pendingDelete.saved.cities.length === 1 ? 'city' : 'cities'}:
                  </p>
                  <div className="mt-1">
                    <CityChips cities={pendingDelete.saved.cities} />
                  </div>
                </div>
              )}
              {pendingDelete.saved.inclusions.length > 0 && (
                <p className="mt-2">
                  Its {pendingDelete.saved.inclusions.length}{' '}
                  {pendingDelete.saved.inclusions.length === 1
                    ? 'campaign inclusion goes'
                    : 'campaign inclusions go'}{' '}
                  with it.
                </p>
              )}
              {/*
                ⚠️ **No "the header totals are recalculated" sentence, unlike the
                quote and invoice grids.** A deal has no header totals for a line
                write to touch, and `deal.amount` is hand-entered and untouched by
                this. Saying otherwise would describe a write that does not happen.
              */}
            </>
          ) : null
        }
      />
    </div>
  );
}
